import React, { useState, useEffect } from 'react';
import { Home, Briefcase, FileText, Building2, ChevronLeft, Plus, Download, Eye, Upload, X, TrendingUp, Calendar, Maximize, BedDouble, Bath, Building, AlertCircle, Clock, CheckCircle, DollarSign, Wallet, PieChart, Search, Tag, ExternalLink, Loader2, XCircle, BarChart3, PenLine, ClipboardList, ScrollText, Scale, Landmark, Receipt, CreditCard, BookOpen, Map, Mail, ChevronRight, FolderOpen } from 'lucide-react';
import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { formatDate, calculateCompletionProgress, getCompletionStatus } from '../utils/PropertyDocumentUtils';
import { unitCategory, hasBothCategories as portfolioHasBoth, UnitCategory } from '../utils/unitCategory';
import { DocumentData } from '../types';
import { useLanguage } from './LanguageContext';
import InstallmentPlanView from './InstallmentPlanView';
import DocumentViewerModal from './DocumentViewerModal';
import ProjectTimeline from './ProjectTimeline';
import { appLanguage, trData } from '../i18n/locale';
import {
  computeSale, summariseSale, trusteeFeeFor, SaleSummary, SaleType, AcquisitionRoute,
  isOffplanUnit, planPaidToDate,
  DLD_TRANSFER_RATE, AGENCY_COMMISSION_RATE
} from './portfolioMetrics';

// Backend URL - update this if your backend URL changes
const backendUrl = process.env.REACT_APP_API_URL || 'https://investmenter-backend-1081814658115.europe-west1.run.app';

/* The catch-all folder that holds documents we could not match to a unit is
   stored as a unit named "other documents". It is not a property, so it never
   sits in amongst the real ones — it is always sorted to the end of the list. */
const isOtherDocsUnit = (u: any): boolean => (u?.name || '').toLowerCase() === 'other documents';

// ============================================
// CONVERSION UTILITIES
// ============================================
const AED_TO_EUR_RATE = 0.25; // Approximate rate - 1 AED ≈ 0.25 EUR
const SQFT_TO_SQM_RATE = 0.0929; // 1 sqft = 0.0929 sqm
const SQM_TO_SQFT_RATE = 10.764; // 1 sqm = 10.764 sqft

const formatAED = (value: number): string => {
  return `AED ${value.toLocaleString('en-AE')}`;
};

const formatEUR = (aedValue: number): string => {
  const eurValue = aedValue * AED_TO_EUR_RATE;
  return `€${eurValue.toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
};

// Smart area detection: if value < 300, it's likely SQM, otherwise SQFT
const isLikelySqm = (area: number): boolean => {
  return area > 0 && area < 300;
};

// Get area in SQFT (auto-converts if input is SQM)
const getAreaInSqft = (area: number): number => {
  if (isLikelySqm(area)) {
    return area * SQM_TO_SQFT_RATE; // Convert SQM to SQFT
  }
  return area; // Already in SQFT
};

// Get area in SQM (auto-converts if input is SQFT)
const getAreaInSqm = (area: number): number => {
  if (isLikelySqm(area)) {
    return area; // Already in SQM
  }
  return area * SQFT_TO_SQM_RATE; // Convert SQFT to SQM
};

// Format area display with both units
const formatAreaDisplay = (area: number): { primary: string; secondary: string; sqftValue: number } => {
  if (isLikelySqm(area)) {
    // Input is SQM - show SQM primary, SQFT secondary
    const sqft = area * SQM_TO_SQFT_RATE;
    return {
      primary: `${area.toLocaleString('en-AE', { minimumFractionDigits: 1, maximumFractionDigits: 2 })} m²`,
      secondary: `${sqft.toLocaleString('en-AE', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} sqft`,
      sqftValue: sqft
    };
  } else {
    // Input is SQFT - show SQFT primary, SQM secondary
    const sqm = area * SQFT_TO_SQM_RATE;
    return {
      primary: `${area.toLocaleString('en-AE', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} sqft`,
      secondary: `${sqm.toLocaleString('en-AE', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} m²`,
      sqftValue: area
    };
  }
};

const sqftToSqm = (sqft: number): string => {
  const sqm = sqft * SQFT_TO_SQM_RATE;
  return sqm.toLocaleString('en-AE', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
};

const calculatePricePerSqft = (price: number, area: number): number => {
  if (!price || !area || area === 0) return 0;
  const sqftArea = getAreaInSqft(area);
  return Math.round(price / sqftArea);
};

const calculatePricePerSqm = (price: number, area: number): number => {
  if (!price || !area || area === 0) return 0;
  const sqmArea = getAreaInSqm(area);
  return Math.round(price / sqmArea);
};

// ============================================
// FEATURE FLAGS - Set to false to show "Coming Soon"
// Set to true when ready to enable features
// ============================================
const CASHFLOW_ENABLED = false;
const INCOME_ENABLED = false;

// Fallback project images (admin can override via Property Photos tab)
export const FALLBACK_PROJECT_IMAGES: { [key: string]: string } = {
  'seagate':                     'https://i.imgur.com/Wq09VFd.jpeg',
  'sobha one':                   'https://i.imgur.com/cUQEinr.jpeg',
  'sobha hartland - the crest':  'https://i.imgur.com/1cg1mrf.jpeg',
  'the crest':                   'https://i.imgur.com/1cg1mrf.jpeg',
  'crest grande':                'https://i.imgur.com/En06J0C.jpeg',
  'silena':                      'https://i.imgur.com/MA8aZv1.jpeg',
  'silena residences':           'https://i.imgur.com/MA8aZv1.jpeg',
  'damac lagoons portofino':     'https://i.imgur.com/xPIGTnD.jpeg',
  'damac lagoons':               'https://i.imgur.com/xPIGTnD.jpeg',
  'portofino':                   'https://i.imgur.com/xPIGTnD.jpeg',
  'potofino':                    'https://i.imgur.com/xPIGTnD.jpeg',
  'gp chevalia fields':          'https://i.imgur.com/piVz6YW.jpeg',
  'chevalia fields':             'https://i.imgur.com/piVz6YW.jpeg',
  'grand polo chevalia fields':  'https://i.imgur.com/piVz6YW.jpeg',
  'lia':                         'https://i.imgur.com/lqynarw.jpeg',
  'clearpoint':                 'https://i.imgur.com/HhXIcMP.jpeg',
 'damac bay':                   'https://i.imgur.com/mx8T6K3.jpeg',
  'salva':                       'https://i.imgur.com/k9ny6Fr.jpeg',
  'the heights salva':           'https://i.imgur.com/k9ny6Fr.jpeg',
  'binghatti':              'https://i.imgur.com/Qh35AiR.jpeg',
  'rena':                        'https://i.imgur.com/5f9HOrF.jpeg',
  'address residences':          'https://i.imgur.com/EneU4Ud.jpeg',
  'address residence':           'https://i.imgur.com/EneU4Ud.jpeg',
  'ocean star':                  'https://i.imgur.com/CdkYtCF.jpeg',
  'the edge':                    'https://i.imgur.com/Gzoo40q.jpeg',
  'wynwood':                     'https://i.imgur.com/Bup61sa.jpeg',
  'wynwood residences':          'https://i.imgur.com/Bup61sa.jpeg',
  'creek vistas heights':          'https://i.imgur.com/U1UdSvR.jpeg',
   'vistas heights':          'https://i.imgur.com/U1UdSvR.jpeg',
   'azizi mirage':                'https://i.imgur.com/uC4rzLM.jpeg',
 'club drive':                   'https://i.imgur.com/8GVxtij.jpeg',
  'sterling':                    'https://i.imgur.com/jBpJvx2.jpeg',
  'aeon':                        'https://i.imgur.com/fQ8BnpN.jpeg',
  'one park central':            'https://i.imgur.com/4lNc5Zb.jpeg',
  'saria':                        'https://i.imgur.com/OT6cYbn.jpeg',
  'seahaven':                        'https://i.imgur.com/Rvg80bH.jpeg',
  'verve':                        'https://i.imgur.com/yhqKBGN.jpeg',
  'samana lakes view':           'https://i.imgur.com/zhZ5t5s.jpeg',
  'samana lakes':                'https://i.imgur.com/zhZ5t5s.jpeg',
  'nautica':                     'https://i.imgur.com/Qgn9Poh.jpeg',
  'creek waters 2':              'https://i.imgur.com/IBbv4Bb.jpeg',
  'creek waters':                'https://i.imgur.com/IBbv4Bb.jpeg',
  'marina place 2':              'https://i.imgur.com/f2Ciq1v.jpeg',
  'royal atlantis':              'https://i.imgur.com/jTEqN2m.png',
  'empire heights':              'https://i.imgur.com/6xLTMnc.png',
  'jumeira business center':     'https://i.imgur.com/CKKxlsD.png',
  'jumeirah business center':    'https://i.imgur.com/CKKxlsD.png',
  'jumeira business centre':     'https://i.imgur.com/CKKxlsD.png',
  'jumeirah business centre':    'https://i.imgur.com/CKKxlsD.png',
  'solara tower':                'https://i.imgur.com/V6amwYt.png',
  'solara':                      'https://i.imgur.com/V6amwYt.png',
};

interface PropertyViewProps {
  units: any[];
  documents: DocumentData[];
  currentInvestor: any;
  apiUrl?: string;
  handleFileUpload: (event: React.ChangeEvent<HTMLInputElement>) => void;
  downloadDocument: (doc: DocumentData) => Promise<void>;
  documentCategory: string;
  setDocumentCategory: (value: string) => void;
  selectedUnitForDoc: string;
  setSelectedUnitForDoc: (value: string) => void;
  documentType: string;
  setDocumentType: (value: string) => void;
  uploadingDocument: boolean;
  expandedFolders: Set<string>;
  toggleFolder: (folderName: string) => void;
  isLoadingData: boolean;
  paginationInfo: any;
  currentPage: number;
  documentSummary: any;
  searchTerm: string;
  setSearchTerm: (value: string) => void;
  selectedDocType: string;
  setSelectedDocType: (value: string) => void;
  isSearching: boolean;
  searchResults: any[];
  loadMoreDocuments: () => void;
  searchDocuments: () => void;
  clearSearch: () => void;
  onUnitCreated: (unit: any) => void;
  /** Push one changed unit back into the portal's units state (used after a sale is recorded). */
  onUnitUpdated?: (unit: any) => void;
  reloadDataFromFirebase: () => Promise<void>;
  handleSmartUpload?: (event: React.ChangeEvent<HTMLInputElement>, unitId?: string) => void;
  smartUploading?: boolean;
  /**
   * Hide the Properties / Cashflow / Personal / Company bar.
   *
   * In the owner shell those four no longer belong together: Cashflow is a
   * tool in More, Personal documents moved to the profile they belong to, and
   * Company documents has its own entry. What is left on this screen is the
   * properties, so the bar would be a row with one live tab.
   */
  hideMainTabs?: boolean;
  /** Which of those sections to open on. Default 'properties'. */
  initialMainTab?: 'properties' | 'cashflow' | 'personal' | 'company';
  /**
   * Render the opened property's detail instead of this file's own.
   *
   * The owner shell hands back the Property Management detail — lifecycle,
   * tenancy, inspection, maintenance, inventory and the unit's documents —
   * because that is what an owner opens a property to see. The list stays
   * here (the cards, the status buckets, the sale flows); only the screen
   * behind a tap changes.
   */
  renderUnitDetail?: (unitId: string, onBack: () => void) => React.ReactNode;
  /**
   * Rendered above the status buckets. The shell puts the two portfolio-wide
   * reports here — they used to sit on the Property Management list, which is
   * no longer where an owner looks for their properties.
   */
  headerActions?: React.ReactNode;
  smartUploadProgress?: { current: number; total: number; fileName: string };
}

interface Unit {
  id: string;
  investorId: string;
  name: string;
  unitNumber: string;
  project: string;
  type: string;
  area: string;
  location: string;
  purchaseValue: number;
  currentValue: number;
  monthlyRental: number;
  occupancyStatus: string;
  appreciationAmount: number;
  appreciationPercentage: number;
  bedrooms?: number;
  floor?: number;
  purchaseDate?: string | null;
  completionDate?: string | null;
  estimatedCompletionDate?: string | null;
  constructionCompletionPercent?: number | null;
  constructionStatus?: string | null;
  constructionDeveloper?: string | null;
  constructionStartDate?: string | null;
  constructionCompletionDate?: string | null;
  constructionRegistrationDate?: string | null;
  constructionProjectNumber?: string | null;
  /** Handover date the backend settled on: DLD when real, else the SPA/OTP date. */
  completionDateResolved?: string | null;
  /** Which record that date came from — 'DLD' | 'SPA' | 'OTP' | 'Booking Form' | … */
  completionDateSource?: string | null;
  constructionLastSync?: string | null;
}

interface NewUnitForm {
  name: string;
  unitNumber: string;
  project: string;
  type: string;
  area: string;
  bedrooms: string;
  purchaseDate: string;
  completionDate: string;
  occupancyStatus: string;
}

// ============================================
// DUAL CURRENCY DISPLAY COMPONENT
// ============================================
const DualCurrencyDisplay: React.FC<{
  aedValue: number;
  label?: string;
  size?: 'small' | 'medium' | 'large';
  showEuro?: boolean;
}> = ({ aedValue, label, size = 'medium', showEuro = true }) => {
  const sizes = {
    small: { aed: '0.9375rem', eur: '0.75rem' },
    medium: { aed: '1.125rem', eur: '0.8125rem' },
    large: { aed: '1.5rem', eur: '0.875rem' }
  };
  
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
      <span style={{
        fontWeight: 700,
        color: '#111827',
        fontSize: sizes[size].aed
      }}>
        AED {aedValue.toLocaleString('en-AE')}
      </span>
      {showEuro && aedValue > 0 && (
        <span style={{
          fontSize: sizes[size].eur,
          color: '#6b7280',
          fontWeight: 500,
          marginTop: '2px'
        }}>
          ≈ {formatEUR(aedValue)}
        </span>
      )}
    </div>
  );
};

// ============================================
// AREA DISPLAY COMPONENT (Smart SQM/SQFT detection)
// ============================================
const AreaDisplay: React.FC<{
  sqft: number | string;
  showSecondary?: boolean;
}> = ({ sqft, showSecondary = true }) => {
  const areaNum = typeof sqft === 'string' ? parseFloat(sqft) || 0 : sqft;
  const areaDisplay = formatAreaDisplay(areaNum);
  
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
      <span style={{
        fontWeight: 600,
        color: '#111827',
        fontSize: '0.9375rem'
      }}>
        {areaDisplay.primary}
      </span>
      {showSecondary && areaNum > 0 && (
        <span style={{
          fontSize: '0.75rem',
          color: '#6b7280',
          fontWeight: 500,
          marginTop: '2px'
        }}>
          ≈ {areaDisplay.secondary}
        </span>
      )}
    </div>
  );
};

// ============================================
// COMING SOON COMPONENT FOR CASHFLOW
// ============================================
const CashflowComingSoon: React.FC = () => {
  const { t } = useLanguage();
  
  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] p-6">
      {/* Decorative Icon */}
      <div className="relative mb-8">
        <div className="w-32 h-32 rounded-full bg-gradient-to-br from-[#D6B160]/20 to-[#D6B160]/5 flex items-center justify-center">
          <div className="w-24 h-24 rounded-full bg-gradient-to-br from-[#D6B160]/30 to-[#D6B160]/10 flex items-center justify-center">
            <TrendingUp className="h-12 w-12 text-[#D6B160]" />
          </div>
        </div>
        {/* Animated pulse ring */}
        <div className="absolute inset-0 rounded-full border-2 border-[#D6B160]/30 animate-ping" style={{ animationDuration: '2s' }} />
      </div>
      
      {/* Title */}
      <h2 className="text-2xl font-bold text-gray-900 mb-3 text-center">
        {t('cashflowComingSoon') || 'Cashflow Tracker'}
      </h2>
      
      {/* Coming Soon Badge */}
      <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#D6B160]/10 border border-[#D6B160]/30 rounded-full mb-6">
        <Clock className="h-4 w-4 text-[#D6B160]" />
        <span className="text-sm font-semibold text-[#D6B160]">
          {t('comingSoon') || 'Coming Soon'}
        </span>
      </div>
      
      {/* Description */}
      <p className="text-gray-600 text-center max-w-md mb-8 leading-relaxed">
        {t('cashflowComingSoonDescription') || 
          'We\'re working on an advanced cashflow tracking system to help you monitor your payment schedules, track milestones, and manage your property investments more effectively.'}
      </p>
      
      {/* Feature Preview Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 w-full max-w-lg">
        <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm text-center">
          <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-2">
            <CheckCircle className="h-5 w-5 text-green-600" />
          </div>
          <p className="text-xs font-medium text-gray-700">
            {t('paymentTracking') || 'Payment Tracking'}
          </p>
        </div>
        
        <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm text-center">
          <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-2">
            <Calendar className="h-5 w-5 text-blue-600" />
          </div>
          <p className="text-xs font-medium text-gray-700">
            {t('milestoneAlerts') || 'Milestone Alerts'}
          </p>
        </div>
        
        <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm text-center">
          <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center mx-auto mb-2">
            <TrendingUp className="h-5 w-5 text-purple-600" />
          </div>
          <p className="text-xs font-medium text-gray-700">
            {t('progressReports') || 'Progress Reports'}
          </p>
        </div>
      </div>
      
      {/* Notification Option */}
      <div className="mt-8 p-4 bg-gray-50 rounded-xl border border-gray-200 w-full max-w-md">
        <p className="text-sm text-gray-600 text-center">
          <span className="font-medium text-gray-900">
            {t('stayTuned') || 'Stay tuned!'}
          </span>
          {' '}
          {t('notifyWhenReady') || 'We\'ll notify you when this feature is ready.'}
        </p>
      </div>
    </div>
  );
};

// ============================================
// COMING SOON COMPONENT FOR INCOME
// ============================================
const IncomeComingSoon: React.FC = () => {
  const { t } = useLanguage();
  
  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-br from-[#1a1a1a] to-[#2d2d2d] p-6 text-center">
        <div className="relative inline-flex items-center justify-center mb-4">
          {/* Animated pulse ring */}
          <div className="absolute w-16 h-16 bg-[#D6B160] rounded-full opacity-20 animate-ping" style={{ animationDuration: '2s' }} />
          <div className="relative w-14 h-14 bg-[#D6B160] rounded-full flex items-center justify-center">
            <DollarSign className="h-7 w-7 text-white" />
          </div>
        </div>
        
        {/* Coming Soon Badge */}
        <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#D6B160]/20 rounded-full mb-3">
          <Clock className="h-3.5 w-3.5 text-[#D6B160]" />
          <span className="text-[#D6B160] font-semibold text-xs">
            {t('comingSoon') || 'Coming Soon'}
          </span>
        </div>
        
        <h2 className="text-xl font-bold text-white mb-2">
          {t('incomeTracking') || 'Income Tracking'}
        </h2>
        <p className="text-gray-400 text-sm max-w-sm mx-auto">
          {t('incomeComingSoonDesc') || 'We\'re building comprehensive income tracking to help you monitor rental income, ROI, and financial performance.'}
        </p>
      </div>
      
      {/* Feature Preview */}
      <div className="p-5 bg-gray-50">
        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3 text-center">
          {t('upcomingFeatures') || 'What\'s Coming'}
        </h3>
        
        <div className="grid grid-cols-3 gap-3">
          {/* Rental Income */}
          <div className="bg-white rounded-lg p-3 border border-gray-200 text-center">
            <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center mx-auto mb-2">
              <DollarSign className="h-4 w-4 text-green-600" />
            </div>
            <p className="text-xs font-medium text-gray-700">
              {t('rentalIncome') || 'Rental Income'}
            </p>
          </div>
          
          {/* ROI Analysis */}
          <div className="bg-white rounded-lg p-3 border border-gray-200 text-center">
            <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-2">
              <PieChart className="h-4 w-4 text-blue-600" />
            </div>
            <p className="text-xs font-medium text-gray-700">
              {t('roiAnalysis') || 'ROI Analysis'}
            </p>
          </div>
          
          {/* Expense Tracking */}
          <div className="bg-white rounded-lg p-3 border border-gray-200 text-center">
            <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center mx-auto mb-2">
              <Wallet className="h-4 w-4 text-purple-600" />
            </div>
            <p className="text-xs font-medium text-gray-700">
              {t('expenseTracking') || 'Expenses'}
            </p>
          </div>
        </div>
      </div>
      
      {/* Stay Tuned Message */}
      <div className="p-3 bg-[#D6B160]/10 border-t border-[#D6B160]/20">
        <p className="text-xs text-[#B8963D] text-center font-medium">
          {t('stayTuned') || 'Stay tuned! This feature will be available soon.'}
        </p>
      </div>
    </div>
  );
};

const PropertyView: React.FC<PropertyViewProps> = ({
  units,
  documents,
  currentInvestor,
  apiUrl,
  handleFileUpload,
  downloadDocument,
  documentCategory,
  setDocumentCategory,
  selectedUnitForDoc,
  setSelectedUnitForDoc,
  documentType,
  setDocumentType,
  uploadingDocument,
  expandedFolders,
  toggleFolder,
  isLoadingData,
  paginationInfo,
  currentPage,
  documentSummary,
  searchTerm,
  setSearchTerm,
  selectedDocType,
  setSelectedDocType,
  isSearching,
  searchResults,
  loadMoreDocuments,
  searchDocuments,
  clearSearch,
  onUnitCreated,
  onUnitUpdated,
  reloadDataFromFirebase,
  handleSmartUpload,
  smartUploading,
  smartUploadProgress,
  hideMainTabs,
  initialMainTab,
  renderUnitDetail,
  headerActions
}) => {
  const { t, language } = useLanguage();

  const [dynamicProjectImages, setDynamicProjectImages] = useState<{ [key: string]: string }>({});
  useEffect(() => {
    if (!apiUrl) return;
    fetch(`${apiUrl}/project-images`)
      .then(r => r.ok ? r.json() : {})
      .then(data => setDynamicProjectImages(data || {}))
      .catch(() => {});
  }, [apiUrl]);

  const [selectedProperty, setSelectedProperty] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'summary' | 'income' | 'documents'>('summary');
  const [dldUnitData, setDldUnitData] = useState<any>(null);
  const [dldLoading, setDldLoading] = useState(false);

  // Calculate effective current value (with appreciation) for selected unit
  const getEffectiveUnitValue = (unit: any) => {
    if (!unit) return 0;
    const purchase = unit.purchaseValue || 0;
    let appr = unit.appreciationAmount || 0;
    if (appr === 0 && unit.purchaseDate) {
      const purchaseDate = new Date(unit.purchaseDate);
      const now = new Date();
      const years = (now.getTime() - purchaseDate.getTime()) / (1000 * 60 * 60 * 24 * 365);
      appr = Math.round(purchase * 0.08 * years);
    }
    return appr > 0 ? purchase + appr : (unit.currentValue || 0);
  };

  // Fetch DLD market intelligence for selected unit
  useEffect(() => {
    if (!selectedProperty || !apiUrl) return;
    const unit = units.find(u => u.id === selectedProperty);
    if (unit?.name?.toLowerCase() === 'other documents') return;
    const token = localStorage.getItem('authToken');
    if (!token) return;
    setDldLoading(true);
    setDldUnitData(null);
    fetch(`${apiUrl}/dld/unit-intelligence/${selectedProperty}`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data?.success) setDldUnitData(data.data);
        setDldLoading(false);
      })
      .catch(() => setDldLoading(false));
  }, [selectedProperty, apiUrl]);
  const [showAddPropertyModal, setShowAddPropertyModal] = useState(false);
  const [showAddDocumentsModal, setShowAddDocumentsModal] = useState(false);
  const [viewerBlob, setViewerBlob] = useState<Blob | null>(null);
  const [viewerFileName, setViewerFileName] = useState<string>('');
  const [viewerContentType, setViewerContentType] = useState<string>('');
const [mainTab, setMainTab] = useState<'properties' | 'cashflow' | 'personal' | 'company'>(initialMainTab || 'properties');
const [cashflowFilter, setCashflowFilter] = useState<'all' | string>('all');
const [paymentStatuses, setPaymentStatuses] = useState<Record<string, string>>({});
const [savingStatus, setSavingStatus] = useState<string | null>(null);
  const [showAddUnitForm, setShowAddUnitForm] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [localSearchTerm, setLocalSearchTerm] = useState('');
const [localFilterType, setLocalFilterType] = useState('All');
const [filteredDocs, setFilteredDocs] = useState<any[]>([]);
const [unitSearchTerm, setUnitSearchTerm] = useState('');
const [propertySearchTerm, setPropertySearchTerm] = useState('');
const [showUnitRequest, setShowUnitRequest] = useState(false);
const [requestMethod, setRequestMethod] = useState('');
const [requestUnitName, setRequestUnitName] = useState('');
const [requestProjectName, setRequestProjectName] = useState('');
const [requestUnitDocType, setRequestUnitDocType] = useState('');
const [requestUnitNotes, setRequestUnitNotes] = useState('');

// --- Property Status Tab (Active / Selling / Sold) ---
const [propertyStatusTab, setPropertyStatusTab] = useState<'active' | 'rented' | 'selling' | 'sold'>('active');

// --- Apartments / Offices folders — only for a portfolio holding both ---
const [propertyFolder, setPropertyFolder] = useState<UnitCategory | null>(() => {
  try {
    const v = sessionStorage.getItem('propertyFolder');
    return v === 'residential' || v === 'commercial' ? v : null;
  } catch { return null; }
});
const openPropertyFolder = (f: UnitCategory | null) => {
  setPropertyFolder(f);
  setPropertyStatusTab('active');
  try { f ? sessionStorage.setItem('propertyFolder', f) : sessionStorage.removeItem('propertyFolder'); } catch { /* storage blocked */ }
};
const realUnits = units.filter(u => !isOtherDocsUnit(u));
const hasBothCategories = portfolioHasBoth(realUnits);
// A search looks across the whole portfolio, so it bypasses the folders.
const inFolder = hasBothCategories && propertyFolder !== null && !propertySearchTerm;
const showFolderPicker = hasBothCategories && propertyFolder === null && !propertySearchTerm;
const tabUnits = inFolder
  ? units.filter(u => isOtherDocsUnit(u) || unitCategory(u) === propertyFolder)
  : units;

// --- Building groups (owner, 2026-11-05) ---
// Two or more units in one building show as ONE group card on the Active and
// Rented tabs; tapping it opens just that building. No folders to maintain:
// a new unit joins its building by itself. Keyed on the project, with a
// trailing list of unit numbers dropped ("Dubai Star 907, 908, 909" ->
// "Dubai Star") but a single building number kept ("Jumeirah Business
// Center 5" stays apart from "... 4").
const [openBuilding, setOpenBuilding] = useState<string | null>(null);
useEffect(() => { setOpenBuilding(null); }, [propertyFolder, propertyStatusTab, propertySearchTerm]);
const buildingName = (u: any) => String(u.project || u.name || '').trim().replace(/\s+\d+(\s*,\s*\d+)+\s*$/, '').replace(/\s+/g, ' ');
const buildingKey = (u: any) => buildingName(u).toLowerCase();
const inStatusTab = (u: any) => (propertyStatusTab === 'rented'
  ? (u.status || '').toLowerCase() === 'rented'
  : (!u.status || u.status === 'active' || u.status === 'selling' || (u.status || '').toLowerCase() === 'rented'));
const buildingGroups = (() => {
  if (propertySearchTerm || (propertyStatusTab !== 'active' && propertyStatusTab !== 'rented')) return [] as { key: string; name: string; units: any[] }[];
  const m = new globalThis.Map<string, { key: string; name: string; units: any[] }>();
  tabUnits.filter(u => !isOtherDocsUnit(u) && inStatusTab(u)).forEach((u) => {
    const k = buildingKey(u);
    if (!k) return;
    if (!m.has(k)) m.set(k, { key: k, name: buildingName(u), units: [] });
    m.get(k)!.units.push(u);
  });
  return Array.from(m.values()).filter(g => g.units.length >= 2).sort((a, b) => b.units.length - a.units.length || a.name.localeCompare(b.name));
})();
const groupedKeys = new Set(buildingGroups.map(g => g.key));
const openGroup = openBuilding ? buildingGroups.find(g => g.key === openBuilding) || null : null;

// --- Sell Platform State ---
const [showSellModal, setShowSellModal] = useState(false);
const [sellLoading, setSellLoading] = useState(false);
const [sellStatus, setSellStatus] = useState<Record<string, { listed: boolean; status?: string; createdAt?: string }>>({});
const [sellResult, setSellResult] = useState<{ success: boolean; sellerFormUrl?: string; credentials?: any } | null>(null);
const [sellIsDistress, setSellIsDistress] = useState(false);
const [sellOriginalPrice, setSellOriginalPrice] = useState('');
const [sellAskingPrice, setSellAskingPrice] = useState('');

// --- Sell Eligibility: fetch installment plans to check ≥50% paid ---
const [unitPaidPercent, setUnitPaidPercent] = useState<Record<string, number>>({});
// Paid-in and still-open AED per unit, from the same payment plans. An off-plan
// sale is an assignment, and its ROI is measured against the money actually
// paid to the developer — so the sale form prefills from here.
const [unitPaidAmounts, setUnitPaidAmounts] = useState<Record<string, { paid: number; open: number; total: number }>>({});
useEffect(() => {
  if (!currentInvestor?.id) return;
  const fetchPlans = async () => {
    try {
      const authToken = localStorage.getItem('authToken');
      const headers: Record<string, string> = {};
      if (authToken) headers.Authorization = `Bearer ${authToken}`;
      const res = await fetch(`${backendUrl}/api/installment-plans/${encodeURIComponent(currentInvestor.id)}`, { headers });
      if (res.ok) {
        const plans = await res.json();
        const pctMap: Record<string, number> = {};
        const amtMap: Record<string, { paid: number; open: number; total: number }> = {};
        (Array.isArray(plans) ? plans : []).forEach((plan: any) => {
          const milestones = plan.milestones || [];
          let totalAmt = 0, paidAmt = 0;
          milestones.forEach((m: any) => {
            totalAmt += m.amount || 0;
            if (m.status === 'paid') paidAmt += m.amount || 0;
          });
          if (totalAmt > 0) {
            pctMap[plan.unitId] = (paidAmt / totalAmt) * 100;
          }
          // planPaidToDate applies the owner's rule as well: an SPA-sourced
          // milestone keeps status 'pending' forever, so anything already past
          // its due date counts as paid. The strict `status === 'paid'` count
          // above still governs sell eligibility, which must stay conservative.
          const { paid, open, total } = planPaidToDate(plan);
          if (total > 0) amtMap[plan.unitId] = { paid, open, total };
        });
        setUnitPaidPercent(pctMap);
        setUnitPaidAmounts(amtMap);
      }
    } catch (err) {
      console.error('Error fetching installment plans for sell eligibility:', err);
    }
  };
  fetchPlans();
}, [currentInvestor?.id]);

const isEligibleForSelling = (unitId: string): boolean => {
  const pct = unitPaidPercent[unitId];
  return pct !== undefined && pct >= 50;
};

// --- Owner-recorded sale (Mark as sold → ROI) ---
// "Sell This Unit" above lists a unit on the sell platform; this is the other
// half — recording the sale that actually happened, wherever it happened, and
// pricing what was left after the transfer bill.
interface SoldFormExpense { label: string; amount: string }
interface SoldForm {
  salePrice: string;
  saleDate: string;
  purchasePrice: string;
  dldFee: string;
  commission: string;
  trusteeFee: string;
  /** off-plan only: developer No-Objection Certificate */
  nocFee: string;
  /** off-plan only: developer administration / assignment fee */
  developerAdminFee: string;
  /** off-plan only: how the unit was acquired — decides what the ROI base is */
  acquisitionRoute: AcquisitionRoute;
  /** off-plan only: price paid to a previous owner to take the SPA over */
  acquisitionPrice: string;
  /**
   * off-plan, taken-over units only: the balance still owed to the developer
   * when this owner took the SPA over. The assignment price on the DLD form is
   * the whole value of the unit and that balance came with it, so only the
   * difference was ever paid to the previous owner.
   */
  assumedBalance: string;
  /** off-plan only: installments THIS owner paid to the developer */
  paidToDate: string;
  /** off-plan only: balance the buyer takes over */
  outstandingBalance: string;
  /**
   * How this owner came to hold the unit, for the ACQUISITION COSTS only.
   * '' until they say, because the answer decides which fees existed: a unit
   * taken over from another owner carried the 4% DLD, the agency's 2% and a
   * trustee fee; one bought from the developer carried the 4% registration and
   * neither of the other two. Prefilling before we know invents spending.
   */
  boughtFrom: '' | 'developer' | 'owner';
  /** what acquiring the unit cost on top of the price \u2014 capital, not profit */
  purchaseDldFee: string;
  purchaseCommission: string;
  purchaseTrusteeFee: string;
  purchaseOtherCosts: string;
  otherExpenses: SoldFormExpense[];
  notes: string;
}
const todayISO = () => new Date().toISOString().split('T')[0];
const emptySoldForm = (): SoldForm => ({
  salePrice: '', saleDate: todayISO(), purchasePrice: '',
  dldFee: '', commission: '', trusteeFee: '', nocFee: '', developerAdminFee: '',
  acquisitionRoute: 'developer', acquisitionPrice: '', assumedBalance: '',
  paidToDate: '', outstandingBalance: '',
  boughtFrom: '', purchaseDldFee: '', purchaseCommission: '', purchaseTrusteeFee: '', purchaseOtherCosts: '',
  otherExpenses: [], notes: ''
});

const [soldUnitId, setSoldUnitId] = useState<string | null>(null);
const [soldForm, setSoldForm] = useState<SoldForm>(emptySoldForm());
// Which fee fields the owner has typed into. An untouched fee tracks the sale
// price at the standard rate; the moment it is edited it stops moving, because
// the entered number is the invoice and the rate was only ever a guess.
const [soldFeeEdited, setSoldFeeEdited] = useState<{ dld: boolean; commission: boolean; trustee: boolean }>(
  { dld: false, commission: false, trustee: false }
);
// The acquisition fees, same rule: an untouched field follows the standard rate
// off the price paid in; once typed into it holds what the invoice said.
const [soldBuyFeeEdited, setSoldBuyFeeEdited] = useState<{ dld: boolean; commission: boolean; trustee: boolean }>(
  { dld: false, commission: false, trustee: false }
);
// Same idea for the developer balance: it follows paid-in (SPA price − paid)
// until the owner types the figure from their own statement of account.
const [soldOutstandingEdited, setSoldOutstandingEdited] = useState(false);
const [soldSaving, setSoldSaving] = useState(false);
const [soldError, setSoldError] = useState<string | null>(null);

const soldUnit = soldUnitId ? units.find(u => u.id === soldUnitId) : null;

const parseMoney = (value: string): number => {
  const n = parseFloat((value || '').replace(/,/g, ''));
  return Number.isFinite(n) && n > 0 ? n : 0;
};

/** Purchase price on record. Empty on units registered before their OTP/SPA landed. */
const purchaseOnRecord = (unit: any): number =>
  Number(unit?.purchaseValue) || Number(unit?.purchasePrice) || Number(unit?.propertyValue) || 0;

const openSoldModal = (unit: any) => {
  const existing = summariseSale(unit as any);
  const known = purchaseOnRecord(unit);
  // Off-plan = no title yet, so the sale is an assignment of the SPA. A sale
  // already recorded keeps the route it was priced under.
  const offplan = existing ? existing.saleType === 'assignment' : isOffplanUnit(unit as any);
  const fromPlan = unitPaidAmounts[unit.id];
  setSoldError(null);
  if (existing) {
    // Editing a recorded sale: every figure comes back as it was stored, and
    // no fee is allowed to drift when the sale price is corrected.
    setSoldForm({
      salePrice: String(existing.salePrice || ''),
      saleDate: (unit.saleDate || unit.soldAt || '').split('T')[0] || todayISO(),
      purchasePrice: known > 0 ? '' : String(existing.purchase || ''),
      dldFee: existing.dldFee ? String(existing.dldFee) : '',
      commission: existing.commission ? String(existing.commission) : '',
      trusteeFee: existing.trusteeFee ? String(existing.trusteeFee) : '',
      nocFee: existing.nocFee ? String(existing.nocFee) : '',
      developerAdminFee: existing.developerAdminFee ? String(existing.developerAdminFee) : '',
      acquisitionRoute: existing.acquisitionRoute || 'developer',
      acquisitionPrice: existing.acquisitionPrice ? String(existing.acquisitionPrice) : '',
      assumedBalance: existing.assumedBalance ? String(existing.assumedBalance) : '',
      paidToDate: existing.saleType === 'assignment' && existing.paidToDate
        ? String(existing.paidToDate) : '',
      outstandingBalance: existing.saleType === 'assignment' && existing.outstandingBalance
        ? String(existing.outstandingBalance) : '',
      // Acquisition costs come back as stored. `boughtFrom` is inferred from
      // them rather than kept: an agency fee on the way in only happens on a
      // unit taken over from another owner.
      boughtFrom: existing.purchaseCommission > 0 ? 'owner'
        : (existing.purchaseDldFee > 0 ? 'developer' : ''),
      purchaseDldFee: existing.purchaseDldFee ? String(existing.purchaseDldFee) : '',
      purchaseCommission: existing.purchaseCommission ? String(existing.purchaseCommission) : '',
      purchaseTrusteeFee: existing.purchaseTrusteeFee ? String(existing.purchaseTrusteeFee) : '',
      purchaseOtherCosts: existing.purchaseOtherCosts ? String(existing.purchaseOtherCosts) : '',
      otherExpenses: existing.otherExpenses.map(e => ({ label: e.label, amount: String(e.amount) })),
      notes: unit.saleNotes || ''
    });
    setSoldFeeEdited({ dld: true, commission: true, trustee: true });
    setSoldBuyFeeEdited({ dld: true, commission: true, trustee: true });
    setSoldOutstandingEdited(true);
  } else {
    const form = emptySoldForm();
    // Prefill the capital base from the payment plan when there is one. It
    // stays editable: units whose documents predate the plan extractor have no
    // plan at all, and a payment made outside the schedule will not appear.
    if (offplan && fromPlan && fromPlan.paid > 0) {
      form.paidToDate = String(Math.round(fromPlan.paid));
      form.outstandingBalance = String(Math.max(0, Math.round((known || fromPlan.total) - fromPlan.paid)));
    }
    setSoldForm(form);
    setSoldFeeEdited({ dld: false, commission: false, trustee: false });
    setSoldBuyFeeEdited({ dld: false, commission: false, trustee: false });
    setSoldOutstandingEdited(false);
  }
  setSoldUnitId(unit.id);
};

/**
 * The sale price drives the agency's 2%, and nothing else.
 *
 * In a Dubai transfer the BUYER pays the 4% DLD and the trustee office — they
 * are the cost of registering the unit into their name, not of selling it.
 * Prefilling them here billed the seller AED 65,000 they never paid and cut
 * the reported profit by that much. They stay empty unless the seller says
 * they carried part of them; the agent's fee is the one that is normally
 * theirs, so it is the only one that still follows the price.
 */
const setSoldSalePrice = (raw: string) => {
  const price = parseMoney(raw);
  setSoldForm(prev => ({
    ...prev,
    salePrice: raw,
    commission: soldFeeEdited.commission ? prev.commission : (price > 0 ? String(Math.round(price * AGENCY_COMMISSION_RATE)) : '')
  }));
};

// Which route this sale takes. A unit already marked sold keeps the route it
// was recorded under, so editing an old sale never silently re-prices it.
const soldSaleType: SaleType = soldUnit
  ? (soldUnit.status === 'sold'
      ? (soldUnit.saleType === 'assignment' ? 'assignment' : 'transfer')
      : (isOffplanUnit(soldUnit as any) ? 'assignment' : 'transfer'))
  : 'transfer';
const soldIsAssignment = soldSaleType === 'assignment';

// Live ROI for the form — the same maths the backend stores and the report reads.
const soldPreview: SaleSummary = computeSale({
  salePrice: soldForm.salePrice,
  purchasePrice: purchaseOnRecord(soldUnit) || soldForm.purchasePrice,
  purchaseDate: soldUnit?.purchaseDate || soldUnit?.otpDate || soldUnit?.createdAt,
  saleDate: soldForm.saleDate,
  dldFee: soldForm.dldFee,
  commission: soldForm.commission,
  trusteeFee: soldForm.trusteeFee,
  nocFee: soldForm.nocFee,
  developerAdminFee: soldForm.developerAdminFee,
  otherExpenses: soldForm.otherExpenses.map(e => ({ label: e.label, amount: parseMoney(e.amount) })),
  saleType: soldSaleType,
  paidToDate: soldForm.paidToDate,
  outstandingBalance: soldForm.outstandingBalance ? soldForm.outstandingBalance : undefined,
  acquisitionRoute: soldForm.acquisitionRoute,
  acquisitionPrice: soldForm.acquisitionPrice,
  assumedBalance: soldForm.assumedBalance,
  purchaseDldFee: soldForm.purchaseDldFee,
  purchaseCommission: soldForm.purchaseCommission,
  purchaseTrusteeFee: soldForm.purchaseTrusteeFee,
  purchaseOtherCosts: soldForm.purchaseOtherCosts
});

/**
 * Answering "how did you buy it?" fills the fees that route actually carries,
 * off the price this owner paid in. Anything already typed by hand is left
 * alone. Choosing 'developer' clears the agency and trustee fees rather than
 * zeroing them silently \u2014 that purchase never involved either.
 */
const setSoldBoughtFrom = (how: '' | 'developer' | 'owner') => {
  // The DLD takes its 4% on the value declared in the transaction, and the
  // agency its 2% on the same figure — not on the cash that changed hands and
  // not on the installments paid since. On a taken-over unit that is the
  // assignment price; otherwise it is the SPA price.
  const priceIn = soldSaleType === 'assignment' && soldForm.acquisitionRoute === 'assigned-in'
    ? parseMoney(soldForm.acquisitionPrice)
    : (purchaseOnRecord(soldUnit) || parseMoney(soldForm.purchasePrice));
  setSoldForm(prev => ({
    ...prev,
    boughtFrom: how,
    purchaseDldFee: soldBuyFeeEdited.dld ? prev.purchaseDldFee
      : (how && priceIn > 0 ? String(Math.round(priceIn * DLD_TRANSFER_RATE)) : ''),
    purchaseCommission: soldBuyFeeEdited.commission ? prev.purchaseCommission
      : (how === 'owner' && priceIn > 0 ? String(Math.round(priceIn * AGENCY_COMMISSION_RATE)) : ''),
    purchaseTrusteeFee: soldBuyFeeEdited.trustee ? prev.purchaseTrusteeFee
      : (how === 'owner' && priceIn > 0 ? String(trusteeFeeFor(priceIn)) : '')
  }));
};

const closeSoldModal = () => {
  if (soldSaving) return;
  setSoldUnitId(null);
  setSoldError(null);
};

const handleSaveSale = async () => {
  if (!soldUnitId) return;
  if (soldPreview.salePrice <= 0) {
    setSoldError(language === 'de' ? 'Bitte einen Verkaufspreis eingeben.' : 'Please enter the sale price.');
    return;
  }
  // Without a capital base there is no return to report, only proceeds — and a
  // "profit" equal to the whole sale price would land in the portfolio's
  // realised total. Which field is missing depends on the route.
  if (!soldPreview.hasBasis) {
    if (soldIsAssignment && soldForm.acquisitionRoute === 'assigned-in') {
      setSoldError(language === 'de'
        ? 'Bitte eingeben, was Sie dem Voreigentümer gezahlt haben (und ggf. Ihre seitherigen Raten) — daran wird der ROI gemessen.'
        : 'Please enter what you paid the previous owner (and any installments since) — the ROI is measured against that capital.');
    } else if (soldIsAssignment) {
      setSoldError(language === 'de'
        ? 'Bitte den an den Entwickler gezahlten Betrag eingeben — daran wird der ROI gemessen.'
        : 'Please enter the amount paid to the developer — the ROI is measured against it.');
    } else {
      setSoldError(language === 'de'
        ? 'Bitte den Kaufpreis eingeben — ohne ihn lässt sich kein ROI berechnen.'
        : 'Please enter the purchase price — the ROI cannot be calculated without it.');
    }
    return;
  }
  setSoldSaving(true);
  setSoldError(null);
  try {
    const authToken = localStorage.getItem('authToken');
    const response = await fetch(`${backendUrl}/api/units/${encodeURIComponent(soldUnitId)}/sale`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(authToken ? { Authorization: `Bearer ${authToken}` } : {})
      },
      body: JSON.stringify({
        salePrice: soldPreview.salePrice,
        saleDate: soldForm.saleDate,
        purchasePrice: parseMoney(soldForm.purchasePrice),
        assumedBalance: soldPreview.assumedBalance,
        purchaseDldFee: soldPreview.purchaseDldFee,
        purchaseCommission: soldPreview.purchaseCommission,
        purchaseTrusteeFee: soldPreview.purchaseTrusteeFee,
        purchaseOtherCosts: soldPreview.purchaseOtherCosts,
        dldFee: soldPreview.dldFee,
        commission: soldPreview.commission,
        trusteeFee: soldPreview.trusteeFee,
        nocFee: soldPreview.nocFee,
        developerAdminFee: soldPreview.developerAdminFee,
        otherExpenses: soldPreview.otherExpenses,
        saleType: soldSaleType,
        paidToDate: soldPreview.paidToDate,
        outstandingBalance: soldPreview.outstandingBalance,
        acquisitionRoute: soldPreview.acquisitionRoute,
        acquisitionPrice: soldPreview.acquisitionPrice,
        notes: soldForm.notes
      })
    });
    const result = await response.json().catch(() => ({}));
    if (!response.ok || !result.success) {
      throw new Error(result.error || `HTTP ${response.status}`);
    }
    if (result.unit && onUnitUpdated) onUnitUpdated(result.unit);
    setSoldUnitId(null);
    setPropertyStatusTab('sold');
    setSelectedProperty(null);
  } catch (err: any) {
    console.error('Error recording sale:', err);
    setSoldError(err.message || (language === 'de' ? 'Speichern fehlgeschlagen.' : 'Could not save the sale.'));
  }
  setSoldSaving(false);
};

const handleRevertSale = async (unitId: string) => {
  const confirmed = window.confirm(language === 'de'
    ? 'Verkauf zurücknehmen? Die Einheit erscheint wieder im aktiven Portfolio.'
    : 'Undo this sale? The unit returns to your active portfolio.');
  if (!confirmed) return;
  try {
    const authToken = localStorage.getItem('authToken');
    const response = await fetch(`${backendUrl}/api/units/${encodeURIComponent(unitId)}/sale`, {
      method: 'DELETE',
      headers: authToken ? { Authorization: `Bearer ${authToken}` } : {}
    });
    const result = await response.json().catch(() => ({}));
    if (!response.ok || !result.success) throw new Error(result.error || `HTTP ${response.status}`);
    if (result.unit && onUnitUpdated) onUnitUpdated(result.unit);
    setPropertyStatusTab('active');
  } catch (err: any) {
    console.error('Error reverting sale:', err);
    alert(err.message || (language === 'de' ? 'Zurücknehmen fehlgeschlagen.' : 'Could not undo the sale.'));
  }
};

// --- Account shareholders --------------------------------------------------
// One account, several people behind it: an SPV, a family, a group who bought a
// set of units together. The register belongs to the ACCOUNT, not to a unit —
// the same split applies to everything held in it.
//
// It never scales a figure. The account owns each unit in full, so portfolio
// value, rent and realised profit all stay whole; the shares only say how a
// payout divides between the people behind the account.
interface ShareholderRow { name: string; email: string; sharePercent: string }
interface Shareholder { name: string; email: string; sharePercent: number }

const [shareholders, setShareholders] = useState<Shareholder[]>([]);
const [shOpen, setShOpen] = useState(false);
const [shRows, setShRows] = useState<ShareholderRow[]>([]);
const [shSaving, setShSaving] = useState(false);
const [shError, setShError] = useState<string | null>(null);

// The register rides in with the account profile. Serialised as the dependency
// because the parent hands back a fresh array on every render, and comparing
// the array itself would re-set state forever.
const shSerialised = JSON.stringify(currentInvestor?.shareholders || []);
useEffect(() => {
  const stored: any[] = JSON.parse(shSerialised);
  setShareholders(stored
    .filter((s: any) => s && (s.name || Number(s.sharePercent) > 0))
    .map((s: any) => ({
      name: s.name || '',
      email: s.email || '',
      sharePercent: Number(s.sharePercent) || 0
    })));
}, [shSerialised]);

const openShModal = () => {
  setShRows(shareholders.length
    ? shareholders.map(s => ({
        name: s.name,
        email: s.email,
        sharePercent: s.sharePercent ? String(s.sharePercent) : ''
      }))
    : [{ name: '', email: '', sharePercent: '' }]);
  setShError(null);
  setShOpen(true);
};

const shRowsTotal = shRows.reduce(
  (s, r) => s + (parseFloat((r.sharePercent || '').replace(/,/g, '')) || 0), 0);
const shTotal = shareholders.reduce((s, h) => s + h.sharePercent, 0);

/** Realised profit across every sold unit in the account — what there is to split. */
const accountRealisedProfit = units
  .filter((u: any) => (u.status || '').toLowerCase() === 'sold')
  .map((u: any) => summariseSale(u))
  .filter((s): s is SaleSummary => !!s && s.hasBasis)
  .reduce((sum, s) => sum + s.netProfit, 0);

const handleSaveShareholders = async () => {
  setShSaving(true);
  setShError(null);
  try {
    const authToken = localStorage.getItem('authToken');
    const response = await fetch(`${backendUrl}/api/account/shareholders`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(authToken ? { Authorization: `Bearer ${authToken}` } : {})
      },
      body: JSON.stringify({
        shareholders: shRows.map(r => ({
          name: r.name.trim(),
          email: r.email.trim(),
          sharePercent: parseFloat((r.sharePercent || '').replace(/,/g, '')) || 0
        }))
      })
    });
    const result = await response.json().catch(() => ({}));
    if (!response.ok || !result.success) throw new Error(result.error || `HTTP ${response.status}`);
    // The account profile is a prop, so the saved register is held here until
    // the portal reloads it.
    setShareholders(Array.isArray(result.shareholders) ? result.shareholders : []);
    setShOpen(false);
  } catch (err: any) {
    console.error('Error saving shareholders:', err);
    setShError(err.message || (language === 'de'
      ? 'Speichern fehlgeschlagen.' : 'Could not save the shareholders.'));
  }
  setShSaving(false);
};

  const [newUnitForm, setNewUnitForm] = useState<NewUnitForm>({
    name: '', unitNumber: '', project: '', type: 'Apartment', area: '',
    bedrooms: '1',
    purchaseDate: new Date().toISOString().split('T')[0],
    completionDate: '',
    occupancyStatus: 'Vacant'
  });

const performLocalSearch = () => {
  
  if (!localSearchTerm.trim() && localFilterType === 'All') {
    setFilteredDocs([]);
    return;
  }
  
  let results = documents;
  
  if (localFilterType !== 'All') {
    results = results.filter(doc => doc.type === localFilterType);
  }
  
  if (localSearchTerm.trim()) {
    const term = localSearchTerm.toLowerCase();
    results = results.filter(doc => {
      const searchIn = [
        doc.fileName || '',
        doc.type || '',
        doc.unitId || '',
        String(doc.extractedData?.property || ''),
        String(doc.extractedData?.unitNumber || '')
      ].join(' ').toLowerCase();
      
      const found = searchIn.includes(term);
      if (found) {
      }
      return found;
    });
  }
  
  setFilteredDocs(results);
};

const clearLocalSearch = () => {
  setLocalSearchTerm('');
  setLocalFilterType('All');
  setFilteredDocs([]);
};

// --- Sell Platform Functions ---
const checkSellStatus = async (unitId: string) => {
  try {
    const response = await fetch(`${backendUrl}/api/sell-platform/status/${unitId}`);
    if (response.ok) {
      const data = await response.json();
      setSellStatus(prev => ({ ...prev, [unitId]: { listed: data.listed, status: data.sellRequest?.status, createdAt: data.sellRequest?.createdAt } }));
    }
  } catch (err) {
    console.error('Error checking sell status:', err);
  }
};

const handleInitiateSell = async () => {
  if (!selectedUnit || !currentInvestor) return;

  // Validate pricing
  const askingPriceNum = parseFloat(sellAskingPrice.replace(/,/g, ''));
  if (!askingPriceNum || askingPriceNum <= 0) return;

  if (sellIsDistress) {
    const originalPriceNum = parseFloat(sellOriginalPrice.replace(/,/g, ''));
    if (!originalPriceNum || originalPriceNum <= 0) return;
  }

  setSellLoading(true);
  setSellResult(null);

  try {
    // Gather unit documents for transfer — match by unitId, unitNumber, or name
    const unitDocs = documents
      .filter(d => {
        if (!d.unitId) return false;
        return d.unitId === selectedUnit.id
          || d.unitId === selectedUnit.unitNumber
          || d.unitId === selectedUnit.name
          || String(d.unitId) === String(selectedUnit.id);
      })
      .map(d => ({
        name: d.fileName || d.name || 'document',
        url: d.fileUrl || d.url || d.downloadUrl || d.driveUrl || `${backendUrl}/api/getDocument?investorId=${currentInvestor.id}&documentId=${d.id}`,
        type: d.type || d.documentType || 'other'
      }));
    console.log(`📄 Sell: Found ${unitDocs.length} documents for unit ${selectedUnit.id} (${selectedUnit.unitNumber})`);

    // Get project photo URL (imgur)
    const merged = { ...FALLBACK_PROJECT_IMAGES, ...dynamicProjectImages };
    const projectKey = Object.keys(merged).find(k => selectedUnit.project?.toLowerCase().includes(k));
    const projectPhotoUrl = projectKey ? merged[projectKey] : null;

    const response = await fetch(`${backendUrl}/api/sell-platform/initiate-sell`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        investorId: currentInvestor.id,
        investorName: currentInvestor.name,
        investorEmail: currentInvestor.email,
        investorPhone: (currentInvestor as any).phone || '',
        unitId: selectedUnit.id,
        unitName: selectedUnit.name,
        project: selectedUnit.project,
        unitNumber: selectedUnit.unitNumber,
        propertyType: selectedUnit.type || 'Apartment',
        location: selectedUnit.location || selectedUnit.project,
        area: selectedUnit.area,
        bedrooms: selectedUnit.bedrooms,
        floor: selectedUnit.floor,
        purchaseDate: selectedUnit.purchaseDate,
        currentValue: askingPriceNum,
        isDistress: sellIsDistress,
        originalPrice: sellIsDistress ? parseFloat(sellOriginalPrice.replace(/,/g, '')) : null,
        marketValue: dldUnitData?.dldMarketValue || null,
        projectPhotoUrl,
        documents: unitDocs
      })
    });

    const result = await response.json();
    setSellResult(result);

    if (result.success) {
      setSellStatus(prev => ({ ...prev, [selectedUnit.id]: { listed: true, status: 'listed', createdAt: new Date().toISOString() } }));
    }
  } catch (err) {
    console.error('Error initiating sell:', err);
    setSellResult({ success: false });
  } finally {
    setSellLoading(false);
  }
};

const updatePaymentStatus = async (
  documentId: string, 
  installmentNumber: number, 
  newStatus: 'paid' | 'pending' | 'overdue'
) => {
  const statusKey = `${documentId}-${installmentNumber}`;
  setSavingStatus(statusKey);
  
  try {
    setPaymentStatuses(prev => ({
      ...prev,
      [statusKey]: newStatus
    }));
    
    const response = await fetch(`${backendUrl}/api/updatePaymentStatus`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        documentId,
        installmentNumber,
        status: newStatus
      })
    });
    
    if (!response.ok) {
      console.error('Failed to save payment status');
    } else {
    }
    
  } catch (error) {
    console.error('Error updating payment status:', error);
  } finally {
    setSavingStatus(null);
  }
};

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [unitError, setUnitError] = useState('');
  const [unitSuccess, setUnitSuccess] = useState(false);

  const [showRequestDocumentsModal, setShowRequestDocumentsModal] = useState(false);
const [requestUnitId, setRequestUnitId] = useState('');
const [requestedDocTypes, setRequestedDocTypes] = useState<string[]>([]);

  const propertyFileInputRef = React.useRef<HTMLInputElement | null>(null);
  const documentFileInputRef = React.useRef<HTMLInputElement | null>(null);

  const selectedUnit = units.find(u => u.id === selectedProperty);

  // Check sell status for all units on mount (for card badges)
  useEffect(() => {
    units.forEach(u => checkSellStatus(u.id));
  }, [units.length]);

  // Also check when a unit is selected
  useEffect(() => {
    if (selectedProperty && selectedUnit) {
      checkSellStatus(selectedUnit.id);
    }
  }, [selectedProperty, selectedUnit?.id]);

  const propertyDocuments = documents.filter(d => d.unitId === selectedProperty);

  // The three documents the unit's numbers are built from. Types arrive in
  // several spellings ('OTP Document', 'SOA Document', 'Oqood'), so match on
  // the type fields and the file name; a rejected upload does not count.
  const missingKeyDocs = (() => {
    const labels = propertyDocuments
      .filter(d => d.verificationStatus !== 'rejected')
      .map(d => [d.type, d.documentType, d.extractedData?.type, d.name].filter(Boolean).join(' ').toLowerCase());
    const has = (re: RegExp) => labels.some(l => re.test(l));
    const missing: string[] = [];
    if (!has(/oqood|title[\s_-]*deed/)) missing.push('pvMissingDocOqood');
    // [^a-z] rather than \b so 'SPA_signed.pdf' counts but 'spacious' does not
    if (!has(/(^|[^a-z])(spa|otp)([^a-z]|$)|sale[\s_-]*(and|&)?[\s_-]*purchase/)) missing.push('pvMissingDocSpa');
    if (!has(/(^|[^a-z])soa([^a-z]|$)|statement[\s_-]*of[\s_-]*account/)) missing.push('pvMissingDocSoa');
    return missing;
  })();


  const filteredUnits = units.filter(unit => 
    unit.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    unit.project.toLowerCase().includes(searchQuery.toLowerCase()) ||
    unit.unitNumber.toLowerCase().includes(searchQuery.toLowerCase())
  );


  const fetchDocumentBlob = async (doc: any): Promise<{ blob: Blob; fileName: string; contentType: string }> => {
    const authToken = localStorage.getItem('authToken');
    if (!authToken) throw new Error(t('pvPleaseLogIn'));
    const docId = doc.id || doc.documentId;
    if (!docId) throw new Error('Missing document id.');
    const res = await fetch(`${backendUrl}/api/document/${encodeURIComponent(docId)}/view`, {
      headers: { 'Authorization': `Bearer ${authToken}` },
    });
    if (!res.ok) throw new Error('HTTP ' + res.status);
    const blob = await res.blob();
    const contentType = res.headers.get('content-type') || blob.type || 'application/octet-stream';
    const fileName = doc.fileName || doc.name || 'document';
    return { blob, fileName, contentType };
  };

  const blobToBase64 = (blob: Blob): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result as string;
        const comma = result.indexOf(',');
        resolve(comma >= 0 ? result.slice(comma + 1) : result);
      };
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(blob);
    });

  const saveBlobToDevice = async (blob: Blob, fileName: string): Promise<string> => {
    if (Capacitor.isNativePlatform()) {
      const base64 = await blobToBase64(blob);
      const safeName = fileName.replace(/[^a-zA-Z0-9._-]/g, '_') || 'document';
      const result = await Filesystem.writeFile({
        path: safeName,
        data: base64,
        directory: Directory.Documents,
        recursive: true,
      });
      return result.uri || safeName;
    }
    const blobUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(blobUrl);
    return fileName;
  };

  const handleViewFile = async (doc: any) => {
    try {
      const { blob, fileName, contentType } = await fetchDocumentBlob(doc);
      setViewerBlob(blob);
      setViewerFileName(fileName);
      setViewerContentType(contentType);
    } catch (err: any) {
      alert(t('pvCouldNotOpenDoc') + ': ' + (err?.message || err));
    }
  };

  const handleDownloadFile = async (doc: any) => {
    try {
      const { blob, fileName } = await fetchDocumentBlob(doc);
      const savedPath = await saveBlobToDevice(blob, fileName);
      if (Capacitor.isNativePlatform()) {
        alert(`${t('pvSavedToDevice')}\n${savedPath}`);
      }
    } catch (err: any) {
      alert(t('pvCouldNotDownloadDoc') + ': ' + (err?.message || err));
    }
  };

  const handleDownloadFromViewer = async () => {
    if (!viewerBlob) return;
    try {
      const savedPath = await saveBlobToDevice(viewerBlob, viewerFileName || 'document');
      if (Capacitor.isNativePlatform()) {
        alert(`${t('pvSavedToDevice')}\n${savedPath}`);
      }
    } catch (err: any) {
      alert(t('pvCouldNotSaveDoc') + ': ' + (err?.message || err));
    }
  };

const createNewUnit = async () => {
  setIsSubmitting(true);
  setUnitError('');
  setUnitSuccess(false);

  try {
    // Combine type with bedroom count (e.g., "2 BR Apartment")
    const typeWithBedrooms = newUnitForm.type === 'Studio' 
      ? 'Studio' 
      : `${newUnitForm.bedrooms} BR ${newUnitForm.type}`;
    
    const unitData = {
      id: Date.now().toString(),
      investorId: currentInvestor.id,
      name: newUnitForm.name,
      unitNumber: newUnitForm.unitNumber,
      project: newUnitForm.project,
      type: typeWithBedrooms,
      bedrooms: newUnitForm.type === 'Studio' ? 0 : parseInt(newUnitForm.bedrooms),
      area: newUnitForm.area,
      location: 'Dubai, UAE',
      purchaseValue: 0,
      currentValue: 0,
      monthlyRental: 0,
      occupancyStatus: newUnitForm.occupancyStatus || 'Vacant',
      appreciationAmount: 0,
      appreciationPercentage: 0,
      purchaseDate: newUnitForm.purchaseDate || new Date().toISOString(),
      estimatedCompletionDate: newUnitForm.completionDate || null 
    };


    const response = await fetch(
      `${backendUrl}/api/createUnitWithForceFolder`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ data: unitData }),
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `Server error: ${response.status}`);
    }

    const result = await response.json();
    
    if (!result.success) {
      throw new Error(result.error || t('pvFailedToCreateUnit'));
    }

    const newUnit = result.data;

    onUnitCreated(newUnit);

    setUnitSuccess(true);
    setUnitError('');


    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      if (typeof reloadDataFromFirebase === 'function') {
        await reloadDataFromFirebase();
      } else {
        console.warn('⚠️ reloadDataFromFirebase not available');
      }
      
      setTimeout(() => {
        setShowAddPropertyModal(false);
        setNewUnitForm({ 
          name: '', 
          unitNumber: '', 
          project: '', 
          type: 'Apartment', 
          area: '',
          bedrooms: '1',
          occupancyStatus: 'Vacant',
          purchaseDate: new Date().toISOString().split('T')[0],
          completionDate: ''
        });
        setSelectedUnitForDoc(newUnit.id);
        setUnitSuccess(false);
      }, 500);
      
    } catch (reloadError) {
      console.error('⚠️ Failed to reload data:', reloadError);
      alert(t('pvUnitCreatedRefresh'));
      
      setTimeout(() => {
        setShowAddPropertyModal(false);
        setNewUnitForm({ 
          name: '', 
          unitNumber: '', 
          project: '', 
          type: 'Apartment', 
          area: '',
          bedrooms: '1',
          occupancyStatus: 'Vacant',
          purchaseDate: new Date().toISOString().split('T')[0],
          completionDate: ''
        });
        setUnitSuccess(false);
      }, 1500);
    }

  } catch (error: any) {
    console.error('❌ Error creating unit:', error);
    
    let displayError = error.message || 'Failed to create unit';
    
    if (displayError.includes('404')) {
      displayError += ' (Investor not found)';
    } else if (displayError.includes('400')) {
      displayError += ' (Invalid data)';
    } else if (displayError.includes('500')) {
      displayError += ' (Server error)';
    } else if (displayError.includes('503')) {
      displayError += ' (Database unavailable)';
    }
    
    setUnitError(displayError);
  } finally {
    setIsSubmitting(false);
  }
};

  const handleOpenAddPropertyModal = () => {
    setDocumentCategory('Unit Documents');
    setSelectedUnitForDoc('');
    setShowAddPropertyModal(true);
  };

  const handlePropertyFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    setDocumentCategory('Unit Documents');
    handleFileUpload(event);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Properties List View */}
      {!selectedProperty ? (
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
            <div className="px-4 py-4">
              {!hideMainTabs && (
              <div className="flex items-center justify-between mb-4">
                <h1 className="text-2xl font-bold text-gray-900">{t('properties')}</h1>
                {/* Search icon removed */}
              </div>
              )}

              {headerActions}

              {/* Main Tabs - Properties / Cashflow */}
             {!hideMainTabs && (
             <div className="flex bg-gray-100 rounded-xl p-1 mb-4 overflow-x-auto no-scrollbar property-main-tabs" style={{ gap: '4px' }}>
                <button
                  onClick={() => setMainTab('properties')}
                  className={`flex-1 min-w-0 px-3 py-2.5 text-xs font-semibold rounded-lg transition-all flex items-center justify-center gap-1.5 whitespace-nowrap ${
                    mainTab === 'properties' 
                      ? 'bg-white shadow-sm text-gray-900' 
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <Home className="h-3.5 w-3.5 flex-shrink-0" />
                  {t('properties')}
                </button>
                <button
                  onClick={() => setMainTab('cashflow')}
                  className={`flex-1 min-w-0 px-3 py-2.5 text-xs font-semibold rounded-lg transition-all flex items-center justify-center gap-1.5 whitespace-nowrap ${
                    mainTab === 'cashflow' 
                      ? 'bg-white shadow-sm text-gray-900' 
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <TrendingUp className="h-3.5 w-3.5 flex-shrink-0" />
                  {t('cashflow')}
                </button>
                <button
                  onClick={() => setMainTab('personal')}
                  className={`flex-1 min-w-0 px-3 py-2.5 text-xs font-semibold rounded-lg transition-all flex items-center justify-center gap-1.5 whitespace-nowrap ${
                    mainTab === 'personal' 
                      ? 'bg-white shadow-sm text-gray-900' 
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <FileText className="h-3.5 w-3.5 flex-shrink-0" />
                  {t('personalDocs') || 'Personal'}
                </button>
                <button
                  onClick={() => setMainTab('company')}
                  className={`flex-1 min-w-0 px-3 py-2.5 text-xs font-semibold rounded-lg transition-all flex items-center justify-center gap-1.5 whitespace-nowrap ${
                    mainTab === 'company' 
                      ? 'bg-white shadow-sm text-gray-900' 
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <Building className="h-3.5 w-3.5 flex-shrink-0" />
                  {t('companyDocs') || 'Company'}
                </button>
              </div>
             )}
            </div>
          </div>

          {/* PROPERTIES TAB CONTENT */}
          {mainTab === 'properties' && (
            <>
              {/* Action Buttons */}
              <div className="bg-white border-b border-gray-200">
                <div className="px-4 py-4">
                  {/* Search Bar */}
                  {units.length > 3 && (
                    <div className="relative mb-3">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <input
                        type="text"
                        placeholder={t('searchProperties') || 'Search properties...'}
                        value={propertySearchTerm}
                        onChange={(e) => setPropertySearchTerm(e.target.value)}
                        className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-[#D6B160] focus:border-transparent"
                      />
                    </div>
                  )}
                  {/* Action Buttons — hidden in Properties, kept for other features */}
                  <div className="flex gap-3" style={{ display: 'none' }}>
                    <button
                      onClick={() => setShowAddPropertyModal(true)}
                      className="flex-1 py-3 bg-[#D6B160] text-white rounded-xl font-semibold flex items-center justify-center gap-2 hover:bg-[#c5a050] transition-colors"
                    >
                      <Plus className="h-5 w-5" />
                      {t('addProperty')}
                    </button>
                    <button
                      onClick={() => setShowAddDocumentsModal(true)}
                      style={{ border: '2px solid #D6B160', backgroundColor: 'white' }}
                      className="flex-1 py-3 rounded-xl font-semibold flex items-center justify-center gap-2 transition-all hover:bg-[#FFF8E7]"
                    >
                      <FileText className="h-5 w-5" style={{ color: '#D6B160' }} />
                      <span style={{ color: '#000000' }}>{t('addDocuments')}</span>
                    </button>
                  </div>

                  {/* ONE way in, stated once. The AI upload is the button; email
                      is the fallback, written out as the address itself so
                      nobody has to guess which of two cards to tap. The link
                      keeps the [ID:...] subject because the team matches an
                      inbound mail to an account by hand. */}
                  {handleSmartUpload && (
                    <>
                      <label
                        htmlFor="smart-upload-main"
                        style={{
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          gap: '10px', width: '100%',
                          padding: '13px 16px', borderRadius: '12px',
                          background: smartUploading ? 'rgba(214,177,96,0.22)' : 'rgba(214,177,96,0.13)',
                          color: '#8A6C22', fontSize: '13.5px', fontWeight: '800', whiteSpace: 'nowrap',
                          cursor: smartUploading ? 'not-allowed' : 'pointer',
                          border: '1px solid rgba(214,177,96,0.30)', transition: 'all 0.2s',
                        }}
                      >
                        {smartUploading ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2" style={{ borderColor: '#8A6C22' }} />
                            <span>{smartUploadProgress?.fileName || (appLanguage() === 'de' ? 'Wird verarbeitet…' : 'Processing...')}</span>
                          </>
                        ) : (
                          <>
                            <Upload size={16} />
                            <span>{language === 'de' ? 'KI Upload' : 'AI Smart Upload'}</span>
                          </>
                        )}
                      </label>
                      <input
                        id="smart-upload-main"
                        type="file"
                        onChange={handleSmartUpload}
                        accept=".pdf,.jpg,.jpeg,.png"
                        multiple
                        className="hidden"
                        disabled={smartUploading}
                      />
                    </>
                  )}
                  <p style={{ color: 'rgba(0,0,0,0.54)', fontSize: '12px', margin: handleSmartUpload ? '8px 0 0' : 0, textAlign: 'center' }}>
                    {handleSmartUpload
                      ? (language === 'de' ? 'oder senden Sie Ihre Dokumente an ' : 'or send your documents to ')
                      : (language === 'de' ? 'Senden Sie Ihre Dokumente an ' : 'Send your documents to ')}
                    <a
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        const email = 'documents@assetdesk.ae';
                        const investorId = currentInvestor?.id || '';
                        const investorEmail = currentInvestor?.email || '';
                        const subject = encodeURIComponent(`Documents from ${currentInvestor?.name || 'Investor'} [ID:${investorId}]`);
                        const body = encodeURIComponent(`Dear Asset Desk Team,\n\nPlease find my documents attached.\n\n---\nInvestor: ${currentInvestor?.name || ''}\nID: ${investorId}\nEmail: ${investorEmail}\n(Please do not remove this information)`);
                        const cc = investorEmail ? `&cc=${encodeURIComponent(investorEmail)}` : '';
                        window.location.href = `mailto:${email}?subject=${subject}&body=${body}${cc}`;
                      }}
                      style={{ color: '#8A6C22', fontWeight: 700, textDecoration: 'underline' }}
                    >
                      documents@assetdesk.ae
                    </a>
                  </p>
                </div>
              </div>

              {/* WHO IS BEHIND THIS ACCOUNT. Above the status tabs because it
                  covers every unit in the account, sold or not. Accounts with a
                  single owner see one muted line and nothing else. */}
              {(() => {
                const pct = (n: number) => `${Number.isInteger(n) ? n : n.toFixed(2)}%`;
                const off = shareholders.length > 0 && Math.abs(shTotal - 100) > 0.01;
                if (!shareholders.length) {
                  return (
                    <div style={{ padding: '10px 16px 0' }}>
                      <span
                        onClick={openShModal}
                        style={{ fontSize: '11.5px', fontWeight: 600, color: '#9ca3af', cursor: 'pointer' }}
                      >
                        + {language === 'de' ? 'Anteilseigner dieses Kontos' : 'Shareholders in this account'}
                      </span>
                    </div>
                  );
                }
                return (
                  <div style={{ padding: '12px 16px 0' }}>
                    <div
                      onClick={openShModal}
                      style={{
                        background: '#fff', border: '1px solid #e5e7eb', borderRadius: '14px',
                        padding: '13px 14px', cursor: 'pointer'
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '2px' }}>
                        <span style={{ fontSize: '10.5px', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                          {language === 'de' ? 'Anteilseigner dieses Kontos' : 'Shareholders in this account'}
                        </span>
                        <span style={{ fontSize: '11px', fontWeight: 700, color: off ? '#b45309' : '#9ca3af' }}>
                          {pct(shTotal)}
                        </span>
                      </div>
                      <p style={{ margin: '0 0 9px', fontSize: '10.5px', color: '#9ca3af' }}>
                        {language === 'de'
                          ? `Gilt für alle ${units.length} Einheiten in diesem Konto`
                          : `Applies to all ${units.length} units in this account`}
                        {accountRealisedProfit !== 0 && (language === 'de'
                          ? ` · realisierter Gewinn AED ${Math.abs(Math.round(accountRealisedProfit)).toLocaleString('en-AE')}`
                          : ` · realised profit AED ${Math.abs(Math.round(accountRealisedProfit)).toLocaleString('en-AE')}`)}
                      </p>
                      {shareholders.map((h, i) => (
                        <div key={i} style={{
                          display: 'flex', justifyContent: 'space-between', gap: '10px',
                          fontSize: '12px', color: '#374151', padding: '3px 0'
                        }}>
                          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {h.name || '—'}
                          </span>
                          <span style={{ whiteSpace: 'nowrap', fontWeight: 600 }}>
                            {pct(h.sharePercent)}
                            {/* Their slice of what the account has actually realised
                                — the number a distribution is made against. */}
                            {accountRealisedProfit !== 0 && h.sharePercent > 0 && (
                              <span style={{ color: accountRealisedProfit >= 0 ? '#047857' : '#b91c1c', fontWeight: 700 }}>
                                {' · '}{accountRealisedProfit >= 0 ? '+' : '−'}AED {Math.abs(Math.round(accountRealisedProfit * h.sharePercent / 100)).toLocaleString('en-AE')}
                              </span>
                            )}
                          </span>
                        </div>
                      ))}
                      {off && (
                        <div style={{ fontSize: '10.5px', color: '#b45309', marginTop: '5px' }}>
                          {language === 'de'
                            ? `Anteile ergeben ${pct(shTotal)} — erwartet werden 100%`
                            : `Shares add up to ${pct(shTotal)} — 100% expected`}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}

              {/* Apartments / Offices folders — the status tabs open inside one */}
              {showFolderPicker && (
                <div style={{ padding: '16px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  {(['residential', 'commercial'] as UnitCategory[]).map(cat => {
                    const inCat = realUnits.filter(u => unitCategory(u) === cat);
                    const live = inCat.filter(u => u.status !== 'sold').length;
                    const rented = inCat.filter(u => (u.status || '').toLowerCase() === 'rented').length;
                    const Icon = cat === 'residential' ? Home : Briefcase;
                    return (
                      <button
                        key={cat}
                        onClick={() => openPropertyFolder(cat)}
                        style={{
                          background: '#fff', border: '1px solid #D6B160', borderRadius: '16px',
                          padding: '20px 12px', cursor: 'pointer', textAlign: 'center',
                          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px',
                        }}
                      >
                        <Icon size={30} color="#D6B160" />
                        <span style={{ fontSize: '15px', fontWeight: 700, color: '#000', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                          {cat === 'residential'
                            ? (language === 'de' ? 'Wohnungen' : 'Apartments')
                            : (language === 'de' ? 'Büros' : 'Offices')}
                        </span>
                        <span style={{ fontSize: '13px', color: '#000' }}>
                          {language === 'de' ? `${live} Einheiten` : `${live} unit${live === 1 ? '' : 's'}`}
                        </span>
                        {rented > 0 && (
                          <span style={{ fontSize: '12px', color: '#6b7280' }}>
                            {language === 'de' ? `${rented} vermietet` : `${rented} rented`}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
              {inFolder && (
                <div style={{ padding: '12px 16px 0', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <button
                    onClick={() => openPropertyFolder(null)}
                    style={{ display: 'flex', alignItems: 'center', gap: '2px', background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: '13px', fontWeight: 600, color: '#6b7280' }}
                  >
                    <ChevronLeft size={16} />
                    {language === 'de' ? 'Immobilien' : 'Properties'}
                  </button>
                  <span style={{ color: '#d1d5db' }}>/</span>
                  {propertyFolder === 'residential' ? <Home size={15} color="#D6B160" /> : <Briefcase size={15} color="#D6B160" />}
                  <span style={{ fontSize: '13px', fontWeight: 700, color: '#000' }}>
                    {propertyFolder === 'residential'
                      ? (language === 'de' ? 'Wohnungen' : 'Apartments')
                      : (language === 'de' ? 'Büros' : 'Offices')}
                  </span>
                </div>
              )}

              {/* Property Status Sub-Tabs */}
              {(() => {
                const sellingCount = tabUnits.filter(u => u.status !== 'sold' && (u.status === 'selling' || sellStatus[u.id]?.listed)).length;
                const soldCount = tabUnits.filter(u => u.status === 'sold').length;
                const rentedCount = tabUnits.filter(u => (u.status || '').toLowerCase() === 'rented').length;
                return !showFolderPicker && (sellingCount > 0 || soldCount > 0 || rentedCount > 0) ? (
                  <div style={{ padding: '0 16px', paddingTop: '12px' }}>
                    <div style={{
                      display: 'flex',
                      gap: '0',
                      backgroundColor: '#f3f4f6',
                      borderRadius: '12px',
                      padding: '3px',
                    }}>
                      <button
                        onClick={() => setPropertyStatusTab('active')}
                        style={{
                          flex: 1,
                          padding: '10px 8px',
                          borderRadius: '10px',
                          border: 'none',
                          fontSize: '13px',
                          fontWeight: 700,
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          background: propertyStatusTab === 'active' ? '#fff' : 'transparent',
                          color: propertyStatusTab === 'active' ? '#111827' : '#6b7280',
                          boxShadow: propertyStatusTab === 'active' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                        }}
                      >
                        {language === 'de' ? 'Aktiv' : 'Active'} ({tabUnits.filter(u => !isOtherDocsUnit(u) && (!u.status || u.status === 'active' || u.status === 'selling' || (u.status || '').toLowerCase() === 'rented')).length})
                      </button>
                      <button
                        onClick={() => setPropertyStatusTab('rented')}
                        style={{
                          flex: 1,
                          padding: '10px 8px',
                          borderRadius: '10px',
                          border: 'none',
                          fontSize: '13px',
                          fontWeight: 700,
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          background: propertyStatusTab === 'rented' ? '#fff' : 'transparent',
                          color: propertyStatusTab === 'rented' ? '#8b5cf6' : '#6b7280',
                          boxShadow: propertyStatusTab === 'rented' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                        }}
                      >
                        {language === 'de' ? 'Vermietet' : 'Rented'} ({rentedCount})
                      </button>
                      <button
                        onClick={() => setPropertyStatusTab('selling')}
                        style={{
                          flex: 1,
                          padding: '10px 8px',
                          borderRadius: '10px',
                          border: 'none',
                          fontSize: '13px',
                          fontWeight: 700,
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          background: propertyStatusTab === 'selling' ? '#fff' : 'transparent',
                          color: propertyStatusTab === 'selling' ? '#D6B160' : '#6b7280',
                          boxShadow: propertyStatusTab === 'selling' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                        }}
                      >
                        {language === 'de' ? 'Verkauf' : 'Selling'} ({sellingCount})
                      </button>
                      <button
                        onClick={() => setPropertyStatusTab('sold')}
                        style={{
                          flex: 1,
                          padding: '10px 8px',
                          borderRadius: '10px',
                          border: 'none',
                          fontSize: '13px',
                          fontWeight: 700,
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          background: propertyStatusTab === 'sold' ? '#fff' : 'transparent',
                          color: propertyStatusTab === 'sold' ? '#10b981' : '#6b7280',
                          boxShadow: propertyStatusTab === 'sold' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                        }}
                      >
                        {language === 'de' ? 'Verkauft' : 'Sold'} ({soldCount})
                      </button>
                    </div>
                  </div>
                ) : null;
              })()}

              {/* SELLING TAB — Units listed on DubaiSell */}
              {propertyStatusTab === 'selling' && !showFolderPicker && (
                <div className="p-4">
                  <div className="property-grid">
                    {tabUnits
                      .filter(u => u.status !== 'sold' && (u.status === 'selling' || sellStatus[u.id]?.listed))
                      .map(unit => {
                        const daysOnMarket = unit.listedAt
                          ? Math.floor((Date.now() - new Date(unit.listedAt).getTime()) / (1000 * 60 * 60 * 24))
                          : sellStatus[unit.id]?.createdAt
                            ? Math.floor((Date.now() - new Date(sellStatus[unit.id].createdAt!).getTime()) / (1000 * 60 * 60 * 24))
                            : 0;

                        return (
                          <div
                            key={unit.id}
                            style={{
                              background: '#fff',
                              borderRadius: '16px',
                              border: '2px solid #D6B160',
                              padding: '16px',
                              boxShadow: '0 2px 8px rgba(214, 177, 96, 0.15)',
                            }}
                          >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                              <div>
                                <div style={{ fontSize: '15px', fontWeight: 700, color: '#111827' }}>
                                  {unit.unitNumber || unit.name}
                                </div>
                                <div style={{ fontSize: '13px', color: '#6b7280', marginTop: '2px' }}>
                                  {unit.project}
                                </div>
                              </div>
                              <div style={{
                                padding: '4px 10px',
                                background: '#FFF8E7',
                                borderRadius: '8px',
                                border: '1px solid #D6B160',
                              }}>
                                <span style={{ fontSize: '11px', fontWeight: 700, color: '#92400e' }}>
                                  {language === 'de' ? 'ZUM VERKAUF' : 'FOR SALE'}
                                </span>
                              </div>
                            </div>

                            {/* Listing Stats */}
                            <div style={{
                              display: 'grid',
                              gridTemplateColumns: '1fr 1fr 1fr',
                              gap: '12px',
                              marginTop: '16px',
                              padding: '12px',
                              background: '#f9fafb',
                              borderRadius: '12px',
                            }}>
                              <div style={{ textAlign: 'center' }}>
                                <div style={{ fontSize: '18px', fontWeight: 700, color: '#D6B160' }}>
                                  {daysOnMarket}
                                </div>
                                <div style={{ fontSize: '10px', color: '#6b7280', textTransform: 'uppercase', marginTop: '2px' }}>
                                  {language === 'de' ? 'Tage' : 'Days'}
                                </div>
                              </div>
                              <div style={{ textAlign: 'center' }}>
                                <div style={{ fontSize: '18px', fontWeight: 700, color: '#374151' }}>
                                  {unit.views || 0}
                                </div>
                                <div style={{ fontSize: '10px', color: '#6b7280', textTransform: 'uppercase', marginTop: '2px' }}>
                                  {language === 'de' ? 'Aufrufe' : 'Views'}
                                </div>
                              </div>
                              <div style={{ textAlign: 'center' }}>
                                <div style={{ fontSize: '18px', fontWeight: 700, color: '#374151' }}>
                                  {unit.inquiries || 0}
                                </div>
                                <div style={{ fontSize: '10px', color: '#6b7280', textTransform: 'uppercase', marginTop: '2px' }}>
                                  {language === 'de' ? 'Anfragen' : 'Inquiries'}
                                </div>
                              </div>
                            </div>

                            {/* Pricing */}
                            <div style={{ marginTop: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <div>
                                <div style={{ fontSize: '11px', color: '#9ca3af', textTransform: 'uppercase' }}>
                                  {language === 'de' ? 'KAUFPREIS' : 'PURCHASE PRICE'}
                                </div>
                                <div style={{ fontSize: '14px', fontWeight: 600, color: '#374151' }}>
                                  AED {(unit.purchaseValue || 0).toLocaleString('en-AE')}
                                </div>
                              </div>
                              <div style={{ textAlign: 'right' }}>
                                <div style={{ fontSize: '11px', color: '#9ca3af', textTransform: 'uppercase' }}>
                                  {language === 'de' ? 'ANGEBOTSPR.' : 'ASKING PRICE'}
                                </div>
                                <div style={{ fontSize: '14px', fontWeight: 700, color: '#D6B160' }}>
                                  AED {(unit.askingPrice || unit.currentValue || 0).toLocaleString('en-AE')}
                                </div>
                              </div>
                            </div>

                            {/* Did it sell? Record the closed deal and price the return. */}
                            <button
                              onClick={() => openSoldModal(unit)}
                              style={{
                                marginTop: '14px', width: '100%', padding: '12px',
                                borderRadius: '12px', border: 'none', cursor: 'pointer',
                                background: '#1a1a2e', color: '#fff',
                                fontSize: '13px', fontWeight: 700, fontFamily: 'inherit',
                                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px'
                              }}
                            >
                              <Receipt size={15} />
                              {language === 'de' ? 'Verkauf abgeschlossen — ROI erfassen' : 'Sale completed — record the ROI'}
                            </button>
                          </div>
                        );
                      })}
                    {units.filter(u => u.status !== 'sold' && (u.status === 'selling' || sellStatus[u.id]?.listed)).length === 0 && (
                      <div style={{
                        gridColumn: '1 / -1',
                        textAlign: 'center',
                        padding: '40px 20px',
                        color: '#9ca3af',
                      }}>
                        <Tag size={32} style={{ margin: '0 auto 12px', color: '#d1d5db' }} />
                        <div style={{ fontSize: '14px', fontWeight: 600 }}>
                          {language === 'de' ? 'Keine Einheiten zum Verkauf gelistet' : 'No units listed for sale'}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* SOLD TAB — Completed sales with ROI report */}
              {propertyStatusTab === 'sold' && !showFolderPicker && (
                <div className="p-4">
                  <div className="property-grid">
                    {tabUnits
                      .filter(u => u.status === 'sold')
                      .map(unit => {
                        // Recomputed from what was stored, with the same helper the
                        // form preview and the portfolio report use — so the three
                        // can never print different returns for one sale.
                        const sale = summariseSale(unit as any)!;
                        const isProfitable = sale.netProfit >= 0;
                        const tone = isProfitable ? '#10b981' : '#ef4444';
                        const costLine = (label: string, amount: number) => (
                          <div key={label} style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', fontSize: '12px', color: '#4b5563', padding: '3px 0' }}>
                            <span>{label}</span>
                            <span style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>− AED {Math.round(amount).toLocaleString('en-AE')}</span>
                          </div>
                        );

                        /**
                         * One line of the profit derivation. `sign` is what the figure
                         * does to the running total, so the block reads as an arithmetic
                         * the owner can follow down the page with their bank statements —
                         * a six-figure result nobody can decompose is a number they have
                         * to take on trust.
                         */
                        const mathRow = (label: string, amount: number, sign: string, strong = false) => (
                          <div key={label} style={{
                            display: 'flex', justifyContent: 'space-between', gap: '12px',
                            fontSize: '11.5px', padding: '3px 0',
                            color: strong ? '#111827' : '#4b5563',
                            fontWeight: strong ? 700 : 400,
                            ...(sign.startsWith('=')
                              ? { borderTop: '1px solid #e5e7eb', marginTop: '4px', paddingTop: '6px' }
                              : {})
                          }}>
                            <span>{label}</span>
                            <span style={{ fontWeight: strong ? 700 : 600, whiteSpace: 'nowrap' }}>
                              {sign ? sign + ' ' : ''}AED {Math.abs(Math.round(amount)).toLocaleString('en-AE')}
                            </span>
                          </div>
                        );
                        /** '=' for a positive subtotal, '= −' for one that went the other way. */
                        const eq = (n: number) => (n < 0 ? '= −' : '=');

                        /**
                         * What the unit cost to get into: the price paid plus the fees
                         * paid on the way in.
                         *
                         * Derived as capital + the balance the buyer took over, not as
                         * price + fees directly, so the profit below always reconciles
                         * even when the owner has typed an outstanding balance of their
                         * own instead of the derived one. The two agree whenever that
                         * balance is the one the payment plan implies, which is the
                         * normal case — and only then is it shown broken down.
                         */
                        const totalCostToBuy = sale.capitalBase + sale.outstandingBalance;

                        /** An indented component of the line above it. */
                        const subRow = (label: string, amount: number) => (
                          <div key={label} style={{
                            display: 'flex', justifyContent: 'space-between', gap: '12px',
                            fontSize: '10.5px', color: '#9ca3af', padding: '1px 0 1px 10px'
                          }}>
                            <span>{label}</span>
                            <span style={{ whiteSpace: 'nowrap' }}>{Math.abs(Math.round(amount)).toLocaleString('en-AE')}</span>
                          </div>
                        );

                        return (
                          <div
                            key={unit.id}
                            style={{
                              background: '#fff',
                              borderRadius: '16px',
                              border: `2px solid ${tone}`,
                              padding: '16px',
                              boxShadow: `0 2px 8px ${isProfitable ? 'rgba(16, 185, 129, 0.15)' : 'rgba(239, 68, 68, 0.15)'}`,
                            }}
                          >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                              <div>
                                <div style={{ fontSize: '15px', fontWeight: 700, color: '#111827' }}>
                                  {unit.unitNumber || unit.name}
                                </div>
                                <div style={{ fontSize: '13px', color: '#6b7280', marginTop: '2px' }}>
                                  {unit.project}
                                </div>
                              </div>
                              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '5px' }}>
                                <div style={{
                                  padding: '4px 10px',
                                  background: isProfitable ? '#f0fdf4' : '#fef2f2',
                                  borderRadius: '8px',
                                  border: `1px solid ${tone}`,
                                }}>
                                  <span style={{ fontSize: '11px', fontWeight: 700, color: isProfitable ? '#065f46' : '#991b1b' }}>
                                    {t('pvSold')}
                                  </span>
                                </div>
                                {sale.saleType === 'assignment' && (
                                  <div style={{
                                    padding: '3px 9px', background: '#EFF6FF',
                                    borderRadius: '7px', border: '1px solid #93C5FD'
                                  }}>
                                    <span style={{ fontSize: '10px', fontWeight: 700, color: '#1e40af' }}>
                                      {language === 'de' ? 'OFF-PLAN' : 'OFF-PLAN'}
                                    </span>
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Sale Date */}
                            {(unit.saleDate || unit.soldAt) && (
                              <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <Calendar size={12} />
                                {language === 'de' ? 'Verkauft am' : 'Sold on'} {new Date(unit.saleDate || unit.soldAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                                {sale.holdingMonths && (
                                  <span style={{ color: '#9ca3af' }}>
                                    ({language === 'de' ? 'gehalten' : 'held'} {sale.holdingMonths} {language === 'de' ? 'Monate' : 'months'})
                                  </span>
                                )}
                              </div>
                            )}

                            {/* ROI Report Card */}
                            <div style={{
                              marginTop: '16px',
                              padding: '16px',
                              background: '#f9fafb',
                              borderRadius: '12px',
                            }}>
                              {/* Purchase vs Sale Price */}
                              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
                                <div>
                                  <div style={{ fontSize: '10px', color: '#9ca3af', textTransform: 'uppercase' }}>
                                    {sale.saleType === 'assignment'
                                      ? (language === 'de' ? 'EINGEZAHLTES KAPITAL' : 'CAPITAL PAID IN')
                                      : (language === 'de' ? 'KAUFPREIS' : 'PURCHASE PRICE')}
                                  </div>
                                  <div style={{ fontSize: '14px', fontWeight: 600, color: '#374151' }}>
                                    AED {Math.round(sale.capitalBase).toLocaleString('en-AE')}
                                  </div>
                                  {/* Just the capital, in both currencies. The SPA price
                                      and the cheque-by-cheque split used to sit here too,
                                      but the derivation below now carries both — and on a
                                      sold unit the SPA price is the developer's contract
                                      value, neither what was paid for it nor the capital
                                      the return is measured on. */}
                                  <div style={{ fontSize: '11px', color: '#9ca3af' }}>
                                    {formatEUR(sale.capitalBase)}
                                  </div>
                                  {/* What the unit itself was bought for, sitting opposite
                                      the sale price so the two read as a pair. It is not the
                                      capital — fees sit on top of it and the unpaid balance
                                      inside it — and the block below decomposes the cost by
                                      who received the money, so this is the one place the
                                      bare price appears. */}
                                  {sale.entryPrice > 0 && Math.round(sale.entryPrice) !== Math.round(sale.capitalBase) && (
                                    <div style={{ fontSize: '10px', color: '#9ca3af', marginTop: '2px' }}>
                                      {language === 'de' ? 'gekauft für' : 'bought at'} AED {Math.round(sale.entryPrice).toLocaleString('en-AE')}
                                    </div>
                                  )}
                                  {/* How the capital above adds up. The block further down
                                      decomposes the total COST, which includes the balance
                                      that left with the buyer — a different sum, so the
                                      figure in the headline had nothing explaining it. Only
                                      shown when there is more than one part to add. */}
                                  {(() => {
                                    const parts = [
                                      sale.acquisitionCash > 0
                                        ? `${Math.round(sale.acquisitionCash).toLocaleString('en-AE')} ${language === 'de' ? 'an Voreigentümer' : 'to owner'}`
                                        : null,
                                      sale.paidToDate > 0
                                        ? `${Math.round(sale.paidToDate).toLocaleString('en-AE')} ${sale.saleType === 'assignment'
                                            ? (language === 'de' ? 'eingezahlt' : 'paid in')
                                            : (language === 'de' ? 'Kaufpreis' : 'price')}`
                                        : null,
                                      sale.purchaseCostsTotal > 0
                                        ? `${Math.round(sale.purchaseCostsTotal).toLocaleString('en-AE')} ${language === 'de' ? 'Gebühren' : 'fees'}`
                                        : null,
                                    ].filter(Boolean);
                                    return parts.length > 1 ? (
                                      <div style={{ fontSize: '10px', color: '#9ca3af', marginTop: '2px', lineHeight: 1.45 }}>
                                        {parts.join(' + ')}
                                      </div>
                                    ) : null;
                                  })()}
                                </div>
                                <div style={{ textAlign: 'right' }}>
                                  <div style={{ fontSize: '10px', color: '#9ca3af', textTransform: 'uppercase' }}>
                                    {language === 'de' ? 'VERKAUFSPREIS' : 'SALE PRICE'}
                                  </div>
                                  <div style={{ fontSize: '14px', fontWeight: 700, color: tone }}>
                                    AED {sale.salePrice.toLocaleString('en-AE')}
                                  </div>
                                  <div style={{ fontSize: '11px', color: '#9ca3af' }}>
                                    {formatEUR(sale.salePrice)}
                                  </div>
                                </div>
                              </div>

                              {/* Transaction costs — what came off the sale price */}
                              {(sale.totalCosts > 0 || sale.outstandingBalance > 0) && (
                                <div style={{
                                  padding: '10px 12px', background: '#fff', borderRadius: '10px',
                                  border: '1px solid #e5e7eb', marginBottom: '12px'
                                }}>
                                  <div style={{ fontSize: '10px', color: '#9ca3af', textTransform: 'uppercase', fontWeight: 700, marginBottom: '6px' }}>
                                    {language === 'de' ? 'Verkaufskosten' : 'Selling costs'}
                                  </div>
                                  {sale.dldFee > 0 && costLine(language === 'de' ? 'DLD-Übertragungsgebühr' : 'DLD transfer fee', sale.dldFee)}
                                  {sale.commission > 0 && costLine(language === 'de' ? 'Maklerprovision' : 'Agency commission', sale.commission)}
                                  {sale.trusteeFee > 0 && costLine(language === 'de' ? 'Trustee-Gebühr' : 'Trustee fee', sale.trusteeFee)}
                                  {sale.nocFee > 0 && costLine(language === 'de' ? 'NOC-Gebühr (Entwickler)' : 'Developer NOC fee', sale.nocFee)}
                                  {sale.developerAdminFee > 0 && costLine(language === 'de' ? 'Verwaltungsgebühr (Entwickler)' : 'Developer admin fee', sale.developerAdminFee)}
                                  {sale.otherExpenses.map((e, i) => costLine(e.label || (language === 'de' ? `Weitere Kosten ${i + 1}` : `Other expense ${i + 1}`), e.amount))}
                                  <div style={{
                                    display: 'flex', justifyContent: 'space-between', gap: '12px',
                                    marginTop: '6px', paddingTop: '6px', borderTop: '1px solid #e5e7eb',
                                    fontSize: '12px', fontWeight: 700, color: '#111827'
                                  }}>
                                    <span>{language === 'de' ? 'Kosten gesamt' : 'Total costs'}</span>
                                    <span style={{ whiteSpace: 'nowrap' }}>− AED {Math.round(sale.totalCosts).toLocaleString('en-AE')}</span>
                                  </div>
                                  {sale.outstandingBalance > 0 && costLine(
                                    language === 'de' ? 'Restschuld (Käufer übernommen)' : 'Outstanding (buyer assumed)',
                                    sale.outstandingBalance
                                  )}
                                  <div style={{
                                    display: 'flex', justifyContent: 'space-between', gap: '12px',
                                    marginTop: '6px', fontSize: '12px', fontWeight: 700, color: '#111827'
                                  }}>
                                    <span>{sale.saleType === 'assignment'
                                      ? (language === 'de' ? 'Auszahlung an Sie' : 'Cash to you')
                                      : (language === 'de' ? 'Nettoerlös' : 'Net proceeds')}</span>
                                    <span style={{ whiteSpace: 'nowrap' }}>
                                      AED {Math.round(sale.cashToSeller).toLocaleString('en-AE')}
                                      <span style={{ color: '#9ca3af', fontWeight: 500 }}> · {formatEUR(sale.cashToSeller)}</span>
                                    </span>
                                  </div>
                                </div>
                              )}

                              {/* Profit & ROI Row */}
                              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px' }}>
                                <div style={{ textAlign: 'center' }}>
                                  <div style={{ fontSize: '16px', fontWeight: 700, color: tone }}>
                                    {sale.hasBasis
                                      ? `${isProfitable ? '+' : '−'}AED ${Math.abs(Math.round(sale.netProfit)).toLocaleString('en-AE')}`
                                      : '—'}
                                  </div>
                                  <div style={{ fontSize: '10px', color: '#6b7280', textTransform: 'uppercase', marginTop: '2px' }}>
                                    {language === 'de' ? 'Nettogewinn' : 'Net profit'}
                                  </div>
                                </div>
                                <div style={{ textAlign: 'center' }}>
                                  <div style={{ fontSize: '16px', fontWeight: 700, color: tone }}>
                                    {sale.hasBasis ? `${isProfitable ? '+' : ''}${sale.roiPercent.toFixed(1)}%` : '—'}
                                  </div>
                                  <div style={{ fontSize: '10px', color: '#6b7280', textTransform: 'uppercase', marginTop: '2px' }}>
                                    ROI
                                  </div>
                                  {sale.hasBasis && (
                                    <div style={{ fontSize: '9px', color: '#9ca3af', marginTop: '1px' }}>
                                      {sale.saleType === 'assignment'
                                        ? (language === 'de' ? 'auf Kapital' : 'on capital in')
                                        : sale.purchaseCostsTotal > 0
                                          /* the base is purchase price + what buying it cost,
                                             so "on purchase" would understate the denominator */
                                          ? (language === 'de' ? 'auf Gesamtkosten' : 'on total cost to buy')
                                          : (language === 'de' ? 'auf Kaufpreis' : 'on purchase')}
                                    </div>
                                  )}
                                </div>
                                <div style={{ textAlign: 'center' }}>
                                  <div style={{ fontSize: '16px', fontWeight: 700, color: '#374151' }}>
                                    {sale.annualizedRoi != null
                                      ? `${sale.annualizedRoi >= 0 ? '+' : ''}${sale.annualizedRoi.toFixed(1)}%`
                                      : '—'}
                                  </div>
                                  <div style={{ fontSize: '10px', color: '#6b7280', textTransform: 'uppercase', marginTop: '2px' }}>
                                    {language === 'de' ? 'Jährl. ROI' : 'Annual ROI'}
                                  </div>
                                </div>
                              </div>

                              {/* The same figures as above, laid out as the sum that
                                  produced them. Off-plan is where this matters most: the
                                  sale price is the whole value of the unit, but part of it
                                  goes to the developer and part of what you paid to get in
                                  was never yours to begin with. */}
                              {sale.hasBasis && (
                                <div style={{
                                  marginTop: '12px', padding: '11px 12px',
                                  background: '#fff', border: '1px solid #e5e7eb', borderRadius: '10px'
                                }}>
                                  <div style={{ fontSize: '10px', color: '#9ca3af', textTransform: 'uppercase', fontWeight: 700, marginBottom: '6px' }}>
                                    {language === 'de' ? 'So entsteht der Nettogewinn' : 'How the net profit is calculated'}
                                  </div>

                                  {mathRow(language === 'de' ? 'Verkaufspreis' : 'Sale price', sale.salePrice, '')}

                                  {mathRow(
                                    language === 'de' ? 'Gesamtkosten beim Kauf' : 'Total cost to buy',
                                    totalCostToBuy, '−')}
                                  {/* Every part of that total, itemised. These four always
                                      add up to it exactly — the first three ARE the capital
                                      base and the fourth is the balance that left with the
                                      buyer — so the block can never show a sum that does not
                                      reconcile, whatever figures were entered. */}
                                  <div style={{ margin: '1px 0 4px' }}>
                                    {sale.acquisitionCash > 0 && subRow(
                                      language === 'de' ? 'An den Voreigentümer gezahlt' : 'Paid to the previous owner',
                                      sale.acquisitionCash)}
                                    {sale.saleType === 'assignment'
                                      ? (sale.paidToDate > 0 && subRow(
                                          sale.acquisitionRoute === 'assigned-in'
                                            ? (language === 'de' ? 'Seit der Übernahme eingezahlt' : 'Paid in since you took over')
                                            : (language === 'de' ? 'An den Entwickler gezahlt' : 'Paid to the developer'),
                                          sale.paidToDate))
                                      : (sale.purchase > 0 && subRow(
                                          language === 'de' ? 'Kaufpreis' : 'Purchase price',
                                          sale.purchase))}
                                    {sale.purchaseCostsTotal > 0 && subRow(
                                      language === 'de'
                                        ? `Kaufnebenkosten${sale.purchaseDldFee > 0 ? ` (DLD ${Math.round(sale.purchaseDldFee).toLocaleString('en-AE')}${sale.purchaseTrusteeFee > 0 ? ` + Trustee ${Math.round(sale.purchaseTrusteeFee).toLocaleString('en-AE')}` : ''})` : ''}`
                                        : `Buying costs${sale.purchaseDldFee > 0 ? ` (DLD ${Math.round(sale.purchaseDldFee).toLocaleString('en-AE')}${sale.purchaseTrusteeFee > 0 ? ` + trustee ${Math.round(sale.purchaseTrusteeFee).toLocaleString('en-AE')}` : ''})` : ''}`,
                                      sale.purchaseCostsTotal)}
                                    {sale.outstandingBalance > 0 && subRow(
                                      language === 'de' ? 'Restschuld, die der Käufer übernommen hat' : 'Balance the next buyer took over',
                                      sale.outstandingBalance)}
                                  </div>

                                  {sale.totalCosts > 0 && mathRow(
                                    language === 'de' ? 'Verkaufskosten' : 'Selling costs',
                                    sale.totalCosts, '−')}

                                  {mathRow(
                                    language === 'de' ? 'Nettogewinn' : 'Net profit',
                                    sale.netProfit, eq(sale.netProfit), true)}

                                  {/* The total above is what the unit cost, not what it
                                      cost YOU: off-plan, part of that price walks away
                                      with the buyer as unpaid installments. The ROI is
                                      measured on the rest, which is the money that was
                                      actually at risk. */}
                                  {sale.outstandingBalance > 0 && (
                                    <p style={{ margin: '7px 0 0', paddingTop: '6px', borderTop: '1px solid #f3f4f6', fontSize: '10px', color: '#9ca3af', lineHeight: 1.5 }}>
                                      {language === 'de'
                                        ? `Davon waren AED ${Math.round(sale.outstandingBalance).toLocaleString('en-AE')} Restschuld, die der Käufer übernommen hat — Ihr eigenes Kapital war AED ${Math.round(sale.capitalBase).toLocaleString('en-AE')}, und daran wird die Rendite gemessen.`
                                        : `Of that, AED ${Math.round(sale.outstandingBalance).toLocaleString('en-AE')} was the developer balance the buyer took over — your own capital was AED ${Math.round(sale.capitalBase).toLocaleString('en-AE')}, and the ROI is measured on that.`}
                                    </p>
                                  )}
                                </div>
                              )}

                              {!sale.hasBasis && (
                                <p style={{ margin: '10px 0 0', fontSize: '11px', color: '#92400e', background: '#FFF8E1', border: '1px solid #FFE082', borderRadius: '8px', padding: '7px 10px' }}>
                                  {language === 'de'
                                    ? 'Ohne Kaufpreis lässt sich kein ROI berechnen — bitte im Verkaufsformular ergänzen.'
                                    : 'No purchase price on record, so the ROI cannot be calculated — add it in the sale form.'}
                                </p>
                              )}

                              {unit.saleNotes && (
                                <p style={{ margin: '10px 0 0', fontSize: '12px', color: '#4b5563', fontStyle: 'italic' }}>
                                  {unit.saleNotes}
                                </p>
                              )}
                            </div>

                            {/* The owner's own record stays editable — a mistyped fee
                                would otherwise be frozen into the portfolio's realised
                                return for good. Platform-closed deals are read-only. */}
                            {(!unit.saleRecordedBy || unit.saleRecordedBy === 'owner') && (
                              <div style={{ display: 'flex', gap: '10px', marginTop: '14px' }}>
                                <button
                                  onClick={() => openSoldModal(unit)}
                                  style={{
                                    flex: 1, padding: '11px', borderRadius: '10px', cursor: 'pointer',
                                    border: '1.5px solid #1a1a2e', background: '#fff', color: '#1a1a2e',
                                    fontSize: '12px', fontWeight: 700, fontFamily: 'inherit',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px'
                                  }}
                                >
                                  <PenLine size={13} />
                                  {language === 'de' ? 'Verkauf bearbeiten' : 'Edit sale details'}
                                </button>
                                <button
                                  onClick={() => handleRevertSale(unit.id)}
                                  style={{
                                    padding: '11px 14px', borderRadius: '10px', cursor: 'pointer',
                                    border: '1px solid #e5e7eb', background: '#fff', color: '#6b7280',
                                    fontSize: '12px', fontWeight: 600, fontFamily: 'inherit'
                                  }}
                                >
                                  {language === 'de' ? 'Zurücknehmen' : 'Undo'}
                                </button>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    {units.filter(u => u.status === 'sold').length === 0 && (
                      <div style={{
                        gridColumn: '1 / -1',
                        textAlign: 'center',
                        padding: '40px 20px',
                        color: '#9ca3af',
                      }}>
                        <BarChart3 size={32} style={{ margin: '0 auto 12px', color: '#d1d5db' }} />
                        <div style={{ fontSize: '14px', fontWeight: 600 }}>
                          {language === 'de' ? 'Noch keine verkauften Einheiten' : 'No sold units yet'}
                        </div>
                        <div style={{ fontSize: '12px', marginTop: '4px' }}>
                          {language === 'de' ? 'Ihre Verkaufshistorie erscheint hier' : 'Your sales history will appear here'}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Properties Grid - Clean Horizontal Cards (ACTIVE + RENTED tabs) */}
              {(propertyStatusTab === 'active' || propertyStatusTab === 'rented') && !showFolderPicker && (
              <div className="p-4">
                {openGroup && (
                  <button type="button" onClick={() => setOpenBuilding(null)}
                    style={{ all: 'unset', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6, marginBottom: 12, fontSize: 13, fontWeight: 700, color: '#000' }}>
                    <ChevronLeft size={16} color="#D6B160" />
                    <span style={{ color: '#6b7280', fontWeight: 600 }}>{language === 'de' ? 'Alle Einheiten' : 'All units'}</span>
                    <span style={{ color: '#9ca3af' }}>/</span>
                    <Building2 size={15} color="#D6B160" />
                    {openGroup.name} ({openGroup.units.length})
                  </button>
                )}
                <div className="property-grid">
                  {!openGroup && buildingGroups.map((g) => {
                    const value = g.units.reduce((s: number, u: any) => s + (Number(u.currentValue) || Number(u.purchaseValue) || Number(u.purchasePrice) || 0), 0);
                    const rented = g.units.filter((u: any) => (u.status || '').toLowerCase() === 'rented').length;
                    const nums = g.units.map((u: any) => String(u.unitNumber || '').trim()).filter(Boolean).sort();
                    // The building photo, by the same rule as the unit cards
                    // (own image, else the project image map); the first unit
                    // that has one.
                    const merged = { ...FALLBACK_PROJECT_IMAGES, ...dynamicProjectImages } as Record<string, string>;
                    const photo = g.units.map((u: any) => {
                      const own = u.image || u.projectImage || '';
                      if (own) return own;
                      const txt = `${u.project || ''} ${u.name || ''} ${u.unitNumber || ''}`.toLowerCase();
                      const hit = Object.entries(merged).find(([k, url]) => url && txt.includes(k));
                      return hit ? hit[1] : '';
                    }).find(Boolean) || '';
                    return (
                      <button key={g.key} type="button" onClick={() => setOpenBuilding(g.key)}
                        style={{ all: 'unset', cursor: 'pointer', boxSizing: 'border-box', display: 'flex', gap: 14, alignItems: 'center', padding: 16, borderRadius: 12, border: '1px solid #D6B160', background: '#fff', minWidth: 0 }}>
                        <div style={{ width: 72, height: 72, borderRadius: 10, background: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, position: 'relative' }}>
                          <Building2 size={30} color="#D6B160" />
                          {photo && (
                            <img src={photo} alt={g.name}
                              style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', borderRadius: 10 }}
                              onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                          )}
                          <span style={{ position: 'absolute', top: -6, right: -6, minWidth: 22, height: 22, borderRadius: 11, background: '#D6B160', color: '#000', fontSize: 12, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 5px' }}>{g.units.length}</span>
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 15, fontWeight: 800, color: '#000', overflowWrap: 'anywhere' }}>{g.name}</div>
                          <div style={{ fontSize: 12.5, color: '#6b7280', marginTop: 2 }}>
                            {language === 'de' ? `${g.units.length} Einheiten · ${rented} vermietet` : `${g.units.length} units · ${rented} rented`}
                          </div>
                          {nums.length > 0 && <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 2, overflowWrap: 'anywhere' }}>{nums.join(' · ')}</div>}
                          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginTop: 6 }}>
                            <span style={{ fontSize: 11, color: '#9ca3af', textTransform: 'uppercase' }}>{t('currentValueLabel')}</span>
                            <span style={{ fontSize: 15, fontWeight: 700, color: '#D6B160' }}>AED {Math.round(value).toLocaleString('en-AE')}</span>
                          </div>
                        </div>
                        <ChevronRight size={18} color="#D6B160" />
                      </button>
                    );
                  })}
                  {tabUnits
                    .filter(u => propertyStatusTab === 'rented'
                      ? (u.status || '').toLowerCase() === 'rented'
                      : (!u.status || u.status === 'active' || u.status === 'selling' || (u.status || '').toLowerCase() === 'rented'))
                    // A building with 2+ units is behind its group card; an
                    // open group shows only its own units.
                    .filter(u => (openGroup ? buildingKey(u) === openGroup.key : !groupedKeys.has(buildingKey(u))))
                    .filter(u => {
                      if (!propertySearchTerm) return true;
                      const search = propertySearchTerm.toLowerCase();
                      return (
                        (u.name || '').toLowerCase().includes(search) ||
                        (u.unitNumber || '').toLowerCase().includes(search) ||
                        (u.project || '').toLowerCase().includes(search)
                      );
                    })
                    // Documents folder after every property, never between two of them
                    .sort((a, b) => Number(isOtherDocsUnit(a)) - Number(isOtherDocsUnit(b)))
                    .map(unit => {
                    // Use dynamically fetched project images (admin), fall back to hardcoded map
                    const searchText = `${unit.project || ''} ${unit.name || ''} ${unit.unitNumber || ''}`.toLowerCase();
                    let imageUrl = unit.image || unit.projectImage || '';
                    if (!imageUrl) {
                      const merged = { ...FALLBACK_PROJECT_IMAGES, ...dynamicProjectImages };
                      for (const [key, url] of Object.entries(merged)) {
                        if (url && searchText.includes(key)) {
                          imageUrl = url;
                          break;
                        }
                      }
                    }

                    // Get bedroom display
                    const getBedroomDisplay = () => {
                      // Try bedrooms field first
                      const br = String(unit.bedrooms ?? '');
                      const num = br.match(/\d+/);
                      if (num && parseInt(num[0]) <= 20) return `${num[0]} ${appLanguage() === 'de' ? 'Schlafzimmer' : (num[0] === '1' ? 'Bedroom' : 'Bedrooms')}`;

                      // Try type field — match "2 B/R", "2BR", "2 bed", "2 bedroom"
                      const typeStr = String(unit.type || '').toLowerCase();
                      if (typeStr.includes('studio')) return 'Studio';
                      const brMatch = typeStr.match(/(\d+)\s*(?:b\/?r|bed|bedroom)/i);
                      if (brMatch) return `${brMatch[1]} ${appLanguage() === 'de' ? 'Schlafzimmer' : (brMatch[1] === '1' ? 'Bedroom' : 'Bedrooms')}`;
                      const bedMatch2 = typeStr.match(/(?:b\/?r|bed|bedroom)\s*(\d+)/i);
                      if (bedMatch2) return `${bedMatch2[1]} ${appLanguage() === 'de' ? 'Schlafzimmer' : (bedMatch2[1] === '1' ? 'Bedroom' : 'Bedrooms')}`;

                      return '-';
                    };

                    // Get floor number
                    const getFloorDisplay = () => {
                      const matches = unit.unitNumber?.match(/(\d{3,4})(?:\D*)?$/);
                      const unitNum = matches ? matches[1] : '';
                      if (unitNum.length === 3) return `${appLanguage() === 'de' ? 'Etage' : 'Floor'} ${unitNum.charAt(0)}`;
                      if (unitNum.length === 4) return `${appLanguage() === 'de' ? 'Etage' : 'Floor'} ${parseInt(unitNum.substring(0, 2))}`;
                      return appLanguage() === 'de' ? 'Etage -' : 'Floor -';
                    };

                    // Special card for "other documents" units
                    const isOtherDocs = isOtherDocsUnit(unit);
                    if (isOtherDocs) {
                      const docCount = documents.filter(d => d.unitId === unit.id).length;
                      return (
                        <button
                          key={unit.id}
                          onClick={() => setSelectedProperty(unit.id)}
                          style={{
                            // Spans the whole row so it closes the list off
                            // instead of pairing up with a property card
                            gridColumn: '1 / -1',
                            width: '100%',
                            background: '#fff',
                            borderRadius: '16px',
                            border: '1px dashed #d1d5db',
                            padding: '20px',
                            cursor: 'pointer',
                            textAlign: 'left',
                            display: 'flex',
                            gap: '16px',
                            alignItems: 'center',
                            transition: 'all 0.2s ease',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.03)'
                          }}
                        >
                          <div style={{
                            width: '64px',
                            minWidth: '64px',
                            height: '64px',
                            borderRadius: '14px',
                            background: '#f3f4f6',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            <FolderOpen size={28} color="#9ca3af" />
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <h3 style={{ fontWeight: 600, fontSize: '15px', color: '#374151', margin: '0 0 4px 0' }}>
                              {language === 'de' ? 'Dokumente ohne Immobilie' : 'Documents without a property'}
                            </h3>
                            <p style={{ fontSize: '13px', color: '#9ca3af', margin: 0 }}>
                              {docCount > 0
                                ? (language === 'de'
                                    ? `${docCount} Dokument${docCount !== 1 ? 'e' : ''}, noch keiner Immobilie zugeordnet`
                                    : `${docCount} document${docCount !== 1 ? 's' : ''} not yet linked to a property`)
                                : (language === 'de'
                                    ? 'Hier landen Dokumente, die zu keiner Ihrer Immobilien passen'
                                    : "Files we can't match to one of your properties land here")}
                            </p>
                          </div>
                          <ChevronRight size={18} color="#d1d5db" />
                        </button>
                      );
                    }

                    return (
                      <button
                        key={unit.id}
                        onClick={() => setSelectedProperty(unit.id)}
                        style={{
                          width: '100%',
                          background: '#fff',
                          borderRadius: '16px',
                          /* Same card as the Selling and Sold buckets: a gold
                             rule and a gold-tinted lift, not a grey hairline.
                             The four buckets are one list seen four ways, so a
                             property should not change its frame when it
                             changes its status — only its contents do. */
                          border: '2px solid #D6B160',
                          boxShadow: '0 2px 8px rgba(214, 177, 96, 0.15)',
                          padding: '16px',
                          cursor: 'pointer',
                          textAlign: 'left',
                          display: 'flex',
                          gap: '16px',
                          alignItems: 'flex-start',
                          transition: 'all 0.2s ease',
                        }}
                      >
                        {/* Property Image - Left Side */}
                        <div
                          style={{
                            width: '100px',
                            minWidth: '100px',
                            height: '100px',
                            flexShrink: 0,
                            background: 'linear-gradient(135deg, #D6B160 0%, #c5a050 100%)',
                            position: 'relative',
                            overflow: 'hidden',
                            borderRadius: '12px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          {/* Only render an <img> when we actually have a URL.
                              With src="" the browser paints the alt text across
                              the tile instead of firing onError, which is why an
                              unmapped project (e.g. Clearpoint) showed its name
                              sprawled behind the status badge. No mapping → show
                              a quiet placeholder mark on the gold tile. */}
                          {imageUrl ? (
                            <img
                              src={imageUrl}
                              alt={unit.project || unit.name}
                              style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover'
                              }}
                              onError={(e) => {
                                const target = e.target as HTMLImageElement;
                                target.style.display = 'none';
                              }}
                            />
                          ) : (
                            <Building2
                              size={30}
                              color="rgba(255,255,255,0.75)"
                              strokeWidth={1.6}
                              aria-hidden="true"
                            />
                          )}
                          {/* Status Badge */}
                          <div 
                            style={{
                              position: 'absolute',
                              top: '6px',
                              left: '6px',
                              padding: '3px 9px',
                              /* Matches the FOR SALE chip on the Selling card:
                                 cream, gold rule, gold-brown type. Five badge
                                 colours (blue / green / violet / orange /
                                 amber) were five hues on a three-colour brand,
                                 and the word already says which status it is. */
                              backgroundColor: '#FFF8E7',
                              border: '1px solid #D6B160',
                              color: '#92400e',
                              fontSize: '10px',
                              fontWeight: 700,
                              borderRadius: '6px',
                              zIndex: 10
                            }}
                          >
                            {/* Off-plan follows the DLD construction feed, the
                                same rule as the bar below (isOffplanUnit). A
                                finished project still stored as 'Offplan' says
                                Ready, not Vacant: no lease on record is a data
                                gap, not proof the unit is empty. */}
                            {isOffplanUnit(unit) ? trData('Off-plan')
                              : (unit.status || '').toLowerCase() === 'rented' ? (language === 'de' ? 'Vermietet' : 'Rented')
                              : (unit.occupancyStatus || '').toLowerCase() === 'offplan' ? trData('Ready')
                              : trData(unit.occupancyStatus) || trData('Vacant')}
                          </div>
                        </div>

                        {/* Property Details - Right Side */}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          {/* Header */}
                          <h3 style={{ 
                            fontWeight: 700, 
                            fontSize: '16px', 
                            color: '#111827', 
                            margin: '0 0 2px 0',
                            lineHeight: 1.3
                          }}>{unit.name}</h3>
                          <p style={{ 
                            fontSize: '13px', 
                            color: '#6b7280', 
                            margin: '0 0 10px 0' 
                          }}>{unit.project}</p>

                          {/* Info Rows */}
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#374151' }}>
                              <Building2 style={{ width: '14px', height: '14px', color: '#9ca3af', flexShrink: 0 }} />
                              <span>{unit.unitNumber}</span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#374151' }}>
                              <Maximize style={{ width: '14px', height: '14px', color: '#9ca3af', flexShrink: 0 }} />
                              {(() => {
                                const areaNum = parseFloat(unit.area) || 0;
                                const areaDisplay = formatAreaDisplay(areaNum);
                                return (
                                  <>
                                    <span>{areaDisplay.primary}</span>
                                    <span style={{ color: '#9ca3af', fontSize: '11px' }}>/ {areaDisplay.secondary}</span>
                                  </>
                                );
                              })()}
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#374151' }}>
                              <span style={{ width: '14px', textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><BedDouble size={14} /></span>
                              <span>{getBedroomDisplay()}</span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#374151' }}>
                              <span style={{ width: '14px', textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Building size={14} /></span>
                              <span>{getFloorDisplay()}</span>
                            </div>
                            {/* Price per sqft with EUR/m² */}
                            {unit.purchaseValue && parseFloat(unit.area) > 0 && (
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#374151' }}>
                                <span style={{ width: '14px', textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><DollarSign size={14} /></span>
                                <span>AED {calculatePricePerSqft(unit.purchaseValue, parseFloat(unit.area)).toLocaleString('en-AE')}/sqft</span>
                                <span style={{ color: '#9ca3af', fontSize: '11px' }}>≈ €{(calculatePricePerSqm(unit.purchaseValue, parseFloat(unit.area)) * AED_TO_EUR_RATE).toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}/m²</span>
                              </div>
                            )}
                          </div>

                          {/* Current Value */}
                          <div style={{ 
                            marginTop: '10px',
                            display: 'flex', 
                            flexDirection: 'column',
                            gap: '2px'
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <span style={{ fontSize: '11px', color: '#9ca3af', textTransform: 'uppercase' }}>{t('currentValueLabel')}</span>
                              <span style={{ fontSize: '15px', fontWeight: 700, color: '#D6B160' }}>
                                {/* A unit created without currentValue (JBC 5 1102-1108) read
                                    "AED 0": fall back to its purchase price. */}
                                AED {(Number(unit.currentValue) || Number(unit.purchaseValue) || Number(unit.purchasePrice) || 0).toLocaleString('en-AE')}
                              </span>
                            </div>
                            {(Number(unit.currentValue) || Number(unit.purchaseValue) || Number(unit.purchasePrice)) > 0 && (
                              <span style={{ fontSize: '11px', color: '#6b7280', marginLeft: '85px' }}>
                                ≈ {formatEUR(Number(unit.currentValue) || Number(unit.purchaseValue) || Number(unit.purchasePrice))}
                              </span>
                            )}
                          </div>

                          {/* Construction Progress Bar on Card */}
                          {unit.constructionCompletionPercent != null && unit.constructionCompletionPercent < 100 && (
                            <div style={{ marginTop: '10px' }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                                <span style={{ fontSize: '11px', color: '#9ca3af', textTransform: 'uppercase' }}>{t('construction')}</span>
                                <span style={{ fontSize: '12px', fontWeight: 700, color: unit.constructionCompletionPercent >= 75 ? '#10b981' : unit.constructionCompletionPercent >= 40 ? '#f59e0b' : '#ef4444' }}>
                                  {unit.constructionCompletionPercent}%
                                </span>
                              </div>
                              <div style={{
                                width: '100%',
                                height: '6px',
                                backgroundColor: '#f3f4f6',
                                borderRadius: '9999px',
                                overflow: 'hidden'
                              }}>
                                <div style={{
                                  height: '100%',
                                  backgroundColor: unit.constructionCompletionPercent >= 75 ? '#10b981' : unit.constructionCompletionPercent >= 40 ? '#f59e0b' : '#ef4444',
                                  borderRadius: '9999px',
                                  transition: 'width 0.7s ease-out',
                                  width: `${Math.min(100, Math.max(0, unit.constructionCompletionPercent))}%`
                                }} />
                              </div>
                            </div>
                          )}
                          {unit.constructionCompletionPercent != null && unit.constructionCompletionPercent >= 100 && (
                            <div style={{
                              marginTop: '8px',
                              padding: '4px 10px',
                              background: '#f0fdf4',
                              borderRadius: '8px',
                              border: '1px solid #bbf7d0',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px'
                            }}>
                              <CheckCircle style={{ width: '14px', height: '14px', color: '#10b981' }} />
                              <span style={{ fontSize: '12px', fontWeight: 600, color: '#065f46' }}>{t('pvConstructionCompleted')}</span>
                            </div>
                          )}

                          {/* Sell Eligibility Badge */}
                          {isEligibleForSelling(unit.id) && !sellStatus[unit.id]?.listed && unit.status !== 'selling' && (
                            <div
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedProperty(unit.id);
                                setSellIsDistress(false); setSellOriginalPrice(''); setSellAskingPrice(''); setSellResult(null);
                                setShowSellModal(true);
                              }}
                              style={{
                                marginTop: '8px',
                                padding: '8px 12px',
                                background: 'linear-gradient(135deg, #D6B160, #c9a14e)',
                                borderRadius: '10px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '6px',
                                cursor: 'pointer',
                                boxShadow: '0 2px 8px rgba(214, 177, 96, 0.3)',
                              }}
                            >
                              <span style={{ fontSize: '13px' }}>🏷️</span>
                              <span style={{ fontSize: '12px', fontWeight: 700, color: '#fff' }}>
                                {language === 'de' ? 'Zum Verkauf anbieten' : 'Sell This Unit'}
                              </span>
                            </div>
                          )}
                          {sellStatus[unit.id]?.listed && (
                            <div style={{
                              marginTop: '8px',
                              padding: '6px 10px',
                              background: '#f0fdf4',
                              borderRadius: '8px',
                              border: '1px solid #bbf7d0',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px'
                            }}>
                              <CheckCircle size={13} color="#16a34a" />
                              <span style={{ fontSize: '11px', fontWeight: 600, color: '#16a34a' }}>
                                {language === 'de' ? 'Gelistet auf Verkaufsplattform' : 'Listed on Sell Platform'}
                              </span>
                            </div>
                          )}

                          {/* Sold — the owner records the completed sale and gets the ROI.
                              Sits under the listing CTA because a unit can be sold
                              off-platform without ever having been listed here. */}
                          <div
                            onClick={(e) => { e.stopPropagation(); openSoldModal(unit); }}
                            style={{
                              marginTop: '8px',
                              padding: '9px 12px',
                              background: '#fff',
                              borderRadius: '10px',
                              border: '1.5px solid #1a1a2e',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '7px',
                              cursor: 'pointer',
                            }}
                          >
                            <Receipt size={14} color="#1a1a2e" />
                            <span style={{ fontSize: '12px', fontWeight: 700, color: '#1a1a2e' }}>
                              {language === 'de' ? 'Als verkauft markieren' : 'Mark as Sold'}
                            </span>
                          </div>

                          {(!unit.purchaseValue || unit.purchaseValue === 0) && (() => {
  const unitDocs = documents.filter(d => d.unitId === unit.id);
  const hasPending = unitDocs.some(d => d.verificationStatus === 'pending_verification');
  const hasUploaded = unitDocs.length > 0;
  
  return (
    <div style={{
      marginTop: '8px',
      padding: '6px 10px',
      background: hasPending ? '#EFF6FF' : '#FFF8E1',
      borderRadius: '8px',
      border: hasPending ? '1px solid #93C5FD' : '1px solid #FFE082',
      display: 'flex',
      alignItems: 'center',
      gap: '6px'
    }}>
      <span style={{ display: 'flex', alignItems: 'center' }}>{hasPending ? <Clock size={14} /> : <FileText size={14} />}</span>
      <span style={{ fontSize: '11px', color: hasPending ? '#1e40af' : '#92400e', fontWeight: 500 }}>
        {hasPending 
          ? (t('pendingVerificationNote') || 'Documents pending verification')
          : (t('uploadDocsNote') || 'Upload documents to update your property data')}
      </span>
    </div>
  );
})()}
                        </div>
                      </button>
                    );
                  })}
                  {propertyStatusTab === 'rented' && tabUnits.filter(u => (u.status || '').toLowerCase() === 'rented').length === 0 && (
                    <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '32px 16px', color: '#9ca3af' }}>
                      <Home size={32} style={{ margin: '0 auto 12px', color: '#d1d5db' }} />
                      <div style={{ fontSize: '14px', fontWeight: 600 }}>
                        {language === 'de' ? 'Keine vermieteten Einheiten' : 'No rented units'}
                      </div>
                      <div style={{ fontSize: '12px', marginTop: '4px' }}>
                        {language === 'de' ? 'Vermietete Einheiten erscheinen hier' : 'Rented units will appear here'}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              )}
            </>
          )}

          {/* CASHFLOW / INSTALLMENT PLAN TAB CONTENT */}
          {mainTab === 'cashflow' && (
            <>
              {!CASHFLOW_ENABLED ? (
                <CashflowComingSoon />
              ) : (
                <InstallmentPlanView
                  currentInvestor={currentInvestor}
                  units={units}
                  onUploadDocument={(unitId) => {
                    setDocumentCategory('Unit Documents');
                    setShowAddDocumentsModal(true);
                  }}
                />
              )}
            </>
          )}

{/* PERSONAL DOCUMENTS TAB */}
          {mainTab === 'personal' && (
            <div className="p-4">
              {/* Upload Buttons - Manual + AI Smart Upload */}
              <div className="mb-4" style={{ display: 'flex', gap: '10px' }}>
                <button
                  onClick={() => {
                    setDocumentCategory('Personal Documents');
                    setShowAddDocumentsModal(true);
                  }}
                  style={{
                    flex: 1, padding: '14px 16px', borderRadius: '14px',
                    background: 'linear-gradient(135deg, #1a1a2e, #16213e)',
                    color: '#fff', fontSize: '14px', fontWeight: 700,
                    border: 'none', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                    boxShadow: '0 2px 12px rgba(0,0,0,0.12)',
                  }}
                >
                  <Plus size={18} />
                  {language === 'de' ? 'Manuell hochladen' : 'Manual Upload'}
                </button>
                {handleSmartUpload && (
                  <div style={{ flex: 1 }}>
                    <label
                      htmlFor="smart-upload-personal"
                      style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        gap: '8px', height: '100%',
                        padding: '14px 16px', borderRadius: '14px',
                        background: smartUploading ? '#b8942e' : 'linear-gradient(135deg, #D6B160, #b8942e)',
                        color: '#fff', fontSize: '14px', fontWeight: 700,
                        cursor: smartUploading ? 'not-allowed' : 'pointer',
                        border: 'none', boxShadow: '0 2px 8px rgba(214, 177, 96, 0.4)',
                      }}
                    >
                      {smartUploading ? (
                        <>
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                          <span>{smartUploadProgress?.fileName || (appLanguage() === 'de' ? 'Wird verarbeitet…' : 'Processing...')}</span>
                        </>
                      ) : (
                        <>
                          <Upload size={18} />
                          <span>{language === 'de' ? 'KI Upload' : 'AI Smart Upload'}</span>
                        </>
                      )}
                    </label>
                    <input
                      id="smart-upload-personal"
                      type="file"
                      onChange={e => handleSmartUpload!(e, selectedUnit?.id)}
                      accept=".pdf,.jpg,.jpeg,.png"
                      multiple
                      className="hidden"
                      disabled={smartUploading}
                    />
                  </div>
                )}
              </div>

              {/* Personal Documents List */}
              {(() => {
                const personalDocs = documents.filter(d => 
                  d.category === 'Personal Documents' || 
                  d.documentType === 'Passport' || 
                  d.documentType === 'Visa' || 
                  d.documentType === 'ID' ||
                  d.type === 'Passport' || 
                  d.type === 'Visa' || 
                  d.type === 'ID'
                );

                if (personalDocs.length === 0) {
                  return (
                    <div style={{
                      padding: '32px 24px',
                      textAlign: 'center',
                      background: 'linear-gradient(135deg, #f0f7ff 0%, #e8f4fd 100%)',
                      borderRadius: '16px',
                      border: '2px dashed #90caf9',
                    }}>
                      <div style={{ marginBottom: '12px', display: 'flex', justifyContent: 'center' }}><FileText size={40} color="#1565c0" /></div>
                      <p style={{ color: '#1565c0', fontSize: '15px', fontWeight: '600', margin: '0 0 4px 0' }}>
                        {t('noPersonalDocs') || 'No personal documents yet'}
                      </p>
                      <p style={{ color: '#64748b', fontSize: '13px', margin: '0 0 16px 0' }}>
                        {t('uploadPassportVisa') || 'Upload your Passport, Visa, or Emirates ID'}
                      </p>
                    </div>
                  );
                }

                return (
                  <div className="space-y-3">
                    {personalDocs.map(doc => (
                      <div key={doc.id} className="bg-white rounded-xl p-4 border border-gray-200" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                        <div className="flex items-start justify-between">
                          <div className="flex items-start gap-3 flex-1">
                            <div style={{
                              width: '40px',
                              height: '40px',
                              borderRadius: '10px',
                              background: doc.type === 'Passport' || doc.documentType === 'Passport' ? '#EFF6FF'
                                : doc.type === 'Visa' || doc.documentType === 'Visa' ? '#F0FDF4'
                                : '#FEF3C7',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '20px',
                              flexShrink: 0
                            }}>
                              <FileText size={20} color="#D6B160" />
                            </div>
                            <div className="flex-1">
                              <h4 className="font-semibold text-gray-900">{trData(doc.type || doc.documentType || 'Document')}</h4>
                              <p className="text-sm text-gray-600">{doc.fileName || doc.name || trData('Unknown')}</p>
                              {doc.extractedData && (
                                <div style={{ marginTop: '6px', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                  {/* Passport */}
                                  {doc.extractedData.passportNumber && (
                                    <p style={{ fontSize: '12px', color: '#374151' }}>
                                      <span style={{ color: '#9ca3af' }}>{t('passportNo') || 'Passport No'}:</span> {doc.extractedData.passportNumber}
                                    </p>
                                  )}
                                  {/* Visa */}
                                  {doc.extractedData.visaNumber && (
                                    <p style={{ fontSize: '12px', color: '#374151' }}>
                                      <span style={{ color: '#9ca3af' }}>{t('visaNo') || 'Visa No'}:</span> {doc.extractedData.visaNumber}
                                    </p>
                                  )}
                                  {doc.extractedData.visaType && (
                                    <p style={{ fontSize: '12px', color: '#374151' }}>
                                      <span style={{ color: '#9ca3af' }}>{t('visaType') || 'Visa Type'}:</span> {doc.extractedData.visaType}
                                    </p>
                                  )}
                                  {/* Emirates ID */}
                                  {doc.extractedData.emiratesId && (
                                    <p style={{ fontSize: '12px', color: '#374151' }}>
                                      <span style={{ color: '#9ca3af' }}>{t('emiratesId') || 'Emirates ID'}:</span> {doc.extractedData.emiratesId}
                                    </p>
                                  )}
                                  {doc.extractedData.idNumber && (
                                    <p style={{ fontSize: '12px', color: '#374151' }}>
                                      <span style={{ color: '#9ca3af' }}>{t('idNo') || 'ID No'}:</span> {doc.extractedData.idNumber}
                                    </p>
                                  )}
                                  {/* Expiry - common to all */}
                                  {doc.extractedData.expiryDate && (
                                    <p style={{ fontSize: '12px', color: new Date(doc.extractedData.expiryDate) < new Date() ? '#ef4444' : '#374151' }}>
                                      <span style={{ color: '#9ca3af' }}>{t('expiry') || 'Expiry'}:</span> {doc.extractedData.expiryDate}
                                      {new Date(doc.extractedData.expiryDate) < new Date() && ' Expired'}
                                    </p>
                                  )}
                                </div>
                              )}
                              <p className="text-xs text-gray-400 mt-1">{doc.uploadDate || (appLanguage() === 'de' ? 'Datum unbekannt' : 'Unknown date')}</p>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleViewFile(doc)}
                              className="p-2 hover:bg-gray-100 rounded-lg"
                            >
                              <Eye className="h-4 w-4 text-gray-600" />
                            </button>
                            <button
                              onClick={() => handleDownloadFile(doc)}
                              className="p-2 hover:bg-gray-100 rounded-lg"
                            >
                              <Download className="h-4 w-4 text-gray-600" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>
          )}

          {/* COMPANY DOCUMENTS TAB */}
          {mainTab === 'company' && (
            <div className="p-4">
              {/* Upload Buttons - Manual + AI Smart Upload */}
              <div className="mb-4" style={{ display: 'flex', gap: '10px' }}>
                <button
                  onClick={() => {
                    setDocumentCategory('Company Documents');
                    setShowAddDocumentsModal(true);
                  }}
                  style={{
                    flex: 1, padding: '14px 16px', borderRadius: '14px',
                    background: 'linear-gradient(135deg, #1a1a2e, #16213e)',
                    color: '#fff', fontSize: '14px', fontWeight: 700,
                    border: 'none', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                    boxShadow: '0 2px 12px rgba(0,0,0,0.12)',
                  }}
                >
                  <Plus size={18} />
                  {language === 'de' ? 'Manuell hochladen' : 'Manual Upload'}
                </button>
                {handleSmartUpload && (
                  <div style={{ flex: 1 }}>
                    <label
                      htmlFor="smart-upload-company"
                      style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        gap: '8px', height: '100%',
                        padding: '14px 16px', borderRadius: '14px',
                        background: smartUploading ? '#b8942e' : 'linear-gradient(135deg, #D6B160, #b8942e)',
                        color: '#fff', fontSize: '14px', fontWeight: 700,
                        cursor: smartUploading ? 'not-allowed' : 'pointer',
                        border: 'none', boxShadow: '0 2px 8px rgba(214, 177, 96, 0.4)',
                      }}
                    >
                      {smartUploading ? (
                        <>
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                          <span>{smartUploadProgress?.fileName || (appLanguage() === 'de' ? 'Wird verarbeitet…' : 'Processing...')}</span>
                        </>
                      ) : (
                        <>
                          <Upload size={18} />
                          <span>{language === 'de' ? 'KI Upload' : 'AI Smart Upload'}</span>
                        </>
                      )}
                    </label>
                    <input
                      id="smart-upload-company"
                      type="file"
                      onChange={e => handleSmartUpload!(e, selectedUnit?.id)}
                      accept=".pdf,.jpg,.jpeg,.png"
                      multiple
                      className="hidden"
                      disabled={smartUploading}
                    />
                  </div>
                )}
              </div>

              {/* Company Documents List */}
              {(() => {
                const companyDocs = documents.filter(d => 
                  d.category === 'Company Documents' || 
                  d.documentType === 'Trade License' || 
                  d.documentType === 'MOA' || 
                  d.documentType === 'Power of Attorney' ||
                  d.documentType === 'Certificate of Incorporation' ||
                  d.documentType === 'Share Certificate' ||
                  d.documentType === 'Board Resolution' ||
                  d.documentType === 'Tax Certificate' ||
                  d.documentType === 'Bank Reference Letter' ||
                  d.type === 'Trade License' || 
                  d.type === 'MOA' || 
                  d.type === 'Power of Attorney' ||
                  d.type === 'Certificate of Incorporation' ||
                  d.type === 'Share Certificate' ||
                  d.type === 'Board Resolution' ||
                  d.type === 'Tax Certificate' ||
                  d.type === 'Bank Reference Letter'
                );

                if (companyDocs.length === 0) {
                  return (
                    <div style={{
                      padding: '32px 24px',
                      textAlign: 'center',
                      background: 'linear-gradient(135deg, #f0f7ff 0%, #e8f4fd 100%)',
                      borderRadius: '16px',
                      border: '2px dashed #90caf9',
                    }}>
                      <div style={{ marginBottom: '12px', display: 'flex', justifyContent: 'center' }}><Building2 size={40} color="#1565c0" /></div>
                      <p style={{ color: '#1565c0', fontSize: '15px', fontWeight: '600', margin: '0 0 4px 0' }}>
                        {t('noCompanyDocs') || 'No company documents yet'}
                      </p>
                      <p style={{ color: '#64748b', fontSize: '13px', margin: '0 0 16px 0' }}>
                        {t('uploadCompanyDocsDesc') || 'Upload your Trade License, MOA, Power of Attorney, and more'}
                      </p>
                    </div>
                  );
                }

                return (
                  <div className="space-y-3">
                    {companyDocs.map(doc => (
                      <div key={doc.id} className="bg-white rounded-xl p-4 border border-gray-200" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                        <div className="flex items-start justify-between">
                          <div className="flex items-start gap-3 flex-1">
                            <div style={{
                              width: '40px',
                              height: '40px',
                              borderRadius: '10px',
                              background: doc.type === 'Trade License' || doc.documentType === 'Trade License' ? '#FEF3C7'
                                : doc.type === 'MOA' || doc.documentType === 'MOA' ? '#EFF6FF'
                                : doc.type === 'Power of Attorney' || doc.documentType === 'Power of Attorney' ? '#F0FDF4'
                                : '#F3E8FF',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '20px',
                              flexShrink: 0
                            }}>
                              {doc.type === 'Trade License' || doc.documentType === 'Trade License' ? <ScrollText size={20} color="#D6B160" />
                                : doc.type === 'MOA' || doc.documentType === 'MOA' ? <FileText size={20} color="#D6B160" />
                                : doc.type === 'Power of Attorney' || doc.documentType === 'Power of Attorney' ? <Scale size={20} color="#D6B160" />
                                : doc.type === 'Certificate of Incorporation' || doc.documentType === 'Certificate of Incorporation' ? <Landmark size={20} color="#D6B160" />
                                : doc.type === 'Share Certificate' || doc.documentType === 'Share Certificate' ? <BarChart3 size={20} color="#D6B160" />
                                : doc.type === 'Board Resolution' || doc.documentType === 'Board Resolution' ? <ClipboardList size={20} color="#D6B160" />
                                : doc.type === 'Tax Certificate' || doc.documentType === 'Tax Certificate' ? <Receipt size={20} color="#D6B160" />
                                : doc.type === 'Bank Reference Letter' || doc.documentType === 'Bank Reference Letter' ? <Landmark size={20} color="#D6B160" />
                                : <FileText size={20} color="#D6B160" />}
                            </div>
                            <div className="flex-1">
                              <h4 className="font-semibold text-gray-900">{trData(doc.type || doc.documentType || 'Document')}</h4>
                              <p className="text-sm text-gray-600">{doc.fileName || doc.name || trData('Unknown')}</p>
                              {doc.extractedData && (
                                <div style={{ marginTop: '6px', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                  {doc.extractedData.licenseNumber && (
                                    <p style={{ fontSize: '12px', color: '#374151' }}>
                                      <span style={{ color: '#9ca3af' }}>{t('licenseNo') || 'License No'}:</span> {doc.extractedData.licenseNumber}
                                    </p>
                                  )}
                                  {doc.extractedData.companyName && (
                                    <p style={{ fontSize: '12px', color: '#374151' }}>
                                      <span style={{ color: '#9ca3af' }}>{t('companyName') || 'Company'}:</span> {doc.extractedData.companyName}
                                    </p>
                                  )}
                                  {doc.extractedData.expiryDate && (
                                    <p style={{ fontSize: '12px', color: new Date(doc.extractedData.expiryDate) < new Date() ? '#ef4444' : '#374151' }}>
                                      <span style={{ color: '#9ca3af' }}>{t('expiry') || 'Expiry'}:</span> {doc.extractedData.expiryDate}
                                      {new Date(doc.extractedData.expiryDate) < new Date() && ' Expired'}
                                    </p>
                                  )}
                                </div>
                              )}
                              <p className="text-xs text-gray-400 mt-1">{doc.uploadDate || (appLanguage() === 'de' ? 'Datum unbekannt' : 'Unknown date')}</p>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleViewFile(doc)}
                              className="p-2 hover:bg-gray-100 rounded-lg"
                            >
                              <Eye className="h-4 w-4 text-gray-600" />
                            </button>
                            <button
                              onClick={() => handleDownloadFile(doc)}
                              className="p-2 hover:bg-gray-100 rounded-lg"
                            >
                              <Download className="h-4 w-4 text-gray-600" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>
          )}

          </div>    
) : renderUnitDetail ? (
        renderUnitDetail(selectedProperty, () => setSelectedProperty(null))
) : ( 

        // Property Detail View
        <div className="max-w-7xl mx-auto">
          {/* Back Button */}
          <div className="bg-white border-b border-gray-200 px-4 py-3">
            <button
              onClick={() => setSelectedProperty(null)}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
            >
              <ChevronLeft className="h-5 w-5" />
              <span className="font-medium">{t('backToProperties')}</span>
            </button>
          </div>

          {/* Other Documents - simplified detail view */}
          {selectedUnit?.name?.toLowerCase() === 'other documents' ? (
            <>
              <div style={{
                padding: '24px 16px',
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
                borderBottom: '1px solid #e5e7eb',
                background: '#fafafa'
              }}>
                <div style={{
                  width: '56px',
                  height: '56px',
                  borderRadius: '14px',
                  background: '#f3f4f6',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <FolderOpen size={28} color="#9ca3af" />
                </div>
                <div>
                  <h1 style={{ fontSize: '20px', fontWeight: 700, color: '#111827', margin: '0 0 4px 0' }}>
                    {language === 'de' ? 'Dokumente ohne Immobilie' : 'Documents without a property'}
                  </h1>
                  <p style={{ fontSize: '13px', color: '#9ca3af', margin: 0 }}>
                    {language === 'de'
                      ? 'Hier landen Dokumente, die zu keiner Ihrer Immobilien passen'
                      : "Files we can't match to one of your properties land here"}
                  </p>
                </div>
              </div>
              <div className="p-4">
                <div className="space-y-4">
                  {(!propertyDocuments || propertyDocuments.length === 0) ? (
                    <div style={{
                      padding: '32px 24px',
                      textAlign: 'center',
                      background: '#fff',
                      borderRadius: '16px',
                      border: '1px solid #e5e7eb'
                    }}>
                      <FolderOpen size={40} color="#d1d5db" style={{ margin: '0 auto 12px' }} />
                      <p style={{ color: '#6b7280', fontSize: '14px', margin: 0 }}>
                        {language === 'de' ? 'Noch keine Dokumente vorhanden' : 'No documents yet'}
                      </p>
                    </div>
                  ) : (
                    propertyDocuments.map((doc: any) => {
                      const docId = doc.id || doc.documentId;
                      return (
                        <div key={docId} style={{
                          background: '#fff',
                          borderRadius: '12px',
                          border: '1px solid #e5e7eb',
                          padding: '14px 16px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px'
                        }}>
                          <FileText size={20} color="#9ca3af" />
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <p style={{ margin: 0, fontWeight: 600, fontSize: '14px', color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {doc.originalName || doc.fileName || 'Document'}
                            </p>
                            {doc.documentType && (
                              <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#9ca3af' }}>{doc.documentType}</p>
                            )}
                          </div>
                          <button
                            onClick={() => handleViewFile(doc)}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '6px' }}
                          >
                            <Eye size={18} color="#6b7280" />
                          </button>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </>
          ) : (
          <>
          {/* Property Header */}
          <div className="bg-gradient-to-br from-[#D6B160] to-[#c5a050] p-6 text-white">
            <h1 className="text-2xl font-bold mb-1">{selectedUnit?.name}</h1>
            <p className="text-white text-opacity-90 mb-4">{selectedUnit?.project}</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <p className="text-white text-opacity-75 text-sm">{t('unitNumber')}</p>
                <p className="font-semibold">{selectedUnit?.unitNumber}</p>
              </div>
              <div>
                <p className="text-white text-opacity-75 text-sm">{t('area')}</p>
                {(() => {
                  const areaNum = parseFloat(selectedUnit?.area || '0');
                  const areaDisplay = formatAreaDisplay(areaNum);
                  return (
                    <>
                      <p className="font-semibold">{areaDisplay.primary}</p>
                      <p className="text-white text-opacity-60 text-xs">≈ {areaDisplay.secondary}</p>
                    </>
                  );
                })()}
              </div>
              <div>
                <p className="text-white text-opacity-75 text-sm">{t('currentValue')}</p>
                <p className="font-semibold">AED {(selectedUnit?.currentValue || 0).toLocaleString('en-AE')}</p>
                <p className="text-white text-opacity-60 text-xs">≈ {formatEUR(selectedUnit?.currentValue || 0)}</p>
              </div>
              <div>
                <p className="text-white text-opacity-75 text-sm">{t('status')}</p>
                <p className="font-semibold">{selectedUnit?.occupancyStatus}</p>
              </div>
            </div>
          </div>

          {/* Sell status badge inside property detail (read-only) */}
          {sellStatus[selectedUnit?.id || '']?.listed && (
          <div className="bg-white px-4 py-3 border-b border-gray-100">
              <div style={{
                display: 'flex', alignItems: 'center', gap: '12px',
                padding: '12px 16px', borderRadius: '12px',
                background: 'linear-gradient(135deg, #f0fdf4, #dcfce7)',
                border: '1px solid #bbf7d0'
              }}>
                <div style={{
                  width: '36px', height: '36px', borderRadius: '50%',
                  background: '#22c55e', display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}>
                  <CheckCircle style={{ width: '18px', height: '18px', color: '#fff' }} />
                </div>
                <div style={{ flex: 1 }}>
                  <p style={{ margin: 0, fontWeight: 600, color: '#166534', fontSize: '14px' }}>
                    {language === 'de' ? 'Auf der Verkaufsplattform gelistet' : 'Listed on Sell Platform'}
                  </p>
                  <p style={{ margin: 0, color: '#16a34a', fontSize: '12px' }}>
                    {language === 'de' ? 'Status' : 'Status'}: {sellStatus[selectedUnit?.id || '']?.status || 'active'}
                    {sellStatus[selectedUnit?.id || '']?.createdAt &&
                      ` · ${new Date(sellStatus[selectedUnit?.id || '']?.createdAt || '').toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}`
                    }
                  </p>
                </div>
              </div>
          </div>
          )}

          {/* Sale status inside the property detail — record it, or correct
              what was recorded, without going back to the list. */}
          {selectedUnit && (
            <div className="bg-white px-4 py-3 border-b border-gray-100">
              {selectedUnit.status === 'sold' ? (() => {
                const sale = summariseSale(selectedUnit as any)!;
                const won = sale.netProfit >= 0;
                return (
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap',
                    padding: '12px 16px', borderRadius: '12px',
                    background: won ? '#f0fdf4' : '#fef2f2',
                    border: `1px solid ${won ? '#bbf7d0' : '#fecaca'}`
                  }}>
                    <div style={{ flex: 1, minWidth: '160px' }}>
                      <p style={{ margin: 0, fontWeight: 700, color: won ? '#065f46' : '#991b1b', fontSize: '14px' }}>
                        {language === 'de' ? 'Verkauft' : 'Sold'}
                        {' · '}AED {Math.round(sale.salePrice).toLocaleString('en-AE')}
                      </p>
                      <p style={{ margin: '2px 0 0', color: won ? '#16a34a' : '#b91c1c', fontSize: '12px' }}>
                        {sale.hasBasis
                          ? `${language === 'de' ? 'Nettogewinn' : 'Net profit'} ${won ? '+' : '−'}AED ${Math.abs(Math.round(sale.netProfit)).toLocaleString('en-AE')} · ROI ${won ? '+' : ''}${sale.roiPercent.toFixed(1)}%`
                          : (language === 'de' ? 'Kaufpreis fehlt — kein ROI' : 'Purchase price missing — no ROI')}
                      </p>
                    </div>
                    <button
                      onClick={() => openSoldModal(selectedUnit)}
                      style={{
                        padding: '10px 14px', borderRadius: '10px', cursor: 'pointer',
                        border: '1.5px solid #1a1a2e', background: '#fff', color: '#1a1a2e',
                        fontSize: '12px', fontWeight: 700, fontFamily: 'inherit',
                        display: 'flex', alignItems: 'center', gap: '6px'
                      }}
                    >
                      <PenLine size={13} />
                      {language === 'de' ? 'Bearbeiten' : 'Edit'}
                    </button>
                  </div>
                );
              })() : (
                <button
                  onClick={() => openSoldModal(selectedUnit)}
                  style={{
                    width: '100%', padding: '13px', borderRadius: '12px', cursor: 'pointer',
                    border: '1.5px solid #1a1a2e', background: '#fff', color: '#1a1a2e',
                    fontSize: '13px', fontWeight: 700, fontFamily: 'inherit',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px'
                  }}
                >
                  <Receipt size={15} />
                  {language === 'de' ? 'Als verkauft markieren — ROI berechnen' : 'Mark as Sold — calculate the ROI'}
                </button>
              )}
            </div>
          )}

          {/* Sell Confirmation Modal */}
          {showSellModal && (
            <div style={{
              position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
              background: 'rgba(0,0,0,0.5)', zIndex: 9999,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              padding: '20px'
            }} onClick={() => { if (!sellLoading) { setShowSellModal(false); setSellResult(null); } }}>
              <div style={{
                background: '#fff', borderRadius: '20px', maxWidth: '420px', width: '100%',
                padding: '28px', boxShadow: '0 20px 60px rgba(0,0,0,0.2)'
              }} onClick={(e) => e.stopPropagation()}>

                {!sellResult ? (
                  <>
                    <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                      <div style={{
                        width: '64px', height: '64px', borderRadius: '50%', margin: '0 auto 16px',
                        background: 'linear-gradient(135deg, #D6B160, #e8c97a)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        boxShadow: '0 4px 12px rgba(214, 177, 96, 0.3)'
                      }}>
                        <Tag style={{ width: '28px', height: '28px', color: '#fff' }} />
                      </div>
                      <h3 style={{ margin: '0 0 8px', fontSize: '20px', fontWeight: 700, color: '#111827' }}>
                        {language === 'de' ? 'Einheit zum Verkauf listen' : 'List Unit for Sale'}
                      </h3>
                      <p style={{ margin: 0, color: '#6b7280', fontSize: '14px', lineHeight: '1.5' }}>
                        {language === 'de'
                          ? `${selectedUnit?.name || selectedUnit?.unitNumber} bei ${selectedUnit?.project} wird auf unserer Verkaufsplattform gelistet.`
                          : `${selectedUnit?.name || selectedUnit?.unitNumber} at ${selectedUnit?.project} will be listed on our sell platform.`}
                      </p>
                    </div>

                    {/* Distress Toggle */}
                    <div style={{
                      background: '#f9fafb', borderRadius: '12px', padding: '16px', marginBottom: '16px'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px' }}>
                        <label style={{ fontSize: '14px', fontWeight: 600, color: '#374151' }}>
                          {language === 'de' ? 'Notverkauf (Distress Sale)?' : 'Is this a distress sale?'}
                        </label>
                        <button
                          type="button"
                          onClick={() => setSellIsDistress(!sellIsDistress)}
                          style={{
                            width: '48px', height: '26px', borderRadius: '13px',
                            background: sellIsDistress ? '#dc2626' : '#d1d5db',
                            border: 'none', cursor: 'pointer', position: 'relative',
                            transition: 'background 0.2s'
                          }}
                        >
                          <div style={{
                            width: '22px', height: '22px', borderRadius: '50%',
                            background: '#fff', position: 'absolute', top: '2px',
                            left: sellIsDistress ? '24px' : '2px',
                            transition: 'left 0.2s',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                          }} />
                        </button>
                      </div>
                      {sellIsDistress && (
                        <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#dc2626' }}>
                          🔥 {language === 'de' ? 'Wird als Notverkauf markiert' : 'Will be marked as distress sale'}
                        </p>
                      )}
                    </div>

                    {/* DLD Market Value */}
                    {dldUnitData?.dldMarketValue > 0 && (
                      <div style={{
                        background: '#f0fdf4', borderRadius: '12px', padding: '16px', marginBottom: '16px',
                        border: '1px solid #bbf7d0'
                      }}>
                        <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#166534', marginBottom: '6px' }}>
                          {language === 'de' ? 'DLD Marktwert (AED)' : 'DLD Market Value (AED)'}
                        </label>
                        <p style={{
                          margin: 0, fontSize: '18px', fontWeight: 700, color: '#166534'
                        }}>
                          AED {dldUnitData.dldMarketValue.toLocaleString('en-AE')}
                        </p>
                        <p style={{ margin: '4px 0 0', fontSize: '11px', color: '#6b7280' }}>
                          {language === 'de' ? 'Basierend auf DLD-Transaktionsdaten' : 'Based on DLD transaction data'}
                          {dldUnitData.comparablesCount > 0 && ` · ${dldUnitData.comparablesCount} ${t('pvComparables')}`}
                        </p>
                      </div>
                    )}

                    {/* Pricing Fields */}
                    <div style={{
                      background: '#fffbeb', borderRadius: '12px', padding: '16px', marginBottom: '20px',
                      border: '1px solid #fde68a'
                    }}>
                      {sellIsDistress && (
                        <div style={{ marginBottom: '12px' }}>
                          <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#92400e', marginBottom: '6px' }}>
                            {language === 'de' ? 'Originalpreis (AED)' : 'Original Price (AED)'}
                          </label>
                          <input
                            type="text"
                            placeholder={t('pvEgPriceOriginal')}
                            value={sellOriginalPrice}
                            onChange={(e) => setSellOriginalPrice(e.target.value)}
                            style={{
                              width: '100%', padding: '10px 12px', borderRadius: '8px',
                              border: '1px solid #fde68a', background: '#fff',
                              fontSize: '15px', fontWeight: 600, color: '#111827',
                              outline: 'none', boxSizing: 'border-box'
                            }}
                          />
                        </div>
                      )}
                      <div>
                        <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#92400e', marginBottom: '6px' }}>
                          {language === 'de' ? 'Verkaufspreis (AED)' : 'Selling Price (AED)'}
                        </label>
                        <input
                          type="text"
                          placeholder={t('pvEgPriceSelling')}
                          value={sellAskingPrice}
                          onChange={(e) => setSellAskingPrice(e.target.value)}
                          style={{
                            width: '100%', padding: '10px 12px', borderRadius: '8px',
                            border: '1px solid #fde68a', background: '#fff',
                            fontSize: '15px', fontWeight: 600, color: '#111827',
                            outline: 'none', boxSizing: 'border-box'
                          }}
                        />
                      </div>
                      {/* Discount vs DLD Market Value (priority) or Original Price */}
                      {sellAskingPrice && (() => {
                        const ask = parseFloat(sellAskingPrice.replace(/,/g, ''));
                        const marketVal = dldUnitData?.dldMarketValue || 0;
                        const orig = sellIsDistress && sellOriginalPrice ? parseFloat(sellOriginalPrice.replace(/,/g, '')) : 0;

                        if (marketVal > 0 && ask > 0 && marketVal > ask) {
                          const discount = Math.round(((marketVal - ask) / marketVal) * 100);
                          return (
                            <p style={{ margin: '8px 0 0', fontSize: '13px', fontWeight: 700, color: '#dc2626' }}>
                              ↘ {discount}% {language === 'de' ? 'unter DLD-Marktwert' : 'below DLD market value'}
                            </p>
                          );
                        } else if (sellIsDistress && orig > 0 && ask > 0 && orig > ask) {
                          const discount = Math.round(((orig - ask) / orig) * 100);
                          return (
                            <p style={{ margin: '8px 0 0', fontSize: '13px', fontWeight: 700, color: '#dc2626' }}>
                              ↘ {discount}% {language === 'de' ? 'unter Originalpreis' : 'below original price'}
                            </p>
                          );
                        }
                        return null;
                      })()}
                    </div>

                    <div style={{ display: 'flex', gap: '12px' }}>
                      <button
                        onClick={() => { setShowSellModal(false); setSellResult(null); }}
                        disabled={sellLoading}
                        style={{
                          flex: 1, padding: '14px', borderRadius: '12px',
                          border: '1px solid #e5e7eb', background: '#fff',
                          color: '#374151', fontWeight: 600, fontSize: '14px', cursor: 'pointer'
                        }}
                      >
                        {language === 'de' ? 'Abbrechen' : 'Cancel'}
                      </button>
                      <button
                        onClick={handleInitiateSell}
                        disabled={sellLoading || !sellAskingPrice || (sellIsDistress && !sellOriginalPrice)}
                        style={{
                          flex: 1, padding: '14px', borderRadius: '12px',
                          border: 'none', background: 'linear-gradient(135deg, #D6B160, #e8c97a)',
                          color: '#fff', fontWeight: 700, fontSize: '14px', cursor: 'pointer',
                          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                          opacity: (sellLoading || !sellAskingPrice || (sellIsDistress && !sellOriginalPrice)) ? 0.5 : 1
                        }}
                      >
                        {sellLoading ? (
                          <>
                            <Loader2 style={{ width: '16px', height: '16px', animation: 'spin 1s linear infinite' }} />
                            {language === 'de' ? 'Wird gelistet...' : 'Listing...'}
                          </>
                        ) : (
                          <>{language === 'de' ? 'Jetzt listen' : 'List Now'}</>
                        )}
                      </button>
                    </div>
                  </>
                ) : sellResult.success ? (
                  <div style={{ textAlign: 'center' }}>
                    <div style={{
                      width: '64px', height: '64px', borderRadius: '50%', margin: '0 auto 16px',
                      background: '#dcfce7', display: 'flex', alignItems: 'center', justifyContent: 'center'
                    }}>
                      <CheckCircle style={{ width: '32px', height: '32px', color: '#22c55e' }} />
                    </div>
                    <h3 style={{ margin: '0 0 8px', fontSize: '20px', fontWeight: 700, color: '#111827' }}>
                      {language === 'de' ? 'Erfolgreich gelistet!' : 'Successfully Listed!'}
                    </h3>
                    <p style={{ margin: '0 0 20px', color: '#6b7280', fontSize: '14px' }}>
                      {language === 'de'
                        ? 'Ihre Einheit ist jetzt auf der Verkaufsplattform live.'
                        : 'Your unit is now live on the sell platform.'}
                    </p>

                    {sellResult.credentials && (
                      <div style={{
                        background: '#eff6ff', borderRadius: '12px', padding: '16px',
                        marginBottom: '20px', textAlign: 'left', border: '1px solid #bfdbfe'
                      }}>
                        <p style={{ margin: '0 0 8px', fontSize: '13px', fontWeight: 600, color: '#1e40af' }}>
                          {language === 'de' ? 'Ihre Zugangsdaten:' : 'Your platform login:'}
                        </p>
                        <p style={{ margin: '0 0 4px', fontSize: '13px', color: '#3b82f6' }}>
                          {t('email')}: <strong>{sellResult.credentials.email}</strong>
                        </p>
                        <p style={{ margin: '0 0 4px', fontSize: '13px', color: '#3b82f6' }}>
                          {t('pvPassword')}: <strong>{sellResult.credentials.tempPassword}</strong>
                        </p>
                        <p style={{ margin: '8px 0 0', fontSize: '11px', color: '#6b7280' }}>
                          {language === 'de' ? 'Bitte aendern Sie Ihr Passwort nach dem ersten Login.' : 'Please change your password after first login.'}
                        </p>
                      </div>
                    )}

                    <div style={{ display: 'flex', gap: '12px' }}>
                      <button
                        onClick={() => { setShowSellModal(false); setSellResult(null); }}
                        style={{
                          flex: 1, padding: '14px', borderRadius: '12px',
                          border: '1px solid #e5e7eb', background: '#fff',
                          color: '#374151', fontWeight: 600, fontSize: '14px', cursor: 'pointer'
                        }}
                      >
                        {language === 'de' ? 'Schliessen' : 'Close'}
                      </button>
                      {sellResult.sellerFormUrl && (
                        <button
                          onClick={() => window.open(sellResult.sellerFormUrl, '_blank')}
                          style={{
                            flex: 1, padding: '14px', borderRadius: '12px',
                            border: 'none', background: 'linear-gradient(135deg, #D6B160, #e8c97a)',
                            color: '#fff', fontWeight: 700, fontSize: '14px', cursor: 'pointer',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px'
                          }}
                        >
                          <ExternalLink style={{ width: '14px', height: '14px' }} />
                          {language === 'de' ? 'Plattform oeffnen' : 'Open Platform'}
                        </button>
                      )}
                    </div>
                  </div>
                ) : (
                  <div style={{ textAlign: 'center' }}>
                    <div style={{
                      width: '64px', height: '64px', borderRadius: '50%', margin: '0 auto 16px',
                      background: '#fee2e2', display: 'flex', alignItems: 'center', justifyContent: 'center'
                    }}>
                      <AlertCircle style={{ width: '32px', height: '32px', color: '#ef4444' }} />
                    </div>
                    <h3 style={{ margin: '0 0 8px', fontSize: '20px', fontWeight: 700, color: '#111827' }}>
                      {language === 'de' ? 'Etwas ist schiefgelaufen' : 'Something Went Wrong'}
                    </h3>
                    <p style={{ margin: '0 0 20px', color: '#6b7280', fontSize: '14px' }}>
                      {language === 'de' ? 'Bitte versuchen Sie es spaeter erneut.' : 'Please try again later or contact support.'}
                    </p>
                    <button
                      onClick={() => { setShowSellModal(false); setSellResult(null); }}
                      style={{
                        width: '100%', padding: '14px', borderRadius: '12px',
                        border: '1px solid #e5e7eb', background: '#fff',
                        color: '#374151', fontWeight: 600, fontSize: '14px', cursor: 'pointer'
                      }}
                    >
                      {language === 'de' ? 'Schliessen' : 'Close'}
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Tabs */}
          <div className="bg-white border-b border-gray-200 px-4">
            <div className="flex gap-6">
              {(['summary', 'income', 'documents'] as const).map(tab => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`py-4 px-2 border-b-2 transition-colors ${
                    activeTab === tab
                      ? 'border-[#D6B160] text-[#D6B160] font-semibold'
                      : 'border-transparent text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {t(tab)}
                </button>
              ))}
            </div>
          </div>

          {/* Tab Content */}
          <div className="p-4">
          {activeTab === 'summary' && (
  <div className="space-y-6">
    
    {/* PROJECT STATUS SECTION */}
    {((selectedUnit?.estimatedCompletionDate && 
       selectedUnit.estimatedCompletionDate !== 'null' && 
       selectedUnit.estimatedCompletionDate !== '') ||
      selectedUnit?.occupancyStatus === 'Offplan') && (
      <div style={{
        backgroundColor: '#ffffff',
        borderRadius: '1rem',
        padding: '1.5rem',
        border: '1px solid #f3f4f6',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
      }}>
        {/* Section Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          marginBottom: '1.5rem'
        }}>
          <h2 style={{
            fontSize: '1.25rem',
            fontWeight: '700',
            color: '#111827',
            margin: 0
          }}>{t('projectStatus')}</h2>
          <button style={{
            padding: '0.25rem',
            color: '#9ca3af',
            background: 'none',
            border: 'none',
            borderRadius: '50%',
            cursor: 'pointer'
          }}>
            <AlertCircle style={{ width: '1rem', height: '1rem' }} />
          </button>
        </div>
        
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
          {/* Status Row */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <span style={{
              color: '#6b7280',
              fontWeight: '500',
              fontSize: '0.9375rem'
            }}>{t('status')}</span>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.625rem'
            }}>
              {(() => {
                const isCompleted = selectedUnit?.estimatedCompletionDate && 
                  new Date(selectedUnit.estimatedCompletionDate) <= new Date();
                
                return isCompleted ? (
                  <>
                    <div style={{
                      padding: '0.375rem',
                      backgroundColor: '#d1fae5',
                      borderRadius: '50%'
                    }}>
                      <CheckCircle style={{ width: '1rem', height: '1rem', color: '#059669' }} />
                    </div>
                    <span style={{
                      fontWeight: '600',
                      color: '#111827',
                      fontSize: '0.9375rem'
                    }}>{t('completed')}</span>
                  </>
                ) : (
                  <>
                    <div style={{
                      padding: '0.375rem',
                      backgroundColor: '#dbeafe',
                      borderRadius: '50%'
                    }}>
                      <Clock style={{ width: '1rem', height: '1rem', color: '#2563eb' }} />
                    </div>
                    <span style={{
                      fontWeight: '600',
                      color: '#111827',
                      fontSize: '0.9375rem'
                    }}>{t('inProgress')}</span>
                  </>
                );
              })()}
            </div>
          </div>

          {/* Completion Date Row */}
          {selectedUnit?.estimatedCompletionDate && 
           selectedUnit.estimatedCompletionDate !== 'null' && 
           selectedUnit.estimatedCompletionDate !== '' && (
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <span style={{
                color: '#6b7280',
                fontWeight: '500',
                fontSize: '0.9375rem'
              }}>
                {new Date(selectedUnit.estimatedCompletionDate) <= new Date() 
                  ? 'Completion date' 
                  : 'Scheduled completion date'}
              </span>
              <span style={{
                fontWeight: '600',
                color: '#111827',
                fontSize: '0.9375rem'
              }}>
                {new Date(selectedUnit.estimatedCompletionDate).toLocaleDateString('en-GB', { 
                  day: 'numeric', 
                  month: 'short', 
                  year: 'numeric' 
                })}
              </span>
            </div>
          )}

        </div>
      </div>
    )}

    {/* PROPERTY SIZE SECTION */}
    <div style={{
      backgroundColor: '#ffffff',
      borderRadius: '1rem',
      padding: '1.5rem',
      border: '1px solid #f3f4f6',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
    }}>
      {/* Section Header */}
      <h2 style={{
        fontSize: '1.25rem',
        fontWeight: '700',
        color: '#111827',
        margin: '0 0 1.5rem 0'
      }}>{t('propertySize') || 'Property Size'}</h2>
      
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        {/* Area in SQFT */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          padding: '0.5rem 0'
        }}>
          <span style={{
            color: '#6b7280',
            fontWeight: '500',
            fontSize: '0.9375rem'
          }}>{t('area') || 'Area'}</span>
          <AreaDisplay sqft={selectedUnit?.area || 0} showSecondary={true} />
        </div>
      </div>
    </div>

    {/* PROJECT TIMELINE SECTION */}
    <ProjectTimeline unit={selectedUnit} />

    {/* PRICE & VALUATION SECTION */}
    <div style={{
      backgroundColor: '#ffffff',
      borderRadius: '1rem',
      padding: '1.5rem',
      border: '1px solid #f3f4f6',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
    }}>
      {/* Section Header */}
      <h2 style={{
        fontSize: '1.25rem',
        fontWeight: '700',
        color: '#111827',
        margin: '0 0 1.5rem 0'
      }}>{t('priceAndValuation')}</h2>
      
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        {/* Purchase Date */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '0.5rem 0'
        }}>
          <span style={{
            color: '#6b7280',
            fontWeight: '500',
            fontSize: '0.9375rem'
          }}>{t('purchaseDate')}</span>
          <span style={{
            fontWeight: '600',
            color: '#111827',
            fontSize: '0.9375rem'
          }}>
            {selectedUnit?.purchaseDate ? 
              new Date(selectedUnit.purchaseDate).toLocaleDateString('en-GB', { 
                day: 'numeric', 
                month: 'short', 
                year: 'numeric' 
              }) 
              : 'N/A'}
          </span>
        </div>

        {/* Purchase Price with EUR */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          padding: '0.5rem 0'
        }}>
          <span style={{
            color: '#6b7280',
            fontWeight: '500',
            fontSize: '0.9375rem'
          }}>{t('purchasePrice')}</span>
          <DualCurrencyDisplay aedValue={selectedUnit?.purchaseValue || 0} size="medium" />
        </div>

        {/* Price per SQFT with EUR/m² */}
        {selectedUnit?.purchaseValue && parseFloat(selectedUnit?.area || '0') > 0 && (
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            padding: '0.5rem 0',
            backgroundColor: '#f9fafb',
            margin: '0 -1.5rem',
            paddingLeft: '1.5rem',
            paddingRight: '1.5rem'
          }}>
            <span style={{
              color: '#6b7280',
              fontWeight: '500',
              fontSize: '0.9375rem'
            }}>{t('pricePerSqft') || 'Price/Area'}</span>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
              <span style={{
                fontWeight: '600',
                color: '#111827',
                fontSize: '0.9375rem'
              }}>
                AED {calculatePricePerSqft(selectedUnit.purchaseValue, parseFloat(selectedUnit.area)).toLocaleString('en-AE')}/sqft
              </span>
              <span style={{
                fontSize: '0.75rem',
                color: '#6b7280',
                fontWeight: 500,
                marginTop: '2px'
              }}>
                ≈ €{(calculatePricePerSqm(selectedUnit.purchaseValue, parseFloat(selectedUnit.area)) * AED_TO_EUR_RATE).toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}/m²
              </span>
            </div>
          </div>
        )}

        {/* Valuation with EUR */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          padding: '0.5rem 0 1rem 0',
          borderBottom: '2px solid #f3f4f6'
        }}>
          <span style={{
            color: '#6b7280',
            fontWeight: '500',
            fontSize: '0.9375rem'
          }}>{t('valuation')}</span>
          <DualCurrencyDisplay aedValue={dldUnitData?.dldMarketValue || getEffectiveUnitValue(selectedUnit)} size="medium" />
        </div>

        {/* DLD Market Valuation */}
        {dldUnitData?.dldMarketValue > 0 && (
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            padding: '0.75rem 0',
            margin: '0 -1.5rem',
            paddingLeft: '1.5rem',
            paddingRight: '1.5rem',
            background: 'linear-gradient(135deg, #f0fdf4, #dcfce7)',
            borderBottom: '2px solid #bbf7d0'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span style={{
                color: '#16a34a',
                fontWeight: '600',
                fontSize: '0.9375rem'
              }}>{t('pvDldMarketValue')}</span>
              <span style={{
                fontSize: '0.6rem',
                padding: '0.1rem 0.4rem',
                borderRadius: '1rem',
                backgroundColor: dldUnitData.valuationConfidence === 'high' ? '#dcfce7' : dldUnitData.valuationConfidence === 'medium' ? '#fef9c3' : '#f3f4f6',
                color: dldUnitData.valuationConfidence === 'high' ? '#16a34a' : dldUnitData.valuationConfidence === 'medium' ? '#ca8a04' : '#6b7280',
                fontWeight: '700',
                textTransform: 'capitalize',
                border: `1px solid ${dldUnitData.valuationConfidence === 'high' ? '#86efac' : dldUnitData.valuationConfidence === 'medium' ? '#fde047' : '#d1d5db'}`
              }}>
                {t(dldUnitData.valuationConfidence === 'high' ? 'vrConfidenceHigh'
                  : dldUnitData.valuationConfidence === 'medium' ? 'vrConfidenceMedium'
                  : 'vrConfidenceLow')}
              </span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
              <span style={{
                fontWeight: '700',
                color: '#14532d',
                fontSize: '1rem'
              }}>
                AED {dldUnitData.dldMarketValue.toLocaleString('en-AE')}
              </span>
              <span style={{
                fontSize: '0.75rem',
                color: '#6b7280',
                fontWeight: 500,
                marginTop: '2px'
              }}>
                ≈ €{(dldUnitData.dldMarketValue * AED_TO_EUR_RATE).toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
              </span>
            </div>
          </div>
        )}

        {/* DLD Price Per Sqft */}
        {dldUnitData?.dldPricePerSqft > 0 && (
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            padding: '0.5rem 0',
            margin: '0 -1.5rem',
            paddingLeft: '1.5rem',
            paddingRight: '1.5rem',
            backgroundColor: '#f0fdf4'
          }}>
            <span style={{
              color: '#16a34a',
              fontWeight: '500',
              fontSize: '0.9375rem'
            }}>{t('pvDldPricePerSqm')}</span>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
              <span style={{
                fontWeight: '600',
                color: '#14532d',
                fontSize: '0.9375rem'
              }}>
                AED {(dldUnitData.dldPricePerSqm || Math.round(dldUnitData.dldPricePerSqft * SQM_TO_SQFT_RATE)).toLocaleString('en-AE')}/m²
              </span>
              <span style={{
                fontSize: '0.75rem',
                color: '#6b7280',
                fontWeight: 500,
                marginTop: '2px'
              }}>
                ≈ €{Math.round((dldUnitData.dldPricePerSqm || dldUnitData.dldPricePerSqft * SQM_TO_SQFT_RATE) * AED_TO_EUR_RATE).toLocaleString('de-DE')}/m²
              </span>
            </div>
          </div>
        )}

        {/* DLD Avg Rental */}
        {dldUnitData?.dldAvgMonthlyRent > 0 && (
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            padding: '0.75rem 0',
            margin: '0 -1.5rem',
            paddingLeft: '1.5rem',
            paddingRight: '1.5rem',
            background: 'linear-gradient(135deg, #faf5ff, #f3e8ff)',
            borderBottom: '1px solid #e9d5ff'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span style={{
                color: '#7c3aed',
                fontWeight: '600',
                fontSize: '0.9375rem'
              }}>{t('pvDldAvgRent')}</span>
              <span style={{
                fontSize: '0.6rem',
                padding: '0.1rem 0.4rem',
                borderRadius: '1rem',
                backgroundColor: '#ede9fe',
                color: '#7c3aed',
                fontWeight: '700',
                border: '1px solid #c4b5fd'
              }}>
                {dldUnitData.rentalComparablesCount} {t('pvContracts')}
              </span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
              <span style={{
                fontWeight: '700',
                color: '#4c1d95',
                fontSize: '1rem'
              }}>
                AED {dldUnitData.dldAvgMonthlyRent.toLocaleString('en-AE')}/mo
              </span>
              <span style={{
                fontSize: '0.75rem',
                color: '#7c3aed',
                fontWeight: 500,
                marginTop: '2px'
              }}>
                AED {dldUnitData.dldAvgAnnualRent.toLocaleString('en-AE')}/yr
                {dldUnitData.dldRentalYield > 0 && ` • ${dldUnitData.dldRentalYield.toFixed(1)}% ${t('pvYieldSuffix')}`}
              </span>
            </div>
          </div>
        )}

        {/* DLD Loading indicator */}
        {dldLoading && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            padding: '0.5rem 0',
            color: '#6b7280',
            fontSize: '0.8rem'
          }}>
            <Loader2 size={14} className="animate-spin" />
            {t('pvLoadingDldData')}
          </div>
        )}

        {/* Gains (AED) with EUR */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          padding: '1rem 0 0.5rem 0'
        }}>
          <span style={{
            color: '#6b7280',
            fontWeight: '500',
            fontSize: '0.9375rem'
          }}>{t('gains')}</span>
          <DualCurrencyDisplay
            aedValue={(dldUnitData?.dldMarketValue || getEffectiveUnitValue(selectedUnit)) - (selectedUnit?.purchaseValue || 0)}
            size="medium"
          />
        </div>

        {/* Gains (%) */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '0.5rem 0'
        }}>
          <span style={{
            color: '#6b7280',
            fontWeight: '500',
            fontSize: '0.9375rem'
          }}>{t('gains')}</span>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            {(() => {
              const hasDld = (dldUnitData?.dldMarketValue || 0) > 0;
              // Recommendation: hide the +/- indicator when DLD value exists but
              // there aren't enough recent comparable sales for a reliable ROI.
              if (hasDld && !dldUnitData?.roiReliable) {
                return (
                  <span style={{ fontSize: '0.8125rem', fontWeight: 600, color: '#9ca3af' }}>
                    {t('vrConfidenceLow')}
                  </span>
                );
              }
              const marketVal = dldUnitData?.dldMarketValue || getEffectiveUnitValue(selectedUnit);
              const purchaseVal = selectedUnit?.purchaseValue || 0;
              const gainPct = purchaseVal > 0 ? ((marketVal - purchaseVal) / purchaseVal) * 100 : 0;
              return (
                <>
                  <span style={{
                    fontWeight: '700',
                    fontSize: '1.125rem',
                    color: gainPct >= 0 ? '#10b981' : '#ef4444'
                  }}>
                    {gainPct.toFixed(1)}%
                  </span>
                  <TrendingUp style={{
                    width: '1.25rem', height: '1.25rem',
                    color: gainPct >= 0 ? '#10b981' : '#ef4444',
                    transform: gainPct < 0 ? 'rotate(180deg)' : 'none'
                  }} />
                  {hasDld && (
                    <span style={{
                      fontSize: '0.6rem', padding: '0.1rem 0.35rem', borderRadius: '0.25rem',
                      backgroundColor: '#f0fdf4', color: '#16a34a', fontWeight: '600',
                      border: '1px solid #bbf7d0'
                    }}>DLD</span>
                  )}
                </>
              );
            })()}
          </div>
        </div>
      </div>

      {/* Exchange Rate Note */}
      <div style={{
        marginTop: '1rem',
        padding: '0.75rem',
        backgroundColor: '#f9fafb',
        borderRadius: '0.5rem',
        borderLeft: '3px solid #D6B160'
      }}>
        <p style={{
          fontSize: '0.75rem',
          color: '#6b7280',
          margin: 0
        }}>
          💱 {t('exchangeRateNote') || `EUR values are approximate (1 AED ≈ ${AED_TO_EUR_RATE} EUR). Actual rates may vary.`}
        </p>
      </div>

      {/* DLD Source Badge */}
      {dldUnitData?.dldMarketValue > 0 && (
        <div style={{
          marginTop: '0.5rem',
          padding: '0.5rem 0.75rem',
          backgroundColor: '#f0fdf4',
          borderRadius: '0.5rem',
          borderLeft: '3px solid #22c55e',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem'
        }}>
          <div style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#22c55e' }} />
          <p style={{
            fontSize: '0.7rem',
            color: '#16a34a',
            margin: 0,
            fontWeight: '500'
          }}>
            {t('pvMarketDataFromDld')} • {dldUnitData.comparablesCount} {t('pvComparableTransactions')}
          </p>
        </div>
      )}
    </div>

  </div>
)}

            {activeTab === 'income' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {/* DLD Rental Intelligence */}
                {(dldUnitData?.dldAvgMonthlyRent > 0 || (selectedUnit?.monthlyRental || 0) > 0) ? (
                  <>
                    {/* Rental Summary Card */}
                    <div style={{
                      backgroundColor: '#fff',
                      borderRadius: '16px',
                      padding: '24px',
                      border: '1px solid #e5e7eb',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
                        <div style={{ width: 36, height: 36, borderRadius: '10px', background: 'linear-gradient(135deg, #7c3aed, #a855f7)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <DollarSign size={18} color="#fff" />
                        </div>
                        <div>
                          <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '700', color: '#111827' }}>{t('rentalIncome')}</h3>
                          <p style={{ margin: 0, fontSize: '0.75rem', color: '#6b7280' }}>
                            {dldUnitData?.dldAvgMonthlyRent > 0 ? t('pvBasedOnEjari') : t('pvCurrentRentalData')}
                          </p>
                        </div>
                        {dldUnitData?.dldAvgMonthlyRent > 0 && (
                          <span style={{ marginLeft: 'auto', fontSize: '0.65rem', padding: '3px 10px', borderRadius: '10px', backgroundColor: '#f0fdf4', color: '#16a34a', fontWeight: '600', border: '1px solid #bbf7d0' }}>
                            {t('pvDldData')}
                          </span>
                        )}
                      </div>

                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
                        <div style={{ background: 'linear-gradient(135deg, #faf5ff, #f3e8ff)', borderRadius: '12px', padding: '16px', borderLeft: '4px solid #7c3aed' }}>
                          <p style={{ margin: 0, fontSize: '0.75rem', color: '#7c3aed', fontWeight: '600' }}>{t('monthlyRental')}</p>
                          <p style={{ margin: '6px 0 0', fontSize: '1.5rem', fontWeight: '800', color: '#4c1d95' }}>
                            AED {(dldUnitData?.dldAvgMonthlyRent || selectedUnit?.monthlyRental || 0).toLocaleString('en-AE')}
                          </p>
                          <p style={{ margin: '2px 0 0', fontSize: '0.7rem', color: '#7c3aed' }}>
                            ≈ €{((dldUnitData?.dldAvgMonthlyRent || selectedUnit?.monthlyRental || 0) * AED_TO_EUR_RATE).toLocaleString('de-DE', { maximumFractionDigits: 0 })}
                          </p>
                        </div>
                        <div style={{ background: 'linear-gradient(135deg, #eff6ff, #dbeafe)', borderRadius: '12px', padding: '16px', borderLeft: '4px solid #2563eb' }}>
                          <p style={{ margin: 0, fontSize: '0.75rem', color: '#2563eb', fontWeight: '600' }}>{t('annualIncome')}</p>
                          <p style={{ margin: '6px 0 0', fontSize: '1.5rem', fontWeight: '800', color: '#1e3a8a' }}>
                            AED {((dldUnitData?.dldAvgAnnualRent || (selectedUnit?.monthlyRental || 0) * 12)).toLocaleString('en-AE')}
                          </p>
                          <p style={{ margin: '2px 0 0', fontSize: '0.7rem', color: '#2563eb' }}>
                            ≈ €{((dldUnitData?.dldAvgAnnualRent || (selectedUnit?.monthlyRental || 0) * 12) * AED_TO_EUR_RATE).toLocaleString('de-DE', { maximumFractionDigits: 0 })}
                          </p>
                        </div>
                      </div>

                      {/* Rental Yield & ROI */}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
                        <div style={{ backgroundColor: '#f9fafb', borderRadius: '12px', padding: '14px', textAlign: 'center' }}>
                          <p style={{ margin: 0, fontSize: '0.7rem', color: '#6b7280', fontWeight: '600' }}>{t('pvGrossYield')}</p>
                          <p style={{ margin: '4px 0 0', fontSize: '1.2rem', fontWeight: '700', color: '#059669' }}>
                            {(() => {
                              const rent = dldUnitData?.dldAvgAnnualRent || (selectedUnit?.monthlyRental || 0) * 12;
                              const val = dldUnitData?.dldMarketValue || getEffectiveUnitValue(selectedUnit);
                              return val > 0 ? (rent / val * 100).toFixed(1) : '0.0';
                            })()}%
                          </p>
                        </div>
                        <div style={{ backgroundColor: '#f9fafb', borderRadius: '12px', padding: '14px', textAlign: 'center' }}>
                          <p style={{ margin: 0, fontSize: '0.7rem', color: '#6b7280', fontWeight: '600' }}>{t('pvNetYieldEst')}</p>
                          <p style={{ margin: '4px 0 0', fontSize: '1.2rem', fontWeight: '700', color: '#2563eb' }}>
                            {(() => {
                              const rent = dldUnitData?.dldAvgAnnualRent || (selectedUnit?.monthlyRental || 0) * 12;
                              const val = dldUnitData?.dldMarketValue || getEffectiveUnitValue(selectedUnit);
                              const netRent = rent * 0.85; // ~15% expenses estimate
                              return val > 0 ? (netRent / val * 100).toFixed(1) : '0.0';
                            })()}%
                          </p>
                          <p style={{ margin: '2px 0 0', fontSize: '0.6rem', color: '#9ca3af' }}>{t('pvAfterExpenses')}</p>
                        </div>
                        <div style={{ backgroundColor: '#f9fafb', borderRadius: '12px', padding: '14px', textAlign: 'center' }}>
                          <p style={{ margin: 0, fontSize: '0.7rem', color: '#6b7280', fontWeight: '600' }}>{t('pvPriceToRent')}</p>
                          <p style={{ margin: '4px 0 0', fontSize: '1.2rem', fontWeight: '700', color: '#111827' }}>
                            {(() => {
                              const rent = dldUnitData?.dldAvgAnnualRent || (selectedUnit?.monthlyRental || 0) * 12;
                              const val = dldUnitData?.dldMarketValue || getEffectiveUnitValue(selectedUnit);
                              return rent > 0 ? (val / rent).toFixed(1) : '—';
                            })()}x
                          </p>
                          <p style={{ margin: '2px 0 0', fontSize: '0.6rem', color: '#9ca3af' }}>{t('pvYearsToRecoup')}</p>
                        </div>
                      </div>
                    </div>

                    {/* DLD Comparable Rentals */}
                    {dldUnitData?.recentRentals?.length > 0 && (
                      <div style={{
                        backgroundColor: '#fff', borderRadius: '16px', padding: '24px',
                        border: '1px solid #e5e7eb', boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
                      }}>
                        <h4 style={{ margin: '0 0 16px', fontSize: '0.95rem', fontWeight: '700', color: '#111827', display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <Building2 size={16} color="#7c3aed" />
                          {t('pvDldComparableRentals')}
                        </h4>
                        <div style={{ overflowX: 'auto' }}>
                          <table style={{ width: '100%', fontSize: '0.8rem', borderCollapse: 'collapse' }}>
                            <thead>
                              <tr style={{ background: '#faf5ff' }}>
                                <th style={{ padding: '8px', textAlign: 'left', fontWeight: '600', color: '#7c3aed' }}>{t('pvColStartDate')}</th>
                                <th style={{ padding: '8px', textAlign: 'right', fontWeight: '600', color: '#7c3aed' }}>{t('vrColRent')}</th>
                                <th style={{ padding: '8px', textAlign: 'right', fontWeight: '600', color: '#7c3aed' }}>{t('monthly')}</th>
                                <th style={{ padding: '8px', textAlign: 'right', fontWeight: '600', color: '#7c3aed' }}>{t('pvColSize')}</th>
                                <th style={{ padding: '8px', textAlign: 'left', fontWeight: '600', color: '#7c3aed' }}>{t('vrType')}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {dldUnitData.recentRentals.map((rent: any, i: number) => (
                                <tr key={i} style={{ borderBottom: '1px solid #f3f4f6' }}>
                                  <td style={{ padding: '8px', color: '#374151' }}>
                                    {rent.startDate ? new Date(rent.startDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: '2-digit' }) : '—'}
                                  </td>
                                  <td style={{ padding: '8px', textAlign: 'right', fontWeight: '600', color: '#4c1d95' }}>
                                    AED {rent.annualRent?.toLocaleString('en-AE') || '—'}
                                  </td>
                                  <td style={{ padding: '8px', textAlign: 'right', color: '#374151' }}>
                                    AED {rent.monthlyRent?.toLocaleString('en-AE') || '—'}
                                  </td>
                                  <td style={{ padding: '8px', textAlign: 'right', color: '#374151' }}>
                                    {rent.sizeSqft ? `${rent.sizeSqft.toLocaleString('en-AE')} sqft` : '—'}
                                  </td>
                                  <td style={{ padding: '8px', color: '#6b7280' }}>
                                    {rent.rooms || '—'} {rent.isRenewal ? t('pvRenewalTag') : t('pvNewTag')}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  /* No rental data state */
                  <div style={{
                    backgroundColor: '#fff', borderRadius: '16px', padding: '48px 24px',
                    border: '1px solid #e5e7eb', textAlign: 'center'
                  }}>
                    <Building2 size={48} color="#d1d5db" style={{ margin: '0 auto 16px' }} />
                    <h3 style={{ margin: '0 0 8px', fontSize: '1.1rem', fontWeight: '700', color: '#111827' }}>
                      {t('pvRentalDataUnavailable')}
                    </h3>
                    <p style={{ margin: 0, fontSize: '0.85rem', color: '#6b7280', maxWidth: '400px', marginLeft: 'auto', marginRight: 'auto' }}>
                      {t('pvRentalDataUnavailableDesc')}
                    </p>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'documents' && (
              <div className="space-y-4">
                {missingKeyDocs.length > 0 && (
                  <div style={{
                    padding: '20px',
                    background: '#FFFFFF',
                    borderRadius: '16px',
                    border: '2px solid #D6B160',
                  }}>
                    <p style={{ color: '#000000', fontSize: '16px', fontWeight: 700, margin: '0 0 6px 0' }}>
                      {t('pvMissingDocsTitle')}
                    </p>
                    <p style={{ color: '#4b5563', fontSize: '14px', margin: '0 0 12px 0', lineHeight: 1.45 }}>
                      {t('pvMissingDocsDesc')}
                    </p>
                    <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 16px 0', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      {missingKeyDocs.map(key => (
                        <li key={key} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '15px', fontWeight: 600, color: '#000000' }}>
                          <FileText size={16} color="#D6B160" />
                          {t(key)}
                        </li>
                      ))}
                    </ul>
                    <button
                      onClick={() => {
                        setSelectedUnitForDoc(selectedProperty || '');
                        setDocumentCategory('Unit Documents');
                        setShowAddDocumentsModal(true);
                      }}
                      style={{
                        padding: '10px 24px',
                        background: '#D6B160',
                        color: 'white',
                        border: 'none',
                        borderRadius: '10px',
                        fontSize: '14px',
                        fontWeight: '600',
                        cursor: 'pointer'
                      }}
                    >
                      <FileText size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: '4px' }} />{t('pvMissingDocsBtn')}
                    </button>
                  </div>
                )}
                {/* Nothing uploaded means all three are missing, so the banner
                    above is the empty state. */}
                {propertyDocuments.length === 0 ? null : (
                  propertyDocuments.map(doc => {
                    const isSnagging = (doc.type || doc.documentType || '').toLowerCase().includes('snag') || (doc.type || doc.documentType || '').toLowerCase().includes('inspection');
                    const snaggingData = isSnagging ? (doc.extractedData || {}) : null;
                    const defects = snaggingData?.defects || [];
                    const severity = snaggingData?.defectsBySeverity || {};
                    const totalDefects = snaggingData?.totalDefects || defects.length || 0;

                    return (
                    <div key={doc.id} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                      <div className="p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex items-start gap-3 flex-1">
                            <FileText className="h-5 w-5 text-[#D6B160] mt-0.5" />
                            <div className="flex-1">
                              <h4 className="font-semibold text-gray-900">{doc.name}</h4>
                              <p className="text-sm text-gray-600">{doc.type}</p>
                              <p className="text-xs text-gray-500 mt-1">{doc.uploadDate}</p>
                              {doc.verificationStatus && (
                                <span style={{
                                  display: 'inline-block',
                                  marginTop: '4px',
                                  padding: '2px 8px',
                                  borderRadius: '12px',
                                  fontSize: '11px',
                                  fontWeight: 600,
                                  backgroundColor: doc.verificationStatus === 'verified' ? '#d1fae5'
                                    : doc.verificationStatus === 'rejected' ? '#fee2e2'
                                    : '#fef3c7',
                                  color: doc.verificationStatus === 'verified' ? '#065f46'
                                    : doc.verificationStatus === 'rejected' ? '#991b1b'
                                    : '#92400e'
                                }}>
                                  {doc.verificationStatus === 'verified' ? <><CheckCircle size={12} style={{ display: 'inline', verticalAlign: 'middle', marginRight: '3px' }} />{t('verified')}</>
                                    : doc.verificationStatus === 'rejected' ? <><XCircle size={12} style={{ display: 'inline', verticalAlign: 'middle', marginRight: '3px' }} />{t('pvRejected')}</>
                                    : <><Clock size={12} style={{ display: 'inline', verticalAlign: 'middle', marginRight: '3px' }} />{t('verificationPending')}</>}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleViewFile(doc)}
                              className="p-2 hover:bg-gray-100 rounded-lg"
                            >
                              <Eye className="h-4 w-4 text-gray-600" />
                            </button>
                            <button
                              onClick={() => handleDownloadFile(doc)}
                              className="p-2 hover:bg-gray-100 rounded-lg"
                            >
                              <Download className="h-4 w-4 text-gray-600" />
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Snagging Report Summary */}
                      {isSnagging && (totalDefects > 0 || snaggingData?.summary) && (
                        <div style={{ borderTop: '1px solid #e5e7eb', padding: '12px 16px', background: '#fef2f2' }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                            <span style={{ fontWeight: 700, fontSize: '13px', color: '#991b1b' }}>
                              {t('pvSnaggingReport')}{totalDefects > 0 ? ` — ${totalDefects} ${totalDefects !== 1 ? t('pvDefects') : t('pvDefect')} ${t('pvFound')}` : ''}
                            </span>
                            {snaggingData?.overallCondition && (
                              <span style={{
                                fontSize: '11px', padding: '2px 8px', borderRadius: '12px', fontWeight: 600,
                                backgroundColor: snaggingData.overallCondition === 'Good' ? '#d1fae5' : snaggingData.overallCondition === 'Fair' ? '#fef3c7' : '#fee2e2',
                                color: snaggingData.overallCondition === 'Good' ? '#065f46' : snaggingData.overallCondition === 'Fair' ? '#92400e' : '#991b1b'
                              }}>
                                {snaggingData.overallCondition}
                              </span>
                            )}
                          </div>

                          {/* Severity Grid */}
                          {totalDefects > 0 && <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '6px', marginBottom: '8px' }}>
                            <div style={{ textAlign: 'center', padding: '6px', borderRadius: '8px', background: '#fff', border: '1px solid #fca5a5' }}>
                              <div style={{ fontSize: '18px', fontWeight: 700, color: '#ef4444' }}>{severity.critical || 0}</div>
                              <div style={{ fontSize: '10px', color: '#991b1b', fontWeight: 600 }}>{t('pvSevCritical')}</div>
                            </div>
                            <div style={{ textAlign: 'center', padding: '6px', borderRadius: '8px', background: '#fff', border: '1px solid #fed7aa' }}>
                              <div style={{ fontSize: '18px', fontWeight: 700, color: '#f97316' }}>{severity.major || 0}</div>
                              <div style={{ fontSize: '10px', color: '#9a3412', fontWeight: 600 }}>{t('pvSevMajor')}</div>
                            </div>
                            <div style={{ textAlign: 'center', padding: '6px', borderRadius: '8px', background: '#fff', border: '1px solid #fde68a' }}>
                              <div style={{ fontSize: '18px', fontWeight: 700, color: '#eab308' }}>{severity.minor || 0}</div>
                              <div style={{ fontSize: '10px', color: '#854d0e', fontWeight: 600 }}>{t('pvSevMinor')}</div>
                            </div>
                            <div style={{ textAlign: 'center', padding: '6px', borderRadius: '8px', background: '#fff', border: '1px solid #e5e7eb' }}>
                              <div style={{ fontSize: '18px', fontWeight: 700, color: '#9ca3af' }}>{severity.cosmetic || 0}</div>
                              <div style={{ fontSize: '10px', color: '#6b7280', fontWeight: 600 }}>{t('pvSevCosmetic')}</div>
                            </div>
                          </div>}

                          {/* Key Issues */}
                          {snaggingData?.keyIssues && snaggingData.keyIssues.length > 0 && (
                            <div style={{ padding: '8px 10px', background: '#fffbeb', borderRadius: '8px', border: '1px solid #fbbf24', marginBottom: '8px' }}>
                              <div style={{ fontSize: '11px', fontWeight: 700, color: '#92400e', marginBottom: '4px' }}>{t('pvKeyIssues')}</div>
                              <ul style={{ margin: 0, paddingLeft: '16px', fontSize: '12px', color: '#78350f' }}>
                                {snaggingData.keyIssues.slice(0, 5).map((issue: string, i: number) => (
                                  <li key={i} style={{ marginBottom: '2px' }}>{issue}</li>
                                ))}
                              </ul>
                            </div>
                          )}

                          {/* Inspector Info */}
                          <div style={{ fontSize: '11px', color: '#6b7280' }}>
                            {snaggingData?.inspectorName && <span>{t('pvInspector')}: <strong>{snaggingData.inspectorName}</strong></span>}
                            {snaggingData?.inspectionDate && <span> — {snaggingData.inspectionDate}</span>}
                          </div>

                          {/* Summary */}
                          {snaggingData?.summary && (
                            <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '6px', lineHeight: 1.4 }}>
                              {snaggingData.summary}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                    );
                  })
                )}
              </div>
            )}
          </div>
          </>
          )}
        </div>
      )}

            {/* Add Property Modal */}
      {showAddPropertyModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md max-h-[85vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="sticky top-0 bg-white border-b border-gray-200 px-5 py-4 rounded-t-2xl flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-gray-900">{t('addProperty')}</h3>
                <p className="text-sm text-gray-600 mt-0.5">{t('createNewProperty')}</p>
              </div>
              <button
                onClick={() => {
                  setShowAddPropertyModal(false);
                  setNewUnitForm({ 
                    name: '', 
                    unitNumber: '', 
                    project: '', 
                    type: 'Apartment', 
                    area: '',
                    bedrooms: '1',
                    occupancyStatus: 'Vacant',
                    purchaseDate: new Date().toISOString().split('T')[0],
                    completionDate: ''
                  });
                  setUnitError('');
                  setUnitSuccess(false);
                }}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <X className="h-5 w-5 text-gray-600" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-5 space-y-4">
              {/* Info Banner */}
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-xl">
                <p className="text-sm text-blue-800">
                  <strong>{t('pvCreateFolderTitle')}</strong><br/>
                  {t('pvCreateFolderDesc')}
                </p>
              </div>

              {/* Property Form */}
              <div className="space-y-3">
                <input
                  type="text"
                  placeholder={`${t('propertyName')} *`}
                  value={newUnitForm.name}
                  onChange={(e) => setNewUnitForm({...newUnitForm, name: e.target.value})}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-[#D6B160] focus:border-transparent"
                />
                
                <div className="grid grid-cols-2 gap-3">
                  <input
                    type="text"
                    placeholder={`${t('unitNumber')} *`}
                    value={newUnitForm.unitNumber}
                    onChange={(e) => setNewUnitForm({...newUnitForm, unitNumber: e.target.value})}
                    className="px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-[#D6B160] focus:border-transparent"
                  />
                  <input
                    type="text"
                    placeholder={`${t('project')} *`}
                    value={newUnitForm.project}
                    onChange={(e) => setNewUnitForm({...newUnitForm, project: e.target.value})}
                    className="px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-[#D6B160] focus:border-transparent"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <select
                    value={newUnitForm.type}
                    onChange={(e) => setNewUnitForm({...newUnitForm, type: e.target.value})}
                    className="px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-[#D6B160] focus:border-transparent"
                  >
                    <option value="Apartment">{t('apartment')}</option>
                    <option value="Villa">{t('villa')}</option>
                    <option value="Townhouse">{t('townhouse')}</option>
                    <option value="Penthouse">{t('penthouse')}</option>
                    <option value="Studio">{t('studio')}</option>
                  </select>
                  <select
                    value={newUnitForm.occupancyStatus}
                    onChange={(e) => setNewUnitForm({...newUnitForm, occupancyStatus: e.target.value})}
                    className="px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-[#D6B160] focus:border-transparent"
                  >
                    <option value="Vacant">{t('vacant')}</option>
                    <option value="Occupied">{t('occupied')}</option>
                    <option value="Offplan">{t('offplan')}</option>
                    <option value="Ready">{t('pvReady')}</option>
                    <option value="To Be Rented Out">{t('toBeRentedOut')}</option>
                    <option value="Undergoing Upgrade">{t('undergoingUpgrade')}</option>
                  </select>
                </div>
                
                {/* Bedrooms selector - only show if not Studio */}
                {newUnitForm.type !== 'Studio' && (
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">{t('bedrooms')}</label>
                    <select
                      value={newUnitForm.bedrooms}
                      onChange={(e) => setNewUnitForm({...newUnitForm, bedrooms: e.target.value})}
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-[#D6B160] focus:border-transparent"
                    >
                      <option value="1">1 {t('bedroom')}</option>
                      <option value="2">2 {t('bedrooms')}</option>
                      <option value="3">3 {t('bedrooms')}</option>
                      <option value="4">4 {t('bedrooms')}</option>
                      <option value="5">5 {t('bedrooms')}</option>
                      <option value="6">6 {t('bedrooms')}</option>
                      <option value="7">7 {t('bedrooms')}</option>
                      <option value="8">8 {t('bedrooms')}</option>
                      <option value="9">9 {t('bedrooms')}</option>
                      <option value="10+">10+ {t('bedrooms')}</option>
                    </select>
                  </div>
                )}

                <input
                  type="text"
                  placeholder={`${t('area')} (sqft) *`}
                  value={newUnitForm.area}
                  onChange={(e) => setNewUnitForm({...newUnitForm, area: e.target.value})}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-[#D6B160] focus:border-transparent"
                />
              </div>

              {unitError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-xl">
                  <p className="text-sm text-red-600">{unitError}</p>
                </div>
              )}
              
              {unitSuccess && (
                <div className="p-3 bg-green-50 border border-green-200 rounded-xl">
                  <p className="text-sm text-green-600">
                    {t('pvPropertyCreated')}
                  </p>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-2 pt-2">
                <button
                  onClick={() => {
                    setShowAddPropertyModal(false);
                    setNewUnitForm({ 
                      name: '', 
                      unitNumber: '', 
                      project: '', 
                      type: 'Apartment', 
                      area: '',
                      bedrooms: '1',
                      occupancyStatus: 'Vacant',
                      purchaseDate: new Date().toISOString().split('T')[0],
                      completionDate: ''
                    });
                    setUnitError('');
                    setUnitSuccess(false);
                  }}
                  className="flex-1 px-4 py-3 border border-gray-300 rounded-xl hover:bg-gray-50 text-sm font-medium"
                >
                  {t('cancel')}
                </button>
                <button
                  onClick={createNewUnit}
                  disabled={isSubmitting || !newUnitForm.name || !newUnitForm.unitNumber || !newUnitForm.project || !newUnitForm.area}
                  className="flex-1 px-4 py-3 bg-[#D6B160] text-white rounded-xl hover:bg-[#c5a050] disabled:opacity-50 text-sm font-medium flex items-center justify-center gap-2"
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      {t('creating')}
                    </>
                  ) : (
                    t('createProperty')
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

            {/* Add Documents Modal */}
{showAddDocumentsModal && (
  <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black bg-opacity-50"
    onClick={(e) => { if (e.target === e.currentTarget) setShowAddDocumentsModal(false); }}
  >
    <div className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl max-h-[90vh] overflow-y-auto">
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #D6B160, #c5a050)',
        padding: '20px 24px',
        borderRadius: '16px 16px 0 0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <div>
          <h3 style={{ color: '#fff', fontSize: '18px', fontWeight: '700', margin: 0 }}>
            {t('addDocuments') || 'Add Documents'}
          </h3>
          <p style={{ color: 'rgba(255,255,255,0.8)', fontSize: '13px', margin: '4px 0 0 0' }}>
            {t('uploadToProperty') || 'Upload documents to your property'}
          </p>
        </div>
        <button 
          onClick={() => setShowAddDocumentsModal(false)}
          style={{ 
            background: 'rgba(255,255,255,0.2)', 
            border: 'none', 
            borderRadius: '10px', 
            padding: '8px',
            cursor: 'pointer'
          }}
        >
          <X className="h-5 w-5 text-white" />
        </button>
      </div>

      <div className="p-5 space-y-5">

        {/* SMART UPLOAD - One Button for Easy Upload */}
        {handleSmartUpload && (
          <div style={{
            background: 'linear-gradient(135deg, #92400e, #b8942e)',
            borderRadius: '16px',
            padding: '20px',
            position: 'relative',
            overflow: 'hidden'
          }}>
            <div style={{
              position: 'absolute', top: '-20px', right: '-20px',
              width: '80px', height: '80px', borderRadius: '50%',
              background: 'rgba(255,255,255,0.1)'
            }} />
            <div style={{ position: 'relative', zIndex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                <span style={{ display: 'flex', alignItems: 'center' }}><Upload size={28} color="#D6B160" /></span>
                <div>
                  <h4 style={{ color: '#fff', fontSize: '16px', fontWeight: '700', margin: 0 }}>
                    {t('smartUpload') || 'Smart Upload'}
                  </h4>
                  <p style={{ color: 'rgba(255,255,255,0.8)', fontSize: '12px', margin: '2px 0 0 0' }}>
                    {t('smartUploadDesc') || 'Upload all your files at once - AI will sort everything automatically'}
                  </p>
                </div>
              </div>
              <label
                htmlFor="smart-upload-input"
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '100%',
                  padding: '20px',
                  borderRadius: '12px',
                  border: '2px dashed rgba(255,255,255,0.4)',
                  background: smartUploading ? 'rgba(255,255,255,0.15)' : 'rgba(255,255,255,0.2)',
                  cursor: smartUploading ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s',
                  marginTop: '12px'
                }}
              >
                {smartUploading ? (
                  <>
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mb-2" />
                    <span style={{ fontSize: '14px', fontWeight: '600', color: '#fff' }}>
                      {smartUploadProgress?.fileName || (appLanguage() === 'de' ? 'Wird verarbeitet…' : 'Processing...')}
                    </span>
                    {smartUploadProgress && smartUploadProgress.total > 0 && (
                      <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.8)', marginTop: '4px' }}>
                        {smartUploadProgress.current} / {smartUploadProgress.total} {appLanguage() === 'de' ? 'Dateien' : 'files'}
                      </span>
                    )}
                  </>
                ) : (
                  <>
                    <Upload className="h-10 w-10 mb-2" style={{ color: '#fff' }} />
                    <span style={{ fontSize: '16px', fontWeight: '700', color: '#fff' }}>
                      {t('tapToUploadAll') || 'Tap here to upload all documents'}
                    </span>
                    <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.8)', marginTop: '4px' }}>
                      {t('smartUploadFormats') || 'Select multiple files - PDF, JPG, PNG (Max 100MB each)'}
                    </span>
                  </>
                )}
              </label>
              <input
                id="smart-upload-input"
                type="file"
                onChange={e => handleSmartUpload!(e, selectedUnit?.id)}
                accept=".pdf,.jpg,.jpeg,.png"
                multiple
                className="hidden"
                disabled={smartUploading}
              />
              <div style={{
                display: 'flex', alignItems: 'center', gap: '6px',
                marginTop: '12px', padding: '8px 12px',
                background: 'rgba(255,255,255,0.1)', borderRadius: '8px'
              }}>
                <span style={{ fontSize: '14px' }}>🔒</span>
                <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.8)' }}>
                  {t('smartUploadVerify') || 'All documents are verified by our team before appearing in your account'}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Divider between Smart Upload and Manual Upload */}
        {handleSmartUpload && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ flex: 1, height: '1px', background: '#e5e7eb' }} />
            <span style={{ fontSize: '12px', color: '#9ca3af', fontWeight: '600' }}>
              {t('orUploadManually') || 'OR UPLOAD MANUALLY'}
            </span>
            <div style={{ flex: 1, height: '1px', background: '#e5e7eb' }} />
          </div>
        )}

        {/* Step 1: Category */}
        <div>
          <label className="block text-sm font-semibold text-gray-900 mb-2">
            <span style={{ 
              display: 'inline-flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              width: '22px', 
              height: '22px', 
              borderRadius: '50%', 
              background: '#D6B160', 
              color: '#fff', 
              fontSize: '12px', 
              fontWeight: '700',
              marginRight: '8px'
            }}>1</span>
            {t('category') || 'Category'}
          </label>
         <div style={{ display: 'flex', gap: '8px' }}>
            {[
              { value: 'Unit Documents', label: t('unitDocs') || 'Unit', icon: <Home size={24} color="#D6B160" /> },
              { value: 'Personal Documents', label: t('personalDocs') || 'Personal', icon: <FileText size={24} color="#D6B160" /> },
              { value: 'Company Documents', label: t('companyDocs') || 'Company', icon: <Building2 size={24} color="#D6B160" /> },
            ].map(cat => (
              <button
                key={cat.value}
                onClick={() => setDocumentCategory(cat.value)}
                style={{
                  padding: '12px 8px',
                  borderRadius: '12px',
                  border: documentCategory === cat.value ? '2px solid #D6B160' : '2px solid #e5e7eb',
                  background: documentCategory === cat.value ? '#FFF8E1' : '#fff',
                  cursor: 'pointer',
                  textAlign: 'center',
                  transition: 'all 0.2s'
                }}
              >
                <div style={{ fontSize: '24px', marginBottom: '4px' }}>{cat.icon}</div>
                <div style={{ fontSize: '12px', fontWeight: '600', color: documentCategory === cat.value ? '#92400e' : '#6b7280' }}>
                  {cat.label}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Step 2: Select Unit (only for Unit Documents) */}
        {documentCategory === 'Unit Documents' && (
          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-2">
              <span style={{ 
                display: 'inline-flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                width: '22px', 
                height: '22px', 
                borderRadius: '50%', 
                background: '#D6B160', 
                color: '#fff', 
                fontSize: '12px', 
                fontWeight: '700',
                marginRight: '8px'
              }}>2</span>
              {t('selectUnit') || 'Select Unit'}
            </label>
            
            {/* Unit Search */}
          {/* Search + Dropdown */}
            <div className="relative">
              <div className="relative mb-2">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder={t('searchUnits') || 'Search units...'}
                  value={unitSearchTerm}
                  onChange={(e) => setUnitSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-[#D6B160] focus:border-transparent"
                />
              </div>
              <select
                value={selectedUnitForDoc}
                onChange={(e) => setSelectedUnitForDoc(e.target.value)}
                className="w-full p-3 border border-gray-200 rounded-xl text-sm bg-white focus:ring-2 focus:ring-[#D6B160] focus:border-transparent"
              >
                <option value="">{t('selectUnit') || '-- Select a unit --'}</option>
                {units
                  .filter(u => {
                    if (!unitSearchTerm) return true;
                    const search = unitSearchTerm.toLowerCase();
                    return (
                      (u.name || '').toLowerCase().includes(search) ||
                      (u.unitNumber || '').toLowerCase().includes(search) ||
                      (u.project || '').toLowerCase().includes(search)
                    );
                  })
                  .map(u => (
                    <option key={u.id} value={u.id}>
                      {u.name} - {appLanguage() === 'de' ? 'Einheit' : 'Unit'} {u.unitNumber} • {u.project}
                    </option>
                  ))
                }
              </select>
            </div>

            {/* Request to Add Unit */}
            <div style={{ marginTop: '8px' }}>
              {!showUnitRequest ? (
                <button
                  onClick={() => setShowUnitRequest(true)}
                  style={{
                    width: '100%',
                    padding: '10px',
                    borderRadius: '10px',
                    border: '1px dashed #93c5fd',
                    background: '#eff6ff',
                    color: '#1e40af',
                    fontSize: '13px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px'
                  }}
                >
                  <Plus className="h-4 w-4" />
                  {t('requestAddUnit') || "Can't find your unit? Request to add"}
                </button>
              ) : (
                <div style={{
                  padding: '16px',
                  borderRadius: '12px',
                  border: '1px solid #93c5fd',
                  background: '#eff6ff',
                }}>
                  <h4 style={{ fontSize: '14px', fontWeight: '700', color: '#1e40af', margin: '0 0 12px 0' }}>
                    {t('requestNewUnit') || 'Request New Unit'}
                  </h4>
                  <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
                    <button
                      onClick={() => setRequestMethod('document')}
                      style={{
                        flex: 1, padding: '12px 8px', borderRadius: '10px',
                        border: requestMethod === 'document' ? '2px solid #1e40af' : '2px solid #d1d5db',
                        background: requestMethod === 'document' ? '#dbeafe' : '#fff',
                        cursor: 'pointer', textAlign: 'center'
                      }}
                    >
                      <div style={{ marginBottom: '4px', display: 'flex', justifyContent: 'center' }}><FileText size={20} color="#1e40af" /></div>
                      <div style={{ fontSize: '12px', fontWeight: '600', color: requestMethod === 'document' ? '#1e40af' : '#6b7280' }}>
                        {t('iHaveDocument') || 'I have a document'}
                      </div>
                    </button>
                    <button
                      onClick={() => setRequestMethod('manual')}
                      style={{
                        flex: 1, padding: '12px 8px', borderRadius: '10px',
                        border: requestMethod === 'manual' ? '2px solid #1e40af' : '2px solid #d1d5db',
                        background: requestMethod === 'manual' ? '#dbeafe' : '#fff',
                        cursor: 'pointer', textAlign: 'center'
                      }}
                    >
                      <div style={{ marginBottom: '4px', display: 'flex', justifyContent: 'center' }}><PenLine size={20} color="#1e40af" /></div>
                      <div style={{ fontSize: '12px', fontWeight: '600', color: requestMethod === 'manual' ? '#1e40af' : '#6b7280' }}>
                        {t('enterManually') || 'Enter details manually'}
                      </div>
                    </button>
                  </div>
                  {requestMethod === 'document' && (
                    <>
                      <div style={{ marginBottom: '10px' }}>
                        <label style={{ fontSize: '12px', fontWeight: '600', color: '#374151', display: 'block', marginBottom: '4px' }}>
                          {t('availableDoc') || 'Which document do you have?'} *
                        </label>
                        <select value={requestUnitDocType} onChange={(e) => setRequestUnitDocType(e.target.value)}
                          style={{ width: '100%', padding: '10px 12px', borderRadius: '10px', border: '1px solid #d1d5db', fontSize: '13px', background: '#fff', boxSizing: 'border-box' as const }}>
                          <option value="">{t('selectDocument') || '-- Select Document --'}</option>
                          <option value="Booking Form">{t('pvDocBookingForm')}</option>
                          <option value="Reservation Agreement">{t('pvDocReservationAgreement')}</option>
                          <option value="EOI">EOI</option>
                          <option value="OTP">OTP</option>
                          <option value="OQOOD">OQOOD</option>
                          <option value="SPA">SPA</option>
                          <option value="Other">{t('other')}</option>
                        </select>
                      </div>
                      <p style={{ fontSize: '11px', color: '#6b7280', margin: '0 0 10px 0', fontStyle: 'italic' }}>
                        {t('docUploadNote') || 'We will create the unit and contact you to upload the document.'}
                      </p>
                    </>
                  )}
                  {requestMethod === 'manual' && (
                    <>
                      <div style={{ marginBottom: '10px' }}>
                        <label style={{ fontSize: '12px', fontWeight: '600', color: '#374151', display: 'block', marginBottom: '4px' }}>
                          {t('projectName') || 'Project Name'} *
                        </label>
                        <input type="text" value={requestProjectName} onChange={(e) => setRequestProjectName(e.target.value)}
                          placeholder={t('projectPlaceholder') || 'e.g. Aeon Tower, Ocean Star, Clearpoint'}
                          style={{ width: '100%', padding: '10px 12px', borderRadius: '10px', border: '1px solid #d1d5db', fontSize: '13px', boxSizing: 'border-box' as const }} />
                      </div>
                      <div style={{ marginBottom: '10px' }}>
                        <label style={{ fontSize: '12px', fontWeight: '600', color: '#374151', display: 'block', marginBottom: '4px' }}>
                          {t('unitNumber') || 'Unit / Property Number'} *
                        </label>
                        <input type="text" value={requestUnitName} onChange={(e) => setRequestUnitName(e.target.value)}
                          placeholder={t('unitPlaceholder') || 'e.g. Building 1, Unit 905 or Villa 23'}
                          style={{ width: '100%', padding: '10px 12px', borderRadius: '10px', border: '1px solid #d1d5db', fontSize: '13px', boxSizing: 'border-box' as const }} />
                      </div>
                    </>
                  )}
                  {requestMethod && (
                    <>
                      <div style={{ marginBottom: '12px' }}>
                        <label style={{ fontSize: '12px', fontWeight: '600', color: '#374151', display: 'block', marginBottom: '4px' }}>
                          {t('additionalNotes') || 'Additional Notes (optional)'}
                        </label>
                        <textarea value={requestUnitNotes} onChange={(e) => setRequestUnitNotes(e.target.value)}
                          placeholder={t('notesPlaceholder') || 'e.g. Developer: Avenew Development, 2BR apartment'}
                          rows={2}
                          style={{ width: '100%', padding: '10px 12px', borderRadius: '10px', border: '1px solid #d1d5db', fontSize: '13px', resize: 'none', boxSizing: 'border-box' as const }} />
                      </div>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                          onClick={() => { setShowUnitRequest(false); setRequestUnitName(''); setRequestUnitDocType(''); setRequestUnitNotes(''); setRequestProjectName(''); setRequestMethod(''); }}
                          style={{ flex: 1, padding: '10px', borderRadius: '10px', border: '1px solid #d1d5db', background: '#fff', color: '#6b7280', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}>
                          {t('cancel') || 'Cancel'}
                        </button>
                        <button
                          onClick={async () => {
                            if (requestMethod === 'document' && !requestUnitDocType) { alert(t('selectDocType') || 'Please select which document you have'); return; }
                            if (requestMethod === 'manual' && (!requestProjectName.trim() || !requestUnitName.trim())) { alert(t('enterProjectUnit') || 'Please enter project name and unit number'); return; }
                            try {
                              const response = await fetch(`${backendUrl}/api/requestAddUnit`, {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({
                                  investorId: currentInvestor?.id, investorName: currentInvestor?.name, investorEmail: currentInvestor?.email,
                                  requestMethod, projectName: requestProjectName, unitDetails: requestUnitName, documentType: requestUnitDocType, notes: requestUnitNotes
                                })
                              });
                              const result = await response.json();
                              if (result.success) {
                                alert(t('unitRequestSent') || 'Request sent! Our team will add the unit shortly.');
                                setShowUnitRequest(false); setRequestUnitName(''); setRequestUnitDocType(''); setRequestUnitNotes(''); setRequestProjectName(''); setRequestMethod('');
                              }
                            } catch (err) { alert(t('pvErrorSendingRequest')); }
                          }}
                          style={{ flex: 1, padding: '10px', borderRadius: '10px', border: 'none', background: '#1e40af', color: '#fff', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}>
                          {t('sendRequest') || 'Send Request'}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Step 3: Document Type */}
        <div>
          <label className="block text-sm font-semibold text-gray-900 mb-2">
            <span style={{ 
              display: 'inline-flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              width: '22px', 
              height: '22px', 
              borderRadius: '50%', 
              background: '#D6B160', 
              color: '#fff', 
              fontSize: '12px', 
              fontWeight: '700',
              marginRight: '8px'
            }}>{documentCategory === 'Unit Documents' ? '3' : '2'}</span>
            {t('documentType') || 'Document Type'}
          </label>
          <select
            value={documentType}
            onChange={(e) => setDocumentType(e.target.value)}
            className="w-full p-3 border border-gray-200 rounded-xl text-sm bg-white focus:ring-2 focus:ring-[#D6B160] focus:border-transparent"
          >
            {documentCategory === 'Personal Documents' ? (
              <>
                <option value="Passport">{t('passport')}</option>
                <option value="Visa">{t('visa')}</option>
                <option value="ID">{t('emiratesId')}</option>
              </>
            ) : documentCategory === 'Company Documents' ? (
              <>
                <option value="Trade License">{t('tradeLicense')}</option>
                <option value="MOA">{t('pvDocMoa')}</option>
                <option value="Power of Attorney">{t('pvDocPoa')}</option>
                <option value="Certificate of Incorporation">{t('pvDocCertIncorporation')}</option>
                <option value="Share Certificate">{t('pvDocShareCertificate')}</option>
                <option value="Board Resolution">{t('pvDocBoardResolution')}</option>
                <option value="Tax Certificate">{t('pvDocTaxCertificate')}</option>
                <option value="Bank Reference Letter">{t('pvDocBankReference')}</option>
                <option value="Other">{t('other')}</option>
              </>
            ) : (
              <>
                <option value="OQOOD">OQOOD</option>
                <option value="OTP Document">OTP</option>
                <option value="SPA">SPA</option>
                <option value="Booking Form">{t('pvDocBookingForm')}</option>
                <option value="Reservation Agreement">{t('pvDocReservationAgreement')}</option>
                <option value="EOI">EOI</option>
                <option value="SOA Document">SOA</option>
                <option value="Payment Proof">{t('paymentProof')}</option>
                <option value="Brochure">{t('pvDocBrochure')}</option>
                <option value="Floor Plan">{t('floorPlan')}</option>
                <option value="NOC">NOC</option>
                <option value="Ejari">Ejari</option>
                <option value="Other">{t('other')}</option>
              </>
            )}
          </select>
        </div>

        {/* AI Extraction Info */}
        {documentCategory === 'Unit Documents' && ['OQOOD', 'OTP Document', 'SPA', 'Booking Form', 'Reservation Agreement', 'EOI'].includes(documentType) && (
          <div style={{
            padding: '12px 16px',
            background: 'linear-gradient(135deg, #EFF6FF, #E0F2FE)',
            borderRadius: '12px',
            border: '1px solid #93C5FD',
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}>
            <span style={{ fontSize: '20px' }}>🤖</span>
            <div>
              <p style={{ fontSize: '13px', fontWeight: '600', color: '#1e40af', margin: 0 }}>
                {t('aiExtraction') || 'AI Extraction'}
              </p>
              <p style={{ fontSize: '11px', color: '#3b82f6', margin: '2px 0 0 0' }}>
                {t('aiExtractionDesc') || 'Property data will be automatically extracted from this document'}
              </p>
            </div>
          </div>
        )}

        {/* Step 4: Upload File */}
        <div>
          <label className="block text-sm font-semibold text-gray-900 mb-2">
            <span style={{ 
              display: 'inline-flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              width: '22px', 
              height: '22px', 
              borderRadius: '50%', 
              background: documentCategory === 'Unit Documents' && !selectedUnitForDoc ? '#d1d5db' : '#D6B160', 
              color: '#fff', 
              fontSize: '12px', 
              fontWeight: '700',
              marginRight: '8px'
            }}>{documentCategory === 'Unit Documents' ? '4' : '3'}</span>
            {t('chooseFile') || 'Choose File'}
          </label>
          <label
            htmlFor="document-file-input"
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              padding: '24px',
              borderRadius: '12px',
              border: `2px dashed ${uploadingDocument || (documentCategory === 'Unit Documents' && !selectedUnitForDoc) ? '#d1d5db' : '#D6B160'}`,
              background: uploadingDocument || (documentCategory === 'Unit Documents' && !selectedUnitForDoc) ? '#f9fafb' : '#FFFBEB',
              cursor: uploadingDocument || (documentCategory === 'Unit Documents' && !selectedUnitForDoc) ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s'
            }}
          >
            <Upload className="h-8 w-8 mb-2" style={{ color: uploadingDocument || (documentCategory === 'Unit Documents' && !selectedUnitForDoc) ? '#d1d5db' : '#D6B160' }} />
            <span style={{ fontSize: '14px', fontWeight: '600', color: '#374151' }}>
              {uploadingDocument 
                ? (t('uploading') || 'Uploading...') 
                : documentCategory === 'Unit Documents' && !selectedUnitForDoc
                ? (t('selectUnitFirst') || 'Select a unit first')
                : (t('tapToUpload') || 'Tap to upload')}
            </span>
            <span style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>{t('pvFileFormats')}</span>
          </label>
          <input
            id="document-file-input"
            type="file"
            onChange={handleFileUpload}
            accept=".pdf,.jpg,.jpeg,.png"
            className="hidden"
            disabled={uploadingDocument || (documentCategory === 'Unit Documents' && !selectedUnitForDoc)}
          />
          {uploadingDocument && (
            <div style={{ marginTop: '12px', display: 'flex', alignItems: 'center', gap: '8px', color: '#D6B160', fontSize: '14px' }}>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#D6B160]"></div>
              <span>
                {['OQOOD', 'OTP Document', 'SPA', 'Booking Form', 'Reservation Agreement', 'EOI'].includes(documentType)
                  ? (t('processingAI') || 'Processing with AI - Extracting property data...')
                  : (t('uploadingDoc') || 'Uploading document...')}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  </div>
)}

{/* Request Documents Modal */}
{showRequestDocumentsModal && (
  <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
    <div className="bg-white rounded-2xl w-full max-w-md max-h-[85vh] overflow-y-auto">
      {/* Modal Header */}
      <div className="sticky top-0 bg-white border-b border-gray-200 px-5 py-4 rounded-t-2xl flex items-center justify-between">
        <div>
          <h3 className="text-lg font-bold text-gray-900">{t('requestDocuments')}</h3>
          <p className="text-sm text-gray-600 mt-0.5">{t('pvRequestDocsSub')}</p>
        </div>
        <button
          onClick={() => {
            setShowRequestDocumentsModal(false);
            setRequestUnitId('');
            setRequestedDocTypes([]);
          }}
          className="p-2 hover:bg-gray-100 rounded-lg"
        >
          <X className="h-5 w-5 text-gray-600" />
        </button>
      </div>

      {/* Modal Content */}
      <div className="p-5 space-y-4">
        {/* Select Unit */}
        <div>
          <label className="block text-sm font-medium text-gray-900 mb-2">
            {t('pvSelectPropertyUnit')} <span className="text-red-500">*</span>
          </label>
          <select
            value={requestUnitId}
            onChange={(e) => setRequestUnitId(e.target.value)}
            className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
          >
            <option value="">{t('selectAUnit')}</option>
            {units.map(unit => (
              <option key={unit.id} value={unit.id}>
                {unit.project} - {t('unit')} {unit.unitNumber} ({unit.area} sqft)
              </option>
            ))}
          </select>
          {units.length === 0 && (
            <p className="text-xs text-amber-600 mt-1">
              <AlertCircle className="h-3 w-3 inline mr-1" />
              {t('pvNoPropsAddFirst')}
            </p>
          )}
        </div>

        {/* Document Types Checkboxes */}
        <div>
          <label className="block text-sm font-medium text-gray-900 mb-3">
            {t('pvSelectDocsNeeded')} <span className="text-red-500">*</span>
          </label>
          <div className="space-y-2">
            {[
              { value: 'OQOOD', label: 'OQOOD' },
              { value: 'OTP', label: 'OTP' },
              { value: 'SPA', label: 'SPA' },
              { value: 'SOA', label: 'SOA' },
              { value: 'Payment Proof', label: t('paymentProof') },
              { value: 'Ejari', label: 'Ejari' },
              { value: 'Brochure', label: t('pvDocPropertyBrochure') },
              { value: 'Floor Plan', label: t('floorPlan') },
              { value: 'Other', label: t('pvDocOtherDocuments') }
            ].map((docType) => (
              <label key={docType.value} className="flex items-center gap-3 p-3 border border-gray-200 rounded-xl hover:bg-gray-50 cursor-pointer">
                <input
                  type="checkbox"
                  checked={requestedDocTypes.includes(docType.value)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setRequestedDocTypes([...requestedDocTypes, docType.value]);
                    } else {
                      setRequestedDocTypes(requestedDocTypes.filter(t => t !== docType.value));
                    }
                  }}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">{docType.label}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Submit Button */}
        <div className="pt-2">
         <button
    onClick={async () => {
      if (!requestUnitId) {
        alert(t('pvAlertSelectUnit'));
        return;
      }
      if (requestedDocTypes.length === 0) {
        alert(t('pvAlertSelectDocType'));
        return;
      }

      const selectedUnit = units.find(u => u.id === requestUnitId);
      
      try {
        const response = await fetch(`${backendUrl}/api/requestDocuments`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            investorId: currentInvestor.id,
            investorName: currentInvestor.name,
            investorEmail: currentInvestor.email,
            unitId: requestUnitId,
            unitDetails: {
              project: selectedUnit?.project,
              unitNumber: selectedUnit?.unitNumber,
              area: selectedUnit?.area
            },
            requestedDocuments: requestedDocTypes,
            requestDate: new Date().toISOString()
          })
        });

        if (response.ok) {
          alert(t('pvDocRequestSuccess'));
          setShowRequestDocumentsModal(false);
          setRequestUnitId('');
          setRequestedDocTypes([]);
        } else {
          throw new Error('Failed to submit request');
        }
      } catch (error) {
        console.error('Error submitting document request:', error);
        alert(t('pvDocRequestError'));
      }
    }}
    disabled={!requestUnitId || requestedDocTypes.length === 0}
    style={{
      backgroundColor: !requestUnitId || requestedDocTypes.length === 0 ? '#9ca3af' : '#D6B160',
      color: '#FFFFFF',
      opacity: !requestUnitId || requestedDocTypes.length === 0 ? 0.5 : 1,
      border: 'none'
    }}
    className="w-full px-4 py-3 rounded-xl font-semibold transition-all hover:opacity-90"
  >
    <span style={{ color: '#FFFFFF' }}>{t('pvSubmitRequest')}</span>
  </button>
        </div>
      </div>
    </div>
  </div>
)}

      {/* ============================================
          RECORD A SALE — sale price in, transaction costs off, ROI out.
          Rendered at the top level (not inside the property detail) so it can
          be opened from the card list, the Selling tab and the Sold tab alike.
          ============================================ */}
      {soldUnitId && soldUnit && (() => {
        const known = purchaseOnRecord(soldUnit);
        const saleP = soldPreview.salePrice;
        const feePct = (amount: number) => (saleP > 0 && amount > 0 ? `${((amount / saleP) * 100).toFixed(2)}% ${language === 'de' ? 'des Verkaufspreises' : 'of sale price'}` : '');
        const labelStyle: React.CSSProperties = { display: 'block', fontSize: '12px', fontWeight: 700, color: '#374151', marginBottom: '5px' };
        const inputStyle: React.CSSProperties = {
          width: '100%', padding: '11px 12px', borderRadius: '10px',
          border: '1px solid #d1d5db', background: '#fff',
          fontSize: '15px', fontWeight: 600, color: '#111827',
          outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit'
        };
        const hintStyle: React.CSSProperties = { margin: '4px 0 0', fontSize: '11px', color: '#9ca3af' };
        const rowStyle: React.CSSProperties = { display: 'flex', justifyContent: 'space-between', gap: '12px', fontSize: '13px', padding: '5px 0' };

        return (
          <div
            style={{
              position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
              background: 'rgba(0,0,0,0.5)', zIndex: 9999,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              padding: '16px'
            }}
            onClick={closeSoldModal}
          >
            <div
              style={{
                background: '#fff', borderRadius: '20px', maxWidth: '460px', width: '100%',
                maxHeight: '92vh', overflowY: 'auto',
                padding: '24px', boxShadow: '0 20px 60px rgba(0,0,0,0.2)'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                <div style={{
                  width: '56px', height: '56px', borderRadius: '50%', margin: '0 auto 14px',
                  background: '#1a1a2e', display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}>
                  <Receipt style={{ width: '26px', height: '26px', color: '#D6B160' }} />
                </div>
                <h3 style={{ margin: '0 0 6px', fontSize: '19px', fontWeight: 700, color: '#111827' }}>
                  {soldUnit.status === 'sold'
                    ? (language === 'de' ? 'Verkauf bearbeiten' : 'Edit sale details')
                    : (language === 'de' ? 'Verkauf erfassen' : 'Record the sale')}
                </h3>
                <p style={{ margin: 0, color: '#6b7280', fontSize: '13px', lineHeight: 1.5 }}>
                  {soldUnit.name || soldUnit.unitNumber}{soldUnit.project ? ` · ${soldUnit.project}` : ''}
                </p>
                {/* Off-plan has no title to transfer: what changes hands is the
                    SPA, and the numbers below follow that. */}
                <div style={{
                  display: 'inline-flex', alignItems: 'center', gap: '6px', marginTop: '10px',
                  padding: '5px 11px', borderRadius: '999px',
                  background: soldIsAssignment ? '#EFF6FF' : '#f3f4f6',
                  border: `1px solid ${soldIsAssignment ? '#93C5FD' : '#e5e7eb'}`
                }}>
                  <span style={{ fontSize: '11px', fontWeight: 700, color: soldIsAssignment ? '#1e40af' : '#4b5563' }}>
                    {soldIsAssignment
                      ? (language === 'de' ? 'OFF-PLAN · SPA-ÜBERTRAGUNG' : 'OFF-PLAN · SPA ASSIGNMENT')
                      : (language === 'de' ? 'FERTIGGESTELLT · EIGENTUMSÜBERTRAGUNG' : 'COMPLETED · TITLE TRANSFER')}
                  </span>
                </div>
                {soldIsAssignment && (
                  <p style={{ margin: '10px 0 0', fontSize: '11.5px', color: '#1e40af', lineHeight: 1.55, textAlign: 'left', background: '#EFF6FF', border: '1px solid #DBEAFE', borderRadius: '10px', padding: '9px 11px' }}>
                    {language === 'de'
                      ? 'Diese Einheit ist noch nicht übergeben. Der Käufer übernimmt die restlichen Raten, und Ihre Rendite wird an dem gemessen, was Sie bisher tatsächlich eingezahlt haben — nicht am vollen SPA-Preis.'
                      : 'This unit has not been handed over yet. The buyer takes over the remaining installments, and your return is measured against what you have actually paid in — not the full SPA price.'}
                  </p>
                )}
              </div>

              {/* Purchase price — read from the record, or asked for when missing */}
              {known > 0 ? (
                <div style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  background: '#f9fafb', borderRadius: '12px', padding: '13px 14px', marginBottom: '14px'
                }}>
                  <span style={{ fontSize: '12px', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase' }}>
                    {soldIsAssignment
                      ? (language === 'de' ? 'SPA-/Objektwert' : 'SPA / unit value')
                      : (language === 'de' ? 'Kaufpreis' : 'Purchase price')}
                  </span>
                  <span style={{ textAlign: 'right' }}>
                    <span style={{ display: 'block', fontSize: '15px', fontWeight: 700, color: '#111827' }}>
                      AED {known.toLocaleString('en-AE')}
                    </span>
                    <span style={{ fontSize: '11px', color: '#9ca3af' }}>{formatEUR(known)}</span>
                  </span>
                </div>
              ) : (
                <div style={{ marginBottom: '14px' }}>
                  <label style={labelStyle}>
                    {language === 'de' ? 'Kaufpreis (AED)' : 'Purchase price (AED)'}
                    <span style={{ color: '#dc2626' }}> *</span>
                  </label>
                  <input
                    type="text"
                    inputMode="decimal"
                    placeholder={t('pvEgPriceOriginal')}
                    value={soldForm.purchasePrice}
                    onChange={(e) => setSoldForm(prev => ({ ...prev, purchasePrice: e.target.value }))}
                    style={inputStyle}
                  />
                  <p style={hintStyle}>
                    {language === 'de'
                      ? 'Für diese Einheit ist kein Kaufpreis gespeichert — ohne ihn gibt es keinen ROI.'
                      : 'No purchase price is on record for this unit — the ROI needs it.'}
                  </p>
                </div>
              )}

              {/* Capital actually put in — the ROI base for an assignment */}
              {soldIsAssignment && (
                <div style={{
                  background: '#F8FAFC', borderRadius: '14px', padding: '14px', marginBottom: '14px',
                  border: '1px solid #E2E8F0'
                }}>
                  <div style={{ fontSize: '12px', fontWeight: 700, color: '#374151', textTransform: 'uppercase', marginBottom: '4px' }}>
                    {language === 'de' ? 'Ihr eingesetztes Kapital' : 'Your capital in the unit'}
                  </div>
                  <p style={{ margin: '0 0 12px', fontSize: '11px', color: '#6b7280', lineHeight: 1.5 }}>
                    {language === 'de'
                      ? 'Der SPA-Wert oben ist der Vertragspreis des Entwicklers — nicht zwangsläufig Ihr Kaufpreis. Entscheidend für die Rendite ist, was SIE eingesetzt haben.'
                      : 'The SPA value above is the developer\'s contract price — not necessarily what you paid. The return is measured against what YOU put in.'}
                  </p>

                  {/* How the unit was acquired. Buying the SPA from the developer
                      and taking it over from an earlier owner are different
                      outlays, and only the owner knows which one happened. */}
                  <div style={{ marginBottom: '12px' }}>
                    <label style={labelStyle}>
                      {language === 'de' ? 'Wie haben Sie die Einheit erworben?' : 'How did you acquire this unit?'}
                    </label>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      {([
                        { key: 'developer' as AcquisitionRoute, en: 'From the developer', de: 'Vom Entwickler' },
                        { key: 'assigned-in' as AcquisitionRoute, en: 'Taken over from another owner', de: 'Von einem Voreigentümer übernommen' },
                      ]).map(opt => {
                        const on = soldForm.acquisitionRoute === opt.key;
                        return (
                          <button
                            key={opt.key}
                            type="button"
                            onClick={() => setSoldForm(prev => {
                              const plan = unitPaidAmounts[soldUnit.id];
                              return {
                                ...prev,
                                acquisitionRoute: opt.key,
                                // Switching route changes what the numbers mean, so the
                                // capital fields are re-seeded rather than carried over.
                                acquisitionPrice: opt.key === 'developer' ? '' : prev.acquisitionPrice,
                                assumedBalance: opt.key === 'developer' ? '' : prev.assumedBalance,
                                paidToDate: opt.key === 'developer'
                                  ? (plan && plan.paid > 0 ? String(Math.round(plan.paid)) : '')
                                  : '',
                              };
                            })}
                            style={{
                              flex: 1, padding: '10px 8px', borderRadius: '10px', cursor: 'pointer',
                              fontFamily: 'inherit', fontSize: '11.5px', fontWeight: 700, lineHeight: 1.35,
                              border: `1.5px solid ${on ? '#1a1a2e' : '#d1d5db'}`,
                              background: on ? '#1a1a2e' : '#fff',
                              color: on ? '#fff' : '#4b5563'
                            }}
                          >
                            {language === 'de' ? opt.de : opt.en}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Taken over from someone else. Two figures, both straight off
                      the DLD transfer form: the price the unit changed hands at,
                      and the developer balance that came with it. The price alone
                      is not capital — the balance inside it was paid off later,
                      installment by installment, and is counted below. Treating
                      the whole assignment price as cash paid, and then taking the
                      exit balance off the proceeds as well, charged the same
                      developer money twice and turned a profit into a loss. */}
                  {soldForm.acquisitionRoute === 'assigned-in' && (
                    <>
                      <div style={{ marginBottom: '12px' }}>
                        <label style={labelStyle}>
                          {language === 'de' ? 'Übernahmepreis laut DLD-Formular (AED)' : 'Assignment price on the DLD form (AED)'}
                          <span style={{ color: '#dc2626' }}> *</span>
                        </label>
                        <input
                          type="text"
                          inputMode="decimal"
                          value={soldForm.acquisitionPrice}
                          onChange={(e) => setSoldForm(prev => ({ ...prev, acquisitionPrice: e.target.value }))}
                          style={{ ...inputStyle, border: '1.5px solid #93C5FD' }}
                        />
                        <p style={hintStyle}>
                          {language === 'de'
                            ? 'Der Verkaufspreis („Sell Price“) auf dem Übertragungsformular — der volle Wert der Einheit an diesem Tag, inklusive der noch offenen Restschuld.'
                            : 'The "Sell Price" on the transfer form — the full value of the unit on that day, including the balance still owed to the developer.'}
                          {soldPreview.acquisitionPrice > 0 && ` · ${formatEUR(soldPreview.acquisitionPrice)}`}
                        </p>
                      </div>

                      <div style={{ marginBottom: '12px' }}>
                        <label style={labelStyle}>
                          {language === 'de' ? 'Damals übernommene Restschuld (AED)' : 'Balance you took over from them (AED)'}
                        </label>
                        <input
                          type="text"
                          inputMode="decimal"
                          value={soldForm.assumedBalance}
                          onChange={(e) => setSoldForm(prev => ({
                            ...prev,
                            assumedBalance: e.target.value,
                            // What the next buyer takes over is simply what is left
                            // of it after the installments paid since.
                            outstandingBalance: soldOutstandingEdited
                              ? prev.outstandingBalance
                              : String(Math.max(0, Math.round(parseMoney(e.target.value) - parseMoney(prev.paidToDate))))
                          }))}
                          style={inputStyle}
                        />
                        <p style={hintStyle}>
                          {language === 'de'
                            ? '„Balance Amount“ auf demselben Formular — SPA-Preis minus das, was bis dahin gezahlt war. Sie kam mit der Einheit zu Ihnen.'
                            : 'The "Balance Amount" on the same form — the SPA price less what had been paid by then. It came across with the unit.'}
                          {soldPreview.assumedBalance > 0 && ` · ${formatEUR(soldPreview.assumedBalance)}`}
                        </p>
                      </div>

                      {/* The number the owner can check against their own bank
                          transfer — if this is not what they handed over, one of
                          the two figures above is wrong. */}
                      {soldPreview.acquisitionPrice > 0 && soldPreview.assumedBalance > 0 && (
                        <div style={{
                          display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '12px',
                          padding: '10px 12px', marginBottom: '12px',
                          background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: '10px'
                        }}>
                          <span style={{ fontSize: '11.5px', fontWeight: 700, color: '#166534' }}>
                            {language === 'de' ? 'An den Voreigentümer geflossen' : 'Cash to the previous owner'}
                          </span>
                          <span style={{ fontSize: '14px', fontWeight: 700, color: '#166534' }}>
                            AED {Math.round(soldPreview.acquisitionCash).toLocaleString('en-AE')}
                          </span>
                        </div>
                      )}
                    </>
                  )}

                  <div style={{ marginBottom: '12px' }}>
                    <label style={labelStyle}>
                      {soldForm.acquisitionRoute === 'assigned-in'
                        ? (language === 'de' ? 'Seit der Übernahme eingezahlt (AED)' : 'Paid in since you took over (AED)')
                        : (language === 'de' ? 'Bisher an den Entwickler gezahlt (AED)' : 'Paid to the developer so far (AED)')}
                      {soldForm.acquisitionRoute === 'developer' && <span style={{ color: '#dc2626' }}> *</span>}
                    </label>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={soldForm.paidToDate}
                      onChange={(e) => setSoldForm(prev => ({
                        ...prev,
                        paidToDate: e.target.value,
                        // What is left for the buyer to take over. On a unit bought
                        // from the developer that is the rest of the SPA price; on a
                        // taken-over one it is the balance that came with it, less
                        // what has been paid since — the SPA price says nothing
                        // there, because the previous owner had already paid part.
                        outstandingBalance: soldOutstandingEdited
                          ? prev.outstandingBalance
                          : String(Math.max(0, Math.round(
                              (prev.acquisitionRoute === 'assigned-in' ? parseMoney(prev.assumedBalance) : known)
                              - parseMoney(e.target.value))))
                      }))}
                      style={{ ...inputStyle, border: soldForm.acquisitionRoute === 'developer' ? '1.5px solid #93C5FD' : '1px solid #d1d5db' }}
                    />
                    <p style={hintStyle}>
                      {unitPaidAmounts[soldUnit.id]
                        ? (soldForm.acquisitionRoute === 'assigned-in'
                            ? (language === 'de'
                                ? `Der Zahlungsplan weist AED ${Math.round(unitPaidAmounts[soldUnit.id].paid).toLocaleString('en-AE')} als insgesamt gezahlt aus — davon zählt nur Ihr eigener Anteil.`
                                : `The payment plan shows AED ${Math.round(unitPaidAmounts[soldUnit.id].paid).toLocaleString('en-AE')} paid in total — only your own share of that counts here.`)
                            : (language === 'de'
                                ? 'Aus Ihrem Zahlungsplan vorbelegt (fällige Raten gelten als bezahlt). Bitte prüfen.'
                                : 'Prefilled from your payment plan (installments already due count as paid). Please check it.'))
                        : (language === 'de'
                            ? 'Für diese Einheit liegt kein Zahlungsplan vor — bitte eintragen.'
                            : 'No payment plan on file for this unit — please enter it.')}
                    </p>
                    {/* An installment falling due between the agreement and the
                        transfer is usually settled by the seller and reimbursed by
                        cheque at the transfer, so this money does not always reach
                        the developer directly. It is the owner's capital either way. */}
                    {soldForm.acquisitionRoute === 'assigned-in' && (
                      <p style={hintStyle}>
                        {language === 'de'
                          ? 'Zählt unabhängig davon, ob Sie direkt an den Entwickler gezahlt oder dem Voreigentümer eine bei der Übertragung fällige Rate erstattet haben — in beiden Fällen ist es Ihr eingesetztes Kapital.'
                          : 'Counts whether you paid the developer directly or reimbursed the previous owner for an installment they settled at the transfer — either way it is your capital in the unit.'}
                      </p>
                    )}
                  </div>



                  {/* What the ROI will actually be measured against */}
                  {soldPreview.capitalBase > 0 && (
                    <div style={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '12px',
                      padding: '11px 12px', marginBottom: '12px',
                      background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: '10px'
                    }}>
                      <span style={{ fontSize: '11.5px', fontWeight: 700, color: '#1e40af' }}>
                        {language === 'de' ? 'Ihr Kapital gesamt' : 'Your total capital'}
                      </span>
                      <span style={{ textAlign: 'right' }}>
                        <span style={{ display: 'block', fontSize: '15px', fontWeight: 700, color: '#1e3a8a' }}>
                          AED {Math.round(soldPreview.capitalBase).toLocaleString('en-AE')}
                        </span>
                        <span style={{ fontSize: '11px', color: '#3b82f6' }}>
                          {formatEUR(soldPreview.capitalBase)}
                          {soldForm.acquisitionRoute === 'assigned-in' && soldPreview.acquisitionCash > 0 && soldPreview.paidToDate > 0 && (
                            <> · {Math.round(soldPreview.acquisitionCash).toLocaleString('en-AE')} + {Math.round(soldPreview.paidToDate).toLocaleString('en-AE')}</>
                          )}
                          {soldPreview.purchaseCostsTotal > 0 && (
                            <> · {language === 'de' ? 'inkl. Kaufkosten' : 'incl. buying costs'} {Math.round(soldPreview.purchaseCostsTotal).toLocaleString('en-AE')}</>
                          )}
                        </span>
                      </span>
                    </div>
                  )}
                  <div>
                    <label style={labelStyle}>
                      {language === 'de' ? 'Restschuld, die der Käufer übernimmt (AED)' : 'Balance the buyer takes over (AED)'}
                    </label>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={soldForm.outstandingBalance}
                      onChange={(e) => { setSoldOutstandingEdited(true); setSoldForm(prev => ({ ...prev, outstandingBalance: e.target.value })); }}
                      style={inputStyle}
                    />
                    <p style={hintStyle}>
                      {language === 'de'
                        ? 'Wird vom Verkaufspreis abgezogen — dieses Geld erhält der Entwickler, nicht Sie.'
                        : 'Comes off the sale price — this money goes to the developer, not to you.'}
                    </p>
                  </div>
                </div>
              )}

              {/* ---------------------------------------------------------------
                  WHAT BUYING IT COST. Never prefilled until the owner says which
                  route they took: a unit taken over from another owner carried the
                  4% DLD, the agency's 2% and a trustee fee; one bought from the
                  developer carried the registration and neither of the others.
                  Guessing would put money into the capital base that was never spent.
                  --------------------------------------------------------------- */}
              <div style={{
                padding: '12px', marginBottom: '12px',
                background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: '10px'
              }}>
                <p style={{ margin: '0 0 2px', fontSize: '12px', fontWeight: 700, color: '#92400E' }}>
                  {language === 'de' ? 'Was der Kauf gekostet hat' : 'What buying it cost'}
                </p>
                <p style={{ margin: '0 0 10px', fontSize: '11.5px', color: '#A16207', lineHeight: 1.5 }}>
                  {language === 'de'
                    ? 'Gebühren, die Sie beim KAUF gezahlt haben. Sie sind eingesetztes Kapital, nicht Gewinn — ohne sie ist die Rendite zu hoch ausgewiesen.'
                    : 'Fees you paid when you BOUGHT it. They are capital you put in, not profit — leaving them out overstates the return.'}
                </p>

                <div style={{ display: 'flex', gap: '8px', marginBottom: '10px', flexWrap: 'wrap' }}>
                  {([['owner', language === 'de' ? 'Von einem Voreigentümer' : 'From another owner'],
                     ['developer', language === 'de' ? 'Direkt vom Entwickler' : 'From the developer']] as const).map(([key, label]) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setSoldBoughtFrom(soldForm.boughtFrom === key ? '' : key)}
                      style={{
                        flex: '1 1 auto', padding: '9px 12px', borderRadius: '8px', cursor: 'pointer',
                        fontSize: '12.5px', fontWeight: 700,
                        border: soldForm.boughtFrom === key ? '1.5px solid #92400E' : '1px solid #FDE68A',
                        background: soldForm.boughtFrom === key ? '#FEF3C7' : '#FFFFFF',
                        color: soldForm.boughtFrom === key ? '#78350F' : '#A16207'
                      }}
                    >
                      {label}
                    </button>
                  ))}
                </div>

                {!soldForm.boughtFrom && (
                  <p style={{ margin: '0', fontSize: '11.5px', color: '#A16207' }}>
                    {language === 'de'
                      ? 'Bitte wählen — davon hängt ab, welche Gebühren überhaupt angefallen sind. Ohne Angabe bleibt die Rendite wie bisher nur am Kaufpreis gemessen.'
                      : 'Pick one — it decides which fees existed at all. Until you do, the return is measured against the purchase price alone, as before.'}
                  </p>
                )}

                {soldForm.boughtFrom && (
                  <>
                    <div style={{ marginBottom: '10px' }}>
                      <label style={labelStyle}>
                        {language === 'de' ? 'DLD-/Oqood-Gebühr beim Kauf (AED)' : 'DLD / Oqood fee on purchase (AED)'}
                      </label>
                      <input
                        type="text" inputMode="decimal" placeholder="0"
                        value={soldForm.purchaseDldFee}
                        onChange={(e) => { setSoldBuyFeeEdited(p => ({ ...p, dld: true })); setSoldForm(prev => ({ ...prev, purchaseDldFee: e.target.value })); }}
                        style={inputStyle}
                      />
                      <p style={hintStyle}>
                        {language === 'de' ? '4% an das DLD — bei einigen Entwicklern übernommen; dann 0 eintragen.'
                                           : '4% to the DLD. Some developers absorb it — set it to 0 if you did not pay it.'}
                      </p>
                    </div>

                    <div style={{ marginBottom: '10px' }}>
                      <label style={labelStyle}>
                        {language === 'de' ? 'Maklerprovision beim Kauf (AED)' : 'Agency commission on purchase (AED)'}
                      </label>
                      <input
                        type="text" inputMode="decimal" placeholder="0"
                        value={soldForm.purchaseCommission}
                        onChange={(e) => { setSoldBuyFeeEdited(p => ({ ...p, commission: true })); setSoldForm(prev => ({ ...prev, purchaseCommission: e.target.value })); }}
                        style={inputStyle}
                      />
                      <p style={hintStyle}>
                        {soldForm.boughtFrom === 'developer'
                          ? (language === 'de' ? 'Beim Direktkauf vom Entwickler fällt normalerweise keine Provision an.'
                                               : 'Buying direct from the developer normally carries no agency commission.')
                          : (language === 'de' ? '2% an den Makler, der Ihnen die Einheit verkauft hat.'
                                               : '2% to the agent who sold you the unit.')}
                      </p>
                    </div>

                    <div style={{ marginBottom: '10px' }}>
                      <label style={labelStyle}>
                        {language === 'de' ? 'Trustee-/Registrierungsgebühr beim Kauf (AED)' : 'Trustee / registration fee on purchase (AED)'}
                      </label>
                      <input
                        type="text" inputMode="decimal" placeholder="0"
                        value={soldForm.purchaseTrusteeFee}
                        onChange={(e) => { setSoldBuyFeeEdited(p => ({ ...p, trustee: true })); setSoldForm(prev => ({ ...prev, purchaseTrusteeFee: e.target.value })); }}
                        style={inputStyle}
                      />
                      <p style={hintStyle}>
                        {soldForm.boughtFrom === 'developer'
                          ? (language === 'de' ? 'Vor der Übergabe registriert der Entwickler, kein Trustee-Office.'
                                               : 'Before handover the developer registers it, not a trustee office.')
                          : (language === 'de' ? 'Trustee-Office, üblicherweise AED 4.200 inkl. MwSt.'
                                               : 'Trustee office, usually AED 4,200 including VAT.')}
                      </p>
                    </div>

                    <div>
                      <label style={labelStyle}>
                        {language === 'de' ? 'Weitere Kaufkosten (AED)' : 'Other purchase costs (AED)'}
                      </label>
                      <input
                        type="text" inputMode="decimal" placeholder="0"
                        value={soldForm.purchaseOtherCosts}
                        onChange={(e) => setSoldForm(prev => ({ ...prev, purchaseOtherCosts: e.target.value }))}
                        style={inputStyle}
                      />
                      <p style={hintStyle}>
                        {language === 'de' ? 'Hypothekenregistrierung (0,25%), NOC, Bankgebühren, Anwalt — alles in einer Summe.'
                                           : 'Mortgage registration (0.25%), NOC, bank charges, legal — as one figure.'}
                      </p>
                    </div>

                    {soldPreview.purchaseCostsTotal > 0 && (
                      <p style={{ margin: '10px 0 0', fontSize: '12px', fontWeight: 700, color: '#78350F' }}>
                        {language === 'de' ? 'Kaufkosten gesamt' : 'Total buying costs'}
                        {': AED '}{Math.round(soldPreview.purchaseCostsTotal).toLocaleString('en-AE')}
                      </p>
                    )}
                  </>
                )}
              </div>

              {/* Sale price + date */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '14px', marginBottom: '14px' }}>
                <div>
                  <label style={labelStyle}>
                    {language === 'de' ? 'Verkaufspreis (AED)' : 'Sale price (AED)'}
                  </label>
                  <input
                    type="text"
                    inputMode="decimal"
                    placeholder={t('pvEgPriceSelling')}
                    value={soldForm.salePrice}
                    onChange={(e) => setSoldSalePrice(e.target.value)}
                    style={{ ...inputStyle, border: '1.5px solid #D6B160' }}
                  />
                  {saleP > 0 && <p style={hintStyle}>{formatEUR(saleP)}</p>}
                </div>
                <div>
                  <label style={labelStyle}>
                    {language === 'de' ? 'Verkaufsdatum' : 'Sale date'}
                  </label>
                  <input
                    type="date"
                    value={soldForm.saleDate}
                    max={todayISO()}
                    onChange={(e) => setSoldForm(prev => ({ ...prev, saleDate: e.target.value }))}
                    style={inputStyle}
                  />
                </div>
              </div>

              {/* Transaction costs */}
              <div style={{
                background: '#f9fafb', borderRadius: '14px', padding: '14px', marginBottom: '14px'
              }}>
                <div style={{ fontSize: '12px', fontWeight: 700, color: '#374151', textTransform: 'uppercase', marginBottom: '4px' }}>
                  {language === 'de' ? 'Verkaufskosten' : 'Selling costs'}
                </div>
                <p style={{ margin: '0 0 12px', fontSize: '11px', color: '#6b7280', lineHeight: 1.5 }}>
                  {language === 'de'
                    ? 'Nur, was SIE beim Verkauf gezahlt haben. Die 4 % DLD und die Trustee-Gebühr zahlt in Dubai der Käufer — Ihre Seite ist üblicherweise die Maklerprovision und, off-plan, die NOC-Gebühr des Entwicklers.'
                    : 'Only what YOU paid to sell. In Dubai the buyer pays the 4% DLD and the trustee office — your side is usually the agent\'s commission and, off-plan, the developer\'s NOC.'}
                </p>

                <div style={{ marginBottom: '12px' }}>
                  <label style={labelStyle}>
                    {language === 'de' ? 'DLD-Übertragungsgebühr (AED)' : 'DLD transfer fee (AED)'}
                  </label>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={soldForm.dldFee}
                    onChange={(e) => { setSoldFeeEdited(p => ({ ...p, dld: true })); setSoldForm(prev => ({ ...prev, dldFee: e.target.value })); }}
                    style={inputStyle}
                  />
                  {soldPreview.dldFee > 0 && <p style={hintStyle}>{feePct(soldPreview.dldFee)}</p>}
                  <p style={{ ...hintStyle, color: '#92400e' }}>
                    {language === 'de'
                      ? `Der Käufer zahlt diese 4 % — leer lassen, außer Sie haben sie ganz oder teilweise übernommen${saleP > 0 ? ` (4 % wären AED ${Math.round(saleP * DLD_TRANSFER_RATE).toLocaleString('en-AE')})` : ''}.`
                      : `The buyer pays this 4%. Leave it empty unless you agreed to carry some of it${saleP > 0 ? ` (4% would be AED ${Math.round(saleP * DLD_TRANSFER_RATE).toLocaleString('en-AE')})` : ''}.`}
                  </p>
                </div>

                <div style={{ marginBottom: '12px' }}>
                  <label style={labelStyle}>
                    {language === 'de' ? 'Maklerprovision (AED)' : 'Agency commission (AED)'}
                  </label>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={soldForm.commission}
                    onChange={(e) => { setSoldFeeEdited(p => ({ ...p, commission: true })); setSoldForm(prev => ({ ...prev, commission: e.target.value })); }}
                    style={inputStyle}
                  />
                  {soldPreview.commission > 0 && <p style={hintStyle}>{feePct(soldPreview.commission)}</p>}
                  <p style={hintStyle}>
                    {language === 'de'
                      ? 'Ihr eigener Makler — die eine Übertragungsgebühr, die üblicherweise auf Ihrer Seite liegt. Ohne Makler auf 0 setzen.'
                      : 'Your own agent — the one transfer fee that is normally yours. Clear it if you sold without an agent.'}
                  </p>
                </div>

                <div style={{ marginBottom: soldIsAssignment ? '12px' : 0 }}>
                  <label style={labelStyle}>
                    {language === 'de' ? 'Trustee-/Registrierungsgebühr (AED)' : 'Trustee / registration fee (AED)'}
                  </label>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={soldForm.trusteeFee}
                    onChange={(e) => { setSoldFeeEdited(p => ({ ...p, trustee: true })); setSoldForm(prev => ({ ...prev, trusteeFee: e.target.value })); }}
                    style={inputStyle}
                  />
                  <p style={{ ...hintStyle, color: '#92400e' }}>
                    {soldIsAssignment
                      ? (language === 'de'
                          ? 'Bei einer SPA-Übertragung vor Übergabe läuft die Registrierung über den Entwickler, nicht über ein Trustee-Büro — und sie geht auf den Käufer.'
                          : 'On a pre-handover assignment the registration runs through the developer, not a trustee office — and it goes on the buyer\'s bill.')
                      : (language === 'de'
                          ? 'Zahlt der Käufer beim Trustee-Büro (üblich AED 4.200 inkl. MwSt.) — nur eintragen, wenn Sie sie getragen haben.'
                          : 'The buyer pays this at the trustee office (usually AED 4,200 incl. VAT) — only enter it if you carried it.')}
                  </p>
                </div>

                {/* Off-plan only — the two fees a pre-handover assignment adds */}
                {soldIsAssignment && (
                  <>
                    <div style={{ marginBottom: '12px' }}>
                      <label style={labelStyle}>
                        {language === 'de' ? 'NOC-Gebühr des Entwicklers (AED)' : 'Developer NOC fee (AED)'}
                      </label>
                      <input
                        type="text"
                        inputMode="decimal"
                        value={soldForm.nocFee}
                        onChange={(e) => setSoldForm(prev => ({ ...prev, nocFee: e.target.value }))}
                        style={inputStyle}
                      />
                      <p style={hintStyle}>
                        {language === 'de'
                          ? 'Pflicht für die Übertragung; je Entwickler meist AED 500 – 5.250.'
                          : 'Mandatory to assign the SPA; usually AED 500 – 5,250 depending on the developer.'}
                      </p>
                    </div>
                    <div>
                      <label style={labelStyle}>
                        {language === 'de' ? 'Verwaltungs-/Übertragungsgebühr des Entwicklers (AED)' : 'Developer admin / assignment fee (AED)'}
                      </label>
                      <input
                        type="text"
                        inputMode="decimal"
                        value={soldForm.developerAdminFee}
                        onChange={(e) => setSoldForm(prev => ({ ...prev, developerAdminFee: e.target.value }))}
                        style={inputStyle}
                      />
                      <p style={hintStyle}>
                        {language === 'de'
                          ? 'Manche Entwickler berechnen einen Prozentsatz des ursprünglichen Kaufpreises.'
                          : 'Some developers charge a percentage of the original purchase price.'}
                      </p>
                    </div>
                  </>
                )}
              </div>

              {/* Other expenses — NOC, mortgage discharge, conveyancing, whatever came up */}
              <div style={{ marginBottom: '16px' }}>
                <div style={{ fontSize: '12px', fontWeight: 700, color: '#374151', textTransform: 'uppercase', marginBottom: '8px' }}>
                  {language === 'de' ? 'Weitere Kosten' : 'Other expenses'}
                </div>
                {soldForm.otherExpenses.map((exp, i) => (
                  <div key={i} style={{ display: 'flex', gap: '8px', marginBottom: '8px', alignItems: 'center' }}>
                    <input
                      type="text"
                      placeholder={language === 'de' ? 'z. B. NOC-Gebühr' : 'e.g. Developer NOC'}
                      value={exp.label}
                      onChange={(e) => setSoldForm(prev => ({
                        ...prev,
                        otherExpenses: prev.otherExpenses.map((x, xi) => xi === i ? { ...x, label: e.target.value } : x)
                      }))}
                      style={{ ...inputStyle, flex: 1.4, fontSize: '13px', fontWeight: 500 }}
                    />
                    <input
                      type="text"
                      inputMode="decimal"
                      placeholder="AED"
                      value={exp.amount}
                      onChange={(e) => setSoldForm(prev => ({
                        ...prev,
                        otherExpenses: prev.otherExpenses.map((x, xi) => xi === i ? { ...x, amount: e.target.value } : x)
                      }))}
                      style={{ ...inputStyle, flex: 1, fontSize: '13px' }}
                    />
                    <button
                      onClick={() => setSoldForm(prev => ({
                        ...prev,
                        otherExpenses: prev.otherExpenses.filter((_, xi) => xi !== i)
                      }))}
                      aria-label={language === 'de' ? 'Kostenposition entfernen' : 'Remove expense'}
                      style={{
                        flex: 'none', width: '34px', height: '38px', borderRadius: '9px',
                        border: '1px solid #e5e7eb', background: '#fff', color: '#9ca3af',
                        cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center'
                      }}
                    >
                      <X size={14} />
                    </button>
                  </div>
                ))}
                <button
                  onClick={() => setSoldForm(prev => ({ ...prev, otherExpenses: [...prev.otherExpenses, { label: '', amount: '' }] }))}
                  style={{
                    width: '100%', padding: '10px', borderRadius: '10px',
                    border: '1px dashed #d1d5db', background: '#fff', color: '#374151',
                    fontSize: '12px', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px'
                  }}
                >
                  <Plus size={14} />
                  {language === 'de' ? 'Kostenposition hinzufügen' : 'Add an expense'}
                </button>
              </div>

              {/* Live result. Every figure below is blank until a sale price is
                  entered — an empty form used to assert a total loss of the
                  purchase price at −100%. */}
              <div style={{
                background: '#1a1a2e', borderRadius: '14px', padding: '16px', marginBottom: '16px', color: '#fff'
              }}>
                {(() => {
                  const ready = soldPreview.hasBasis;
                  const aed = (n: number) => `AED ${Math.abs(Math.round(n)).toLocaleString('en-AE')}`;
                  const dim = { color: 'rgba(255,255,255,0.75)' };
                  const neg = { fontWeight: 700, color: '#fca5a5' };
                  const dash = <span style={{ fontWeight: 700, color: 'rgba(255,255,255,0.5)' }}>—</span>;
                  return (
                    <>
                      <div style={{ ...rowStyle, ...dim }}>
                        <span>{soldIsAssignment
                          ? (language === 'de' ? 'Übertragungspreis' : 'Assignment price')
                          : (language === 'de' ? 'Verkaufspreis' : 'Sale price')}</span>
                        <span style={{ fontWeight: 700, color: '#fff' }}>
                          {saleP > 0 ? aed(saleP) : dash}
                        </span>
                      </div>

                      {/* The developer's balance leaves with the buyer, so it never
                          reaches the seller — it is not a cost, it is not profit. */}
                      {soldIsAssignment && (
                        <div style={{ ...rowStyle, ...dim }}>
                          <span>{language === 'de' ? 'Restschuld (Käufer übernimmt)' : 'Outstanding (buyer assumes)'}</span>
                          <span style={neg}>− {aed(soldPreview.outstandingBalance)}</span>
                        </div>
                      )}

                      <div style={{ ...rowStyle, ...dim }}>
                        <span>{language === 'de' ? 'Verkaufskosten' : 'Selling costs'}</span>
                        <span style={neg}>− {aed(soldPreview.totalCosts)}</span>
                      </div>

                      <div style={{ ...rowStyle, borderTop: '1px solid rgba(255,255,255,0.15)', marginTop: '4px', paddingTop: '9px' }}>
                        <span style={dim}>{soldIsAssignment
                          ? (language === 'de' ? 'Auszahlung an Sie' : 'Cash to you')
                          : (language === 'de' ? 'Nettoerlös' : 'Net proceeds')}</span>
                        <span style={{ fontWeight: 700 }}>
                          {saleP > 0 ? aed(soldPreview.cashToSeller) : dash}
                        </span>
                      </div>

                      <div style={{ ...rowStyle, ...dim }}>
                        <span>{soldIsAssignment
                          ? (language === 'de' ? 'Ihr eingezahltes Kapital' : 'Your capital paid in')
                          : (language === 'de' ? 'Kaufpreis' : 'Purchase price')}</span>
                        <span style={neg}>
                          {soldPreview.capitalBase > 0 ? `− ${aed(soldPreview.capitalBase)}` : dash}
                        </span>
                      </div>

                      <div style={{
                        display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px',
                        marginTop: '12px', paddingTop: '12px', borderTop: '1px solid rgba(255,255,255,0.15)'
                      }}>
                        <div>
                          <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.6)', textTransform: 'uppercase', fontWeight: 700 }}>
                            {language === 'de' ? 'Nettogewinn' : 'Net profit'}
                          </div>
                          <div style={{ fontSize: '16px', fontWeight: 700, marginTop: '3px', color: !ready ? 'rgba(255,255,255,0.5)' : soldPreview.netProfit >= 0 ? '#6ee7b7' : '#fca5a5' }}>
                            {ready
                              ? `${soldPreview.netProfit >= 0 ? '+' : '−'}${aed(soldPreview.netProfit)}`
                              : '—'}
                          </div>
                          {ready && (
                            <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.55)' }}>
                              {formatEUR(soldPreview.netProfit)}
                            </div>
                          )}
                        </div>
                        <div>
                          <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.6)', textTransform: 'uppercase', fontWeight: 700 }}>
                            ROI
                          </div>
                          <div style={{ fontSize: '16px', fontWeight: 700, marginTop: '3px', color: !ready ? 'rgba(255,255,255,0.5)' : soldPreview.netProfit >= 0 ? '#6ee7b7' : '#fca5a5' }}>
                            {ready ? `${soldPreview.roiPercent >= 0 ? '+' : ''}${soldPreview.roiPercent.toFixed(1)}%` : '—'}
                          </div>
                          {ready && (
                            <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.55)' }}>
                              {soldIsAssignment
                                ? (language === 'de' ? 'auf eingezahltes Kapital' : 'on capital paid in')
                                : (language === 'de' ? 'auf den Kaufpreis' : 'on the purchase price')}
                            </div>
                          )}
                        </div>
                        <div>
                          <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.6)', textTransform: 'uppercase', fontWeight: 700 }}>
                            {language === 'de' ? 'Jährl. ROI' : 'Annual ROI'}
                          </div>
                          <div style={{ fontSize: '16px', fontWeight: 700, color: ready ? '#D6B160' : 'rgba(255,255,255,0.5)', marginTop: '3px' }}>
                            {ready && soldPreview.annualizedRoi != null
                              ? `${soldPreview.annualizedRoi >= 0 ? '+' : ''}${soldPreview.annualizedRoi.toFixed(1)}%`
                              : '—'}
                          </div>
                          {soldPreview.holdingMonths && (
                            <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.55)' }}>
                              {soldPreview.holdingMonths} {language === 'de' ? 'Mon. gehalten' : 'months held'}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* On an assignment the price movement is the headline number
                          everyone quotes, but it is not what you made. */}
                      {ready && soldIsAssignment && (
                        <p style={{ margin: '12px 0 0', paddingTop: '10px', borderTop: '1px solid rgba(255,255,255,0.15)', fontSize: '11px', color: 'rgba(255,255,255,0.6)', lineHeight: 1.55 }}>
                          {language === 'de' ? 'Preisentwicklung' : 'Price movement'}:{' '}
                          {soldPreview.grossProfit >= 0 ? '+' : '−'}{aed(soldPreview.grossProfit)}{' '}
                          ({soldPreview.grossRoiPercent >= 0 ? '+' : ''}{soldPreview.grossRoiPercent.toFixed(1)}%{' '}
                          {soldPreview.entryPrice > 0 && soldPreview.entryPrice !== soldPreview.purchase
                            ? (language === 'de' ? 'auf Ihren Einstiegspreis' : 'on your entry price')
                            : (language === 'de' ? 'auf den SPA-Preis' : 'on the SPA price')})
                        </p>
                      )}
                    </>
                  );
                })()}
              </div>

              {/* Note */}
              <div style={{ marginBottom: '16px' }}>
                <label style={labelStyle}>
                  {language === 'de' ? 'Notiz (optional)' : 'Note (optional)'}
                </label>
                <input
                  type="text"
                  placeholder={language === 'de' ? 'z. B. Käufer, Makler, Bemerkung' : 'e.g. buyer, broker, remark'}
                  value={soldForm.notes}
                  onChange={(e) => setSoldForm(prev => ({ ...prev, notes: e.target.value }))}
                  style={{ ...inputStyle, fontWeight: 500, fontSize: '13px' }}
                />
              </div>

              {soldError && (
                <p style={{
                  margin: '0 0 14px', padding: '10px 12px', borderRadius: '10px',
                  background: '#fef2f2', border: '1px solid #fecaca',
                  fontSize: '12px', color: '#991b1b'
                }}>
                  {soldError}
                </p>
              )}

              <p style={{ margin: '0 0 14px', fontSize: '11px', color: '#6b7280', lineHeight: 1.5 }}>
                {language === 'de'
                  ? 'Nach dem Speichern zählt die Einheit nicht mehr zum aktiven Portfolio; der Gewinn erscheint im Bericht als realisierter Verkaufserlös.'
                  : 'Once saved, the unit leaves your active portfolio and the profit appears in the report as realised sale income.'}
              </p>

              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  onClick={closeSoldModal}
                  disabled={soldSaving}
                  style={{
                    flex: 1, padding: '14px', borderRadius: '12px',
                    border: '1px solid #e5e7eb', background: '#fff',
                    color: '#374151', fontWeight: 600, fontSize: '14px', cursor: 'pointer', fontFamily: 'inherit'
                  }}
                >
                  {language === 'de' ? 'Abbrechen' : 'Cancel'}
                </button>
                <button
                  onClick={handleSaveSale}
                  disabled={soldSaving || saleP <= 0 || !soldPreview.hasBasis}
                  style={{
                    flex: 1.4, padding: '14px', borderRadius: '12px',
                    border: 'none', background: 'linear-gradient(135deg, #D6B160, #e8c97a)',
                    color: '#fff', fontWeight: 700, fontSize: '14px', cursor: 'pointer', fontFamily: 'inherit',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                    opacity: (soldSaving || saleP <= 0 || !soldPreview.hasBasis) ? 0.5 : 1
                  }}
                >
                  {soldSaving ? (
                    <>
                      <Loader2 style={{ width: '16px', height: '16px', animation: 'spin 1s linear infinite' }} />
                      {language === 'de' ? 'Speichern …' : 'Saving…'}
                    </>
                  ) : (
                    <>
                      <CheckCircle size={16} />
                      {language === 'de' ? 'Als verkauft speichern' : 'Save as sold'}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ---------------------------------------------------------------
          THE ACCOUNT REGISTER. Saved onto the account, not onto a unit, and
          read back only to divide a payout — see the state block above.
          --------------------------------------------------------------- */}
      {shOpen && (() => {
        const label: React.CSSProperties = { display: 'block', fontSize: '11px', fontWeight: 700, color: '#6b7280', marginBottom: '4px' };
        const input: React.CSSProperties = {
          width: '100%', padding: '9px 11px', borderRadius: '9px', border: '1px solid #d1d5db',
          fontSize: '13.5px', fontWeight: 600, color: '#111827', fontFamily: 'inherit', boxSizing: 'border-box'
        };
        const off = shRowsTotal > 0 && Math.abs(shRowsTotal - 100) > 0.01;
        const pct = (n: number) => `${Number.isInteger(n) ? n : n.toFixed(2)}%`;
        const setRow = (i: number, patch: Partial<ShareholderRow>) =>
          setShRows(prev => prev.map((r, ri) => (ri === i ? { ...r, ...patch } : r)));

        return (
          <div
            onClick={() => { if (!shSaving) setShOpen(false); }}
            style={{
              position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
              background: 'rgba(0,0,0,0.55)', zIndex: 1000,
              display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px'
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                background: '#fff', borderRadius: '20px', maxWidth: '460px', width: '100%',
                maxHeight: '92vh', overflowY: 'auto', padding: '24px',
                boxShadow: '0 20px 60px rgba(0,0,0,0.2)'
              }}
            >
              <div style={{ textAlign: 'center', marginBottom: '18px' }}>
                <h3 style={{ margin: '0 0 6px', fontSize: '18px', fontWeight: 700, color: '#111827' }}>
                  {language === 'de' ? 'Anteilseigner dieses Kontos' : 'Shareholders in this account'}
                </h3>
                <p style={{ margin: 0, color: '#6b7280', fontSize: '12.5px' }}>
                  {currentInvestor?.name || currentInvestor?.email || ''}
                </p>
                <p style={{
                  margin: '10px 0 0', fontSize: '11.5px', color: '#4b5563', lineHeight: 1.55,
                  textAlign: 'left', background: '#f9fafb', border: '1px solid #e5e7eb',
                  borderRadius: '10px', padding: '9px 11px'
                }}>
                  {language === 'de'
                    ? `Gilt für alle ${units.length} Einheiten in diesem Konto. Ändert keine Zahl: das Konto hält jede Einheit weiterhin vollständig — die Anteile bestimmen nur, wie eine Auszahlung unter den Beteiligten aufgeteilt wird.`
                    : `Applies to all ${units.length} units in this account. It changes no figure: the account still holds every unit in full — the shares only decide how a payout divides between the people behind it.`}
                </p>
              </div>

              {shRows.map((row, i) => (
                <div key={i} style={{
                  border: '1px solid #e5e7eb', borderRadius: '12px', padding: '12px',
                  marginBottom: '10px', background: '#fff'
                }}>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <label style={label}>{language === 'de' ? 'Name' : 'Name'}</label>
                      <input
                        type="text"
                        value={row.name}
                        onChange={(e) => setRow(i, { name: e.target.value })}
                        style={input}
                      />
                    </div>
                    <div style={{ width: '86px', flex: 'none' }}>
                      <label style={label}>{language === 'de' ? 'Anteil %' : 'Share %'}</label>
                      <input
                        type="text"
                        inputMode="decimal"
                        value={row.sharePercent}
                        onChange={(e) => setRow(i, { sharePercent: e.target.value })}
                        style={input}
                      />
                    </div>
                    <button
                      onClick={() => setShRows(prev => prev.filter((_, ri) => ri !== i))}
                      aria-label={language === 'de' ? 'Anteilseigner entfernen' : 'Remove shareholder'}
                      style={{
                        flex: 'none', width: '34px', height: '38px', borderRadius: '9px',
                        border: '1px solid #e5e7eb', background: '#fff', color: '#9ca3af',
                        cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center'
                      }}
                    >
                      <X size={14} />
                    </button>
                  </div>
                  <div style={{ marginTop: '8px' }}>
                    <input
                      type="email"
                      placeholder={language === 'de' ? 'E-Mail (optional)' : 'Email (optional)'}
                      value={row.email}
                      onChange={(e) => setRow(i, { email: e.target.value })}
                      style={{ ...input, fontWeight: 500, fontSize: '12.5px' }}
                    />
                  </div>
                </div>
              ))}

              <button
                onClick={() => setShRows(prev => [...prev, { name: '', email: '', sharePercent: '' }])}
                style={{
                  width: '100%', padding: '10px', borderRadius: '10px',
                  border: '1px dashed #d1d5db', background: '#fff', color: '#374151',
                  fontSize: '12px', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px'
                }}
              >
                <Plus size={14} />
                {language === 'de' ? 'Anteilseigner hinzufügen' : 'Add a shareholder'}
              </button>

              {/* A part-filled register is normal while paperwork is still
                  coming in, so this warns rather than blocks. */}
              <div style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
                marginTop: '14px', padding: '11px 12px', borderRadius: '10px',
                background: off ? '#FFFBEB' : '#F0FDF4',
                border: `1px solid ${off ? '#FDE68A' : '#BBF7D0'}`
              }}>
                <span style={{ fontSize: '11.5px', fontWeight: 700, color: off ? '#92400E' : '#166534' }}>
                  {language === 'de' ? 'Summe der Anteile' : 'Shares total'}
                </span>
                <span style={{ fontSize: '14px', fontWeight: 700, color: off ? '#92400E' : '#166534' }}>
                  {pct(Math.round(shRowsTotal * 100) / 100)}
                </span>
              </div>
              {off && (
                <p style={{ margin: '6px 0 0', fontSize: '11px', color: '#92400E' }}>
                  {language === 'de'
                    ? 'Erwartet werden 100%. Sie können trotzdem speichern — die Aufteilung wird als unvollständig markiert.'
                    : '100% is what is expected. You can still save — the split is simply flagged as incomplete.'}
                </p>
              )}

              {shError && (
                <p style={{
                  margin: '14px 0 0', padding: '10px 12px', borderRadius: '10px',
                  background: '#fef2f2', border: '1px solid #fecaca',
                  fontSize: '12px', color: '#991b1b'
                }}>
                  {shError}
                </p>
              )}

              <div style={{ display: 'flex', gap: '10px', marginTop: '18px' }}>
                <button
                  onClick={() => { if (!shSaving) setShOpen(false); }}
                  disabled={shSaving}
                  style={{
                    flex: 1, padding: '13px', borderRadius: '12px', cursor: shSaving ? 'default' : 'pointer',
                    border: '1.5px solid #d1d5db', background: '#fff', color: '#374151',
                    fontSize: '13px', fontWeight: 700, fontFamily: 'inherit'
                  }}
                >
                  {language === 'de' ? 'Abbrechen' : 'Cancel'}
                </button>
                <button
                  onClick={handleSaveShareholders}
                  disabled={shSaving}
                  style={{
                    flex: 2, padding: '13px', borderRadius: '12px', cursor: shSaving ? 'default' : 'pointer',
                    border: 'none', background: '#1a1a2e', color: '#fff',
                    fontSize: '13px', fontWeight: 700, fontFamily: 'inherit',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '7px',
                    opacity: shSaving ? 0.6 : 1
                  }}
                >
                  {shSaving ? <Loader2 size={15} className="animate-spin" /> : <CheckCircle size={15} />}
                  {language === 'de' ? 'Anteile speichern' : 'Save shares'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {viewerBlob && (
        <DocumentViewerModal
          blob={viewerBlob}
          fileName={viewerFileName}
          contentType={viewerContentType}
          onClose={() => { setViewerBlob(null); setViewerFileName(''); setViewerContentType(''); }}
          onDownload={handleDownloadFromViewer}
        />
      )}
    </div>
  );
};

export default PropertyView;